import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import { notifyError } from "../utils/notify";

export const firebaseConfig = {
  apiKey: "AIzaSyAGja_x1__V1qR3QMTwQD6048r0zWwlWzA",
  authDomain: "zapai-8585e.firebaseapp.com",
  projectId: "zapai-8585e",
  storageBucket: "zapai-8585e.firebasestorage.app",
  messagingSenderId: "438341250413",
  appId: "1:438341250413:web:fa004fd2b91b27c8a7be64",
  measurementId: "G-RVQLMPK1DL",
};

firebase.initializeApp(firebaseConfig);

export const auth = firebase.auth();
export const firestore = firebase.firestore();
export const storage = firebase.storage();

export const createAdminProfileDocument = async (userAuth, additionalData) => {
  if (!userAuth) return;

  const adminRef = firestore.doc(`admins/${userAuth.uid}`);

  const snapShot = await adminRef.get();
  if (!snapShot.exists) {
    const { name, email } = userAuth;
    const createdAt = new Date();
    try {
      await adminRef.set({
        name,
        email,
        createdAt,
        pending_orders: [],
        balance: 0,
        used_balance: 0,
        successfully_delivered_orders: [],
        remaining_balance: 0,
        ...additionalData,
      });
    } catch (error) {
      console.log("error creating admin", error.message);
    }
  }
  return adminRef;
};
export const updateAllUser = async () => {
  const usersRef = firestore.collection("users");
  const users = await usersRef.get();

  users.forEach(async (doc) => {
    const userRef = firestore.doc(`users/${doc.id}`);
    const user = await userRef.get();
    // do any updates here
  });
};
export const updateTransactionOrderStatus = async (orderObj) => {
  // A blank status (e.g. from a form state that was already reset) used to
  // wipe the status off every transaction of the order.
  if (!orderObj.orderStatus) return;
  const transactionsRef = firestore
    .collection("transactions")
    .where("subCategory", "==", `Order Id:${orderObj.orderId}`);
  const transactions = await transactionsRef.get();
  // Awaited so callers know the statuses are written before they move on.
  // "Reduced" rows are manual stock adjustments, not order statuses.
  await Promise.all(
    transactions.docs
      .filter(
        (doc) =>
          doc.data().orderStatus !== "Reduced" &&
          doc.data().orderStatus !== orderObj.orderStatus,
      )
      .map((doc) => doc.ref.update({ orderStatus: orderObj.orderStatus })),
  );
};

// The sales report shows each transaction's own copy of its order's status,
// so every order status change must be pushed onto the order's transactions.
// Called by every admin path that changes orderStatus (the customer site's
// updateOrder does the same). Never throws — the status change itself has
// already been committed.
const syncTransactionStatuses = async (changes) => {
  await Promise.all(
    changes.map(({ orderId, orderStatus }) =>
      updateTransactionOrderStatus({ orderId, orderStatus }).catch((e) =>
        console.error(`Syncing transactions for order ${orderId} failed`, e),
      ),
    ),
  );
};

// One-time repair: before every status path synced transactions, the copy
// of orderStatus on an order's transactions went stale (e.g. orders marked
// Delivered by Steadfast still showing Processing on the sales report).
// Reads every ordersList doc once for the live status, then every order
// transaction once, and rewrites only the mismatched ones. Orders missing
// from ordersList are skipped.
export const backfillTransactionStatuses = async (onProgress) => {
  const PAGE = 500;
  const statusById = new Map();
  let last = null;
  while (true) {
    let q = firestore
      .collection("ordersList")
      .orderBy(firebase.firestore.FieldPath.documentId())
      .limit(PAGE);
    if (last) q = q.startAfter(last);
    const snap = await q.get();
    snap.docs.forEach((d) => {
      const status = d.data().orderStatus;
      if (status) statusById.set(d.id, status);
    });
    if (snap.size < PAGE) break;
    last = snap.docs[snap.docs.length - 1];
  }

  let scanned = 0;
  let fixed = 0;
  last = null;
  while (true) {
    // Every "Order Id:<id>" subCategory sorts between these two bounds.
    let q = firestore
      .collection("transactions")
      .where("subCategory", ">=", "Order Id:")
      .where("subCategory", "<", "Order Id;")
      .orderBy("subCategory")
      .limit(PAGE);
    if (last) q = q.startAfter(last);
    const snap = await q.get();
    const batch = firestore.batch();
    let writes = 0;
    snap.docs.forEach((d) => {
      const t = d.data();
      const status = statusById.get(t.subCategory.slice("Order Id:".length));
      if (status && t.orderStatus !== "Reduced" && t.orderStatus !== status) {
        batch.update(d.ref, { orderStatus: status });
        writes++;
      }
    });
    if (writes > 0) await batch.commit();
    scanned += snap.size;
    fixed += writes;
    if (onProgress) onProgress(scanned, fixed);
    if (snap.size < PAGE) break;
    last = snap.docs[snap.docs.length - 1];
  }
  return { scanned, fixed };
};

export const getAllDeviceTokens = async () => {
  const tokensCollectionRef = firestore.collection("deviceTokens");
  try {
    const tokens = await tokensCollectionRef.get();
    const tokensArray = [];
    tokens.forEach((doc) => {
      // console.log(doc.id, " => ", doc.data());
      tokensArray.push(doc.data().deviceToken[0]);
    });
    return tokensArray;
  } catch (error) {
    notifyError(error.message || String(error));
    return [];
  }
};

export const uploadProduct = async (productObj) => {
  const productRef = firestore.doc(`products/${productObj.id}`);
  const snapShot = await productRef.get();
  delete productObj.file;
  const newProductObj = {
    ...productObj,
  };
  if (!snapShot.exists) {
    try {
      const { stockDeltas, ...fields } = newProductObj;
      await productRef.set(fields);
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    if (productObj.edited) {
      const { stockDeltas, ...fields } = newProductObj;
      if (stockDeltas) {
        // The edit form's stock numbers were loaded when the page opened, so
        // writing them back would erase every sale made in the meantime.
        // Instead apply only the restock amounts to the LIVE stock, inside a
        // transaction, and keep the live totalSold.
        await firestore.runTransaction(async (t) => {
          const live = (await t.get(productRef)).data() || {};
          const liveVariations = new Map(
            (live.displayedVariations || []).map((v) => [String(v.id), v]),
          );
          const merged = { ...fields };
          if (fields.displayedVariations?.length > 0) {
            merged.displayedVariations = fields.displayedVariations.map((v) => {
              const lv = liveVariations.get(String(v.id));
              if (!lv) return v; // new variation — form value is the truth
              const delta = parseInt(stockDeltas.variations?.[v.id]) || 0;
              return {
                ...v,
                inventory: (parseInt(lv.inventory) || 0) + delta,
                totalSold: lv.totalSold ?? v.totalSold ?? 0,
              };
            });
          } else {
            merged.inventory =
              (parseInt(live.inventory) || 0) + (parseInt(stockDeltas.base) || 0);
            merged.totalSold = live.totalSold ?? fields.totalSold ?? 0;
          }
          t.update(productRef, merged);
        });
      } else {
        await productRef.update(fields);
      }
    } else {
      notifyError(
        "there is already a product with this given prodcut Id, please change the product Id and upload again",
      );
    }
  }
  const updatedSnapShot = await productRef.get();
  if (updatedSnapShot.exists) {
    try {
      await firestore
        .collection("productList")
        .doc(productObj.id)
        .set(
          stripProductForList({ id: productObj.id, ...updatedSnapShot.data() }),
          { merge: true },
        );
    } catch (e) {
      console.error("productList sync failed", e);
    }
  }
  return updatedSnapShot.data();
};
export const uploadDisplayedVariation = async (variationObj) => {
  const productRef = firestore.doc(`variations/${variationObj.id}`);
  const snapShot = await productRef.get();

  const newVariationObj = {
    ...variationObj,
  };
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newVariationObj,
      });
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    await productRef.update({
      ...newVariationObj,
    });
  }
  const updatedSnapShot = await productRef.get();
  return updatedSnapShot.data();
};
export const uploadProductTax = async (productObj) => {
  const productRef = firestore.doc(`taxes/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj };
  if (!snapShot.exists) {
    try {
      productRef.set({
        ...newProductObj,
      });
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "there is already a product with this given prodcut Id, please change the product Id and upload again",
    );
  }
};
export const uploadImageRechargeRequest = async (file) => {
  const imageRef = storage.ref(`rechargeRequests/${file.name}`);

  try {
    await imageRef.put(file);
    var imgUrl = [];
    await imageRef.getDownloadURL().then((url) => {
      console.log(url);
      imgUrl.push(url);
    });
    return imgUrl[0];
  } catch (error) {
    return null;
  }
};
export const uploadImage = async (file, folder) => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("folder", folder);

    const response = await fetch("https://zapai.com.bd/api/images/upload", {
      method: "POST",
      body: formData,
    });

    const result = await response.json();
    console.log(result);
    if (response.ok && result.imageUrl) {
      return result.imageUrl; // 👈 URL like: https://yourdomain.com/images/banners/filename.jpg
    } else {
      console.error("❌ Upload failed:", result.error);
      return null;
    }
  } catch (error) {
    console.error("❌ Upload error:", error);
    return null;
  }
};

export const deleteImage = async (imageUrl) => {
  try {
    const res = await fetch("https://zapai.com.bd/api/images/delete", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ imageUrl }), // 👈 key point
    });

    const result = await res.json();

    if (res.ok) {
      console.log("✅ Image deleted successfully");
    } else {
      console.error("❌ Failed to delete image:", result.error);
    }
  } catch (error) {
    console.error("❌ Error calling delete API:", error);
  }
};

export const updateImage = async (file, oldImageUrl, folder) => {
  const formData = new FormData();
  formData.append("image", file); // `file` is a File object from input
  formData.append("oldImageUrl", oldImageUrl || "");
  formData.append("folder", folder || "default");

  try {
    const response = await fetch("https://zapai.com.bd/api/images/update", {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to update image.");
    }

    console.log("Update Response:", data);

    // ✅ Return imageUrl if present
    return data.imageUrl || oldImageUrl;
  } catch (error) {
    console.error("Image update failed:", error);
    return oldImageUrl; // fallback to previous image if update fails
  }
};

export const uploadAliProduct = async (productObj) => {
  const productRef = firestore.doc(`aliproducts/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj };
  if (!snapShot.exists) {
    try {
      productRef.set({
        ...newProductObj,
      });
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "this product is already added to your website. try adding different product",
    );
  }
};

let lastVisible = null; // This will hold the last document snapshot from the previous query
const cache = {}; // Store results in the cache for efficiency

export const getAllProducts = async (pageNo) => {
  console.log(`Fetching page: ${pageNo}`);
  const productsPerPage = 100;
  let productsArray = [];

  // Check if the data for this page is already in the cache
  if (cache[pageNo]) {
    console.log("Returning from cache");
    return cache[pageNo];
  }

  try {
    // Initial query, ordered by 'name', limited to productsPerPage
    let query = firestore
      .collection("products")
      .orderBy("name")
      .limit(productsPerPage);

    // If we're beyond the first page, use the last document from the previous page as the cursor
    if (pageNo > 1 && lastVisible) {
      query = query.startAfter(lastVisible); // Start after the last document of the previous page
    }

    // Fetch the products snapshot
    const productsSnapshot = await query.get();

    // Check if there are any products
    if (!productsSnapshot.empty) {
      // Update the lastVisible document to the last document in the current snapshot
      lastVisible = productsSnapshot.docs[productsSnapshot.docs.length - 1];

      // Push the fetched products to the array
      productsSnapshot.forEach((doc) => {
        productsArray.push(doc.data());
      });

      // Cache the result for the current page
      cache[pageNo] = productsArray;

      console.log(
        `Fetched ${productsArray.length} products for page ${pageNo}`,
      );
      return productsArray;
    } else {
      console.log("No products found");
      return []; // Return an empty array if no products are found
    }
  } catch (error) {
    console.error("Error fetching products:", error);
    notifyError("Failed to fetch products. Please try again.");
  }
};

export const getAllReviews = async () => {
  const productsCollectionRef = firestore
    .collection("products")
    .orderBy("reviews");
  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllProductsTax = async () => {
  const productsCollectionRef = firestore.collection("taxes");
  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllAliProducts = async () => {
  const aliProductsCollectionRef = firestore.collection("aliproducts");
  try {
    const products = await aliProductsCollectionRef.get();
    const aliProductsArray = [];
    products.forEach((doc) => {
      aliProductsArray.push(doc.data());
    });
    return aliProductsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteProduct = async (id) => {
  const productRef = firestore.doc(`products/${id}`);
  try {
    await productRef.delete();
    // Otherwise this product's historical sales stats keep showing up in the
    // "Top Sold Products" / restock dashboards even after it's gone.
    await firestore.doc(`productStats/${id}`).delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteReview = async (productId, reviewId) => {
  const productRef = firestore.doc(`products/${productId}`);
  const product = await productRef.get();
  let updatedReviews = [];
  product.data().reviews.map((review) => {
    if (review.id != reviewId) {
      updatedReviews.push(review);
    }
  });
  try {
    await productRef.update({ ...product.data(), reviews: updatedReviews });
    const updatedProduct = await productRef.get();
    return updatedProduct.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteProductTax = async (id) => {
  const productRef = firestore.doc(`taxes/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updateProductTax = async (productObj) => {
  const productRef = firestore.doc(`taxes/${productObj.id}`);
  try {
    await productRef.update({ ...productObj });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleProductTax = async (id) => {
  const productRef = firestore.doc(`taxes/${id}`);
  try {
    const product = await productRef.get();
    return product.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getFreeShipping = async () => {
  const productRef = firestore.doc(`freeShipping/freeShipping`);
  try {
    const product = await productRef.get();
    return product.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getSingleProduct = async (id) => {
  const productRef = firestore.doc(`products/${id}`);
  try {
    const product = await productRef.get();
    return product.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadDraftProduct = async (productObj) => {
  const ref = firestore.doc(`draft-products/${productObj.id}`);
  delete productObj.file;
  try {
    await ref.set({ ...productObj, isDraft: true });
    return (await ref.get()).data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllDraftProducts = async () => {
  try {
    const [draftSnap, statusSnap] = await Promise.all([
      firestore.collection("draft-products").get(),
      firestore.collection("products").where("status", "==", "draft").get(),
    ]);
    const drafts = draftSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const statusDrafts = statusSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      _isStatusDraft: true,
    }));
    return [...drafts, ...statusDrafts];
  } catch (error) {
    notifyError(error.message || String(error));
    return [];
  }
};

export const getSingleDraftProduct = async (id) => {
  try {
    const snap = await firestore.doc(`draft-products/${id}`).get();
    return snap.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const publishDraftProduct = async (productObj) => {
  const product = await uploadProduct(productObj);
  await firestore.doc(`draft-products/${productObj.id}`).delete();
  return product;
};

export const deleteDraftProduct = async (id) => {
  try {
    await firestore.doc(`draft-products/${id}`).delete();
    return id;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getDisplayedVariation = async (id) => {
  const productRef = firestore.doc(`variations/${id}`);
  try {
    const product = await productRef.get();
    return product.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// get all users

export const getAllUsers = async () => {
  const usersCollectionRef = firestore.collection("users");
  try {
    const users = await usersCollectionRef.get();
    const usersArray = [];
    users.forEach((doc) => {
      usersArray.push({ uid: doc.id, ...doc.data() });
    });
    return usersArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteUser = async (id) => {
  const productRef = firestore.doc(`users/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// Orders management (get all orders)

export const getAllOrders = async (orderStatus, lastOrderId = null, startTs = null, endTs = null) => {
  let queryRef = firestore.collection("orders");

  if (orderStatus !== "All Orders") {
    queryRef = queryRef.where("orderStatus", "==", orderStatus);
  }

  if (startTs != null && endTs != null) {
    const startTimestamp = firebase.firestore.Timestamp.fromMillis(startTs);
    const endTimestamp   = firebase.firestore.Timestamp.fromMillis(endTs);
    queryRef = queryRef
      .where("createdAt", ">=", startTimestamp)
      .where("createdAt", "<=", endTimestamp);
  }

  queryRef = queryRef.orderBy("createdAt", "desc").limit(20);

  if (lastOrderId) {
    const lastSnap = await firestore
      .collection("orders")
      .doc(lastOrderId)
      .get();
    if (!lastSnap.exists) {
      return { orders: [], lastVisible: null, hasMore: false };
    }
    const lastData = lastSnap.data();
    if (!lastData || lastData.createdAt === undefined) {
      throw new Error("Invalid last document: missing 'createdAt' field.");
    }
    // Snapshot-based cursor (see getOrdersList for why) — avoids skipping
    // documents that share an identical createdAt timestamp.
    queryRef = queryRef.startAfter(lastSnap);
  }

  try {
    const snapshot = await queryRef.get();
    const ordersArray = [];
    snapshot.forEach((doc) => {
      ordersArray.push({ id: doc.id, ...doc.data() });
    });
    const newLastDocId = snapshot.docs[snapshot.docs.length - 1]?.id || null;
    return {
      orders: ordersArray,
      lastVisible: newLastDocId,
      hasMore: snapshot.size > 0,
    };
  } catch (error) {
    console.error("Error fetching orders:", error);
    throw error;
  }
};

// Fetches every full order doc (with line items, unlike the lean ordersList
// projection) whose orderStatus is "Delivered"/"Partial Delivered" within a
// date range, for the profit report. Filters status client-side against a
// date-range-only query to avoid needing a composite index.
export const getOrdersForProfitReport = async ({
  startTs,
  endTs,
  orderStatuses = ["Delivered", "Partial Delivered"],
}) => {
  // Filter orderStatus in the query itself (not client-side) so this only
  // ever reads the (heavy — full line items with embedded product data)
  // orders that actually qualify, instead of paging through every order of
  // every status in the date range and discarding most of them. Firestore
  // needs a composite index for "orderStatus in [...]" combined with a
  // createdAt range — if this throws with a "create index" link the first
  // time it runs, follow that link once in the Firebase console.
  let baseQuery = firestore
    .collection("orders")
    .where("orderStatus", "in", orderStatuses)
    .orderBy("createdAt", "asc");
  if (startTs != null && endTs != null) {
    baseQuery = baseQuery
      .where("createdAt", ">=", firebase.firestore.Timestamp.fromMillis(startTs))
      .where("createdAt", "<=", firebase.firestore.Timestamp.fromMillis(endTs));
  }

  const PAGE_SIZE = 200;
  const results = [];
  let lastDoc = null;

  while (true) {
    let pageQuery = baseQuery.limit(PAGE_SIZE);
    if (lastDoc) pageQuery = pageQuery.startAfter(lastDoc);
    const snap = await pageQuery.get();
    if (snap.empty) break;

    snap.docs.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() });
    });

    lastDoc = snap.docs[snap.docs.length - 1];
    if (snap.docs.length < PAGE_SIZE) break;
  }

  return results;
};

// ─── ordersList helpers ────────────────────────────────────────────────────

const stripOrderForList = (order) => {
  const addrArr =
    (order.currentUser && order.currentUser.address) ||
    (order.guest && order.guest.address) ||
    [];
  const addr = addrArr.find((a) => a.defaultShipping) || addrArr[0] || null;

  const slim = {
    id: order.id,
    date: order.date || null,
    createdAt: order.createdAt || null,
    orderStatus: order.orderStatus || "",
    orderNote: order.orderNote || "",
    currentUser: {
      displayName:
        (order.currentUser && order.currentUser.displayName) || "Guest",
    },
    deliveryAddress: addr
      ? {
          fullName: addr.fullName || "",
          mobileNo: addr.mobileNo || "",
          address: addr.address || "",
          district: addr.district || "",
          division: addr.division || "",
        }
      : null,
    subTotal: order.subTotal || 0,
    deliveryCharge: order.deliveryCharge || 0,
    discountApplied: order.discountApplied || 0,
    couponApplied:
      order.couponApplied
        ? { discount: order.couponApplied.discount || 0 }
        : null,
    finalDiscount: order.finalDiscount || 0,
    payments: order.payments || [],
    paymentStatus: order.paymentStatus || "",
    orders: (order.orders || []).map((item) => ({
      name: (item.product && item.product.name) || item.name || "",
      image:
        item.productImage ||
        (item.selectedVariation &&
          item.selectedVariation.pictures &&
          item.selectedVariation.pictures[0]) ||
        (item.product &&
          item.product.pictures &&
          item.product.pictures[0]) ||
        "",
      quantity: item.quantity || 1,
    })),
    courier: order.courier
      ? {
          pathao: order.courier.pathao || null,
          consignment: order.courier.consignment
            ? {
                consignment_id:
                  order.courier.consignment.consignment_id || null,
                status: order.courier.consignment.status || null,
              }
            : null,
          consignment_id: order.courier.consignment_id || null,
        }
      : null,
    totalBill:
      (order.subTotal || 0) +
      (order.deliveryCharge || 0) -
      (order.discountApplied || 0) -
      (order.couponApplied ? order.couponApplied.discount || 0 : 0) -
      (order.finalDiscount || 0),
    totalPaid: (order.payments || []).reduce(
      (s, p) => s + (parseInt(p.amount) || 0),
      0,
    ),
  };
  Object.assign(slim, paymentDueFields(slim));
  return slim;
};

const stripProductForList = (product) => ({
  id: product.id,
  name: product.name || "",
  pictures: product.pictures?.slice(0, 1) || [],
  pictures2: product.pictures2?.slice(0, 1) || [],
  price: product.price || 0,
  salePrice: product.salePrice || 0,
  stockStatus: product.stockStatus || "",
  inventory: product.inventory || 0,
  displayedVariations: (product.displayedVariations || []).map((v) => ({
    id: v.id,
    combination: (v.combination || []).map((c) => ({ name: c.name })),
    stockStatus: v.stockStatus || "",
    inventory: v.inventory || 0,
    price: v.price || 0,
    salePrice: v.salePrice || 0,
    pictures: v.pictures?.slice(0, 1) || [],
  })),
  selectedCategories: (product.selectedCategories || []).map((c) => ({
    id: c.id,
    name: c.name,
  })),
  selectedBrands: (product.selectedBrands || []).map((b) => ({
    id: b.id,
    name: b.name,
  })),
  selectedTags: product.selectedTags || [],
  published: product.published || "",
  publishedOn: product.publishedOn || "",
  edited: product.edited || false,
  totalSold: product.totalSold || 0,
  isPreOrder: product.isPreOrder || false,
  new: product.new || false,
  checkedValues: product.checkedValues || [],
  checkedValues2: product.checkedValues2 || [],
  selectedCampaign: product.selectedCampaign || "",
});

export const migrateProductsToProductList = async (onProgress) => {
  let lastDoc = null;
  let total = 0;
  const BATCH_SIZE = 20;
  while (true) {
    let q = firestore.collection("products").orderBy("name").limit(BATCH_SIZE);
    if (lastDoc) q = q.startAfter(lastDoc);
    const snap = await q.get();
    if (snap.empty) break;
    const batch = firestore.batch();
    snap.docs.forEach((doc) => {
      const p = { id: doc.id, ...doc.data() };
      batch.set(
        firestore.collection("productList").doc(doc.id),
        stripProductForList(p),
        { merge: true },
      );
    });
    await batch.commit();
    total += snap.docs.length;
    lastDoc = snap.docs[snap.docs.length - 1];
    if (onProgress) onProgress(total);
    if (snap.docs.length < BATCH_SIZE) break;
  }
  return total;
};

// Live "amount payable"/"amount paid" for an order, recomputed from
// subTotal/discounts/payments rather than the separately-stored
// totalBill/totalPaid fields (which can drift after an order is edited).
// Shared by paymentDueContribution (summary counters) and paymentDueFields (the
// stored flag the Payment Due tab queries) so both agree on the same orders.
const computeOrderAmountPayable = (data) =>
  (data.subTotal || 0) +
  (data.deliveryCharge || 0) -
  (data.discountApplied || 0) -
  (data.couponApplied ? data.couponApplied.discount || 0 : 0) -
  (data.finalDiscount || 0);

const computeOrderTotalPaid = (data) => {
  const fromPayments = (data.payments || []).reduce(
    (sum, p) => sum + (parseInt(p.amount) || 0),
    0,
  );
  // Legacy payment-approval flow (updateOrderAmount, the /payments module)
  // records paid amounts into paymentStatus.paid instead of the payments[]
  // array. Take whichever is larger rather than summing them — safer
  // default if an order was ever touched by both mechanisms, and it means
  // an order that was actually paid via the legacy flow can never still
  // show as "due" just because payments[] doesn't know about it.
  const legacyPaid =
    data.paymentStatus && typeof data.paymentStatus === "object"
      ? parseInt(data.paymentStatus.paid) || 0
      : 0;
  return Math.max(fromPayments, legacyPaid);
};

// What an order contributes to the "Payment Due" pseudo-bucket in
// summaries/orderStatsByStatus right now — null if it's Cancelled or has no
// positive live balance. Shared by buildSummaryDelta, applyPaymentBatch, and
// buildOrderSummary so their Payment Due bookkeeping can't diverge from each
// other or from what getOrdersList's Payment Due filter itself considers due.
const paymentDueContribution = (data, resolvedStatus) => {
  if (resolvedStatus === "Cancelled") return null;
  const totalBill = computeOrderAmountPayable(data);
  const totalPaid = computeOrderTotalPaid(data);
  if (totalBill - totalPaid <= 0) return null;
  return { totalBill, totalPaid, totalDiscount: data.finalDiscount || 0 };
};

// Stored on every ordersList doc so the Payment Due tab can be a plain indexed
// query + server-side aggregation instead of a client-side scan and a
// drift-prone counter. Every ordersList write must keep these in sync (the
// customer-site repo has a copy of this rule). The card sums dueBill/duePaid
// rather than totalBill/totalPaid, which can drift after an edit and ignore
// legacy paymentStatus.paid — so its money figures come from exactly the
// same numbers as the "is it due" decision. Both are 0 when not due.
const paymentDueFields = (data, status = data.orderStatus) => {
  const c = paymentDueContribution(data, status);
  return {
    paymentDue: !!c,
    dueBill: c ? c.totalBill : 0,
    duePaid: c ? c.totalPaid : 0,
  };
};

export const getOrdersList = async (
  orderStatus,
  lastOrderId = null,
  startTs = null,
  endTs = null,
) => {
  // "Payment Due" is a pseudo-status (spans every real orderStatus) — it
  // isn't a value ever stored on a doc, so it bypasses the status filter
  // the same way "All Orders" does.
  const isPaymentDue = orderStatus === "Payment Due";
  const isAllOrders = orderStatus === "All Orders";

  let queryRef = firestore.collection("ordersList");

  if (isPaymentDue) {
    // Stored flag (see paymentDueFields) — an indexed query instead of scanning.
    queryRef = queryRef.where("paymentDue", "==", true);
  } else if (!isAllOrders) {
    queryRef = queryRef.where("orderStatus", "==", orderStatus);
  }

  if (startTs != null && endTs != null) {
    queryRef = queryRef
      .where("createdAt", ">=", firebase.firestore.Timestamp.fromMillis(startTs))
      .where("createdAt", "<=", firebase.firestore.Timestamp.fromMillis(endTs));
  }

  queryRef = queryRef.orderBy("createdAt", "desc");

  if (lastOrderId) {
    const lastSnap = await firestore.collection("ordersList").doc(lastOrderId).get();
    if (!lastSnap.exists) return { orders: [], lastVisible: null, hasMore: false };
    const lastData = lastSnap.data();
    if (!lastData || lastData.createdAt === undefined)
      throw new Error("Invalid last document: missing 'createdAt' field.");
    // Cursor on the document snapshot itself, not just the bare createdAt
    // value — Firestore implicitly breaks ties on document id for any
    // snapshot-based cursor, so orders that happen to share an identical
    // createdAt timestamp (e.g. from a batch backfill) can no longer be
    // silently skipped across a page boundary the way a bare-value cursor
    // would skip every document sharing that same timestamp.
    queryRef = queryRef.startAfter(lastSnap);
  }

  try {
    const snapshot = await queryRef.limit(20).get();
    const ordersArray = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return {
      orders: ordersArray,
      lastVisible: snapshot.docs[snapshot.docs.length - 1]?.id || null,
      hasMore: snapshot.size > 0,
    };
  } catch (error) {
    console.error("Error fetching ordersList:", error);
    throw error;
  }
};

export const updateOrderInList = async (orderId) => {
  try {
    const snap = await firestore.collection("orders").doc(orderId).get();
    if (!snap.exists) return;
    const order = { id: snap.id, ...snap.data() };
    const stripped = stripOrderForList(order);
    await firestore.collection("ordersList").doc(orderId).set(stripped, { merge: true });
  } catch (error) {
    console.error("Error syncing ordersList:", error);
  }
};

export const migrateOrdersToOrdersList = async (onProgress, cutoffTs = null) => {
  let lastDoc = null;
  let totalMigrated = 0;
  const BATCH_SIZE = 20;

  while (true) {
    let queryRef = firestore
      .collection("orders")
      .orderBy("createdAt", "desc")
      .limit(BATCH_SIZE);
    // If resuming, only fetch orders older than the cutoff (already-migrated ones are newer)
    if (cutoffTs) queryRef = queryRef.where("createdAt", "<", cutoffTs);
    if (lastDoc) queryRef = queryRef.startAfter(lastDoc);

    const snapshot = await queryRef.get();
    if (snapshot.empty) break;

    const batch = firestore.batch();
    snapshot.docs.forEach((doc) => {
      const order = { id: doc.id, ...doc.data() };
      const stripped = stripOrderForList(order);
      batch.set(firestore.collection("ordersList").doc(doc.id), stripped, {
        merge: true,
      });
    });
    await batch.commit();

    totalMigrated += snapshot.docs.length;
    lastDoc = snapshot.docs[snapshot.docs.length - 1];
    if (onProgress) onProgress(totalMigrated);
    if (snapshot.docs.length < BATCH_SIZE) break;
  }

  return totalMigrated;
};

// Finds missing orders by comparing IDs within a time window (default last 60 days).
// Reads: orders_in_window + ordersList_in_window (both in parallel), writes only the missing ones.
export const syncNewOrdersToList = async (onProgress, daysBack = 60) => {
  const startTs = firebase.firestore.Timestamp.fromMillis(Date.now() - daysBack * 86400000);

  const ordersSnap = await firestore.collection("orders")
    .where("createdAt", ">=", startTs)
    .orderBy("createdAt", "asc")
    .get();
  if (ordersSnap.empty) return 0;

  // Check each candidate order's ordersList doc for TRUE existence via a
  // direct read. The previous approach queried ordersList filtered by
  // createdAt to build a "which ids already exist" set — but that silently
  // excludes any ordersList doc that's missing createdAt (a real state that
  // could happen before pickListFields/bulkUpdateOrderStatus always stamped
  // it), making this function think such a doc was "missing" and re-count
  // it into the summary a second time even though it already existed.
  const listSnaps = await Promise.all(
    ordersSnap.docs.map((d) => firestore.doc(`ordersList/${d.id}`).get())
  );
  const missingDocs = ordersSnap.docs.filter((d, i) => !listSnaps[i].exists);
  return writeOrdersToList(missingDocs, onProgress);
};

// Syncs specific orders (already known to be missing from ordersList) by id.
// Costs exactly one read per id — it does NOT re-check ordersList, so only
// pass ids you've confirmed are missing, or the summary counters get counted
// twice for them.
export const syncOrdersToListByIds = async (ids, onProgress) => {
  const snaps = await Promise.all(
    ids.map((id) => firestore.doc(`orders/${id}`).get())
  );
  return writeOrdersToList(snaps.filter((s) => s.exists), onProgress);
};

// Writes the given orders/ docs into ordersList and adds them to the status
// and daily summary counters.
const writeOrdersToList = async (missingDocs, onProgress) => {
  if (missingDocs.length === 0) return 0;

  const statusDelta = {};
  const dailyAccum = {};

  const missing = missingDocs.map((d) => {
    const order = { id: d.id, ...d.data() };
    const stripped = stripOrderForList(order);
    const totalBill = (order.subTotal || 0) + (order.deliveryCharge || 0)
      - (order.discountApplied || 0)
      - (order.couponApplied ? order.couponApplied.discount || 0 : 0)
      - (order.finalDiscount || 0);
    const totalPaid = (order.payments || []).reduce((s, p) => s + (parseInt(p.amount) || 0), 0);
    stripped.totalBill = totalBill;
    stripped.totalPaid = totalPaid;

    const st = order.orderStatus || "";
    if (st) {
      statusDelta[`${st}.count`]         = (statusDelta[`${st}.count`]         || 0) + 1;
      statusDelta[`${st}.totalBill`]     = (statusDelta[`${st}.totalBill`]     || 0) + totalBill;
      statusDelta[`${st}.totalPaid`]     = (statusDelta[`${st}.totalPaid`]     || 0) + totalPaid;
      statusDelta[`${st}.totalDiscount`] = (statusDelta[`${st}.totalDiscount`] || 0) + (order.finalDiscount || 0);
    }

    const contribution = paymentDueContribution(order, st);
    if (contribution) {
      statusDelta["Payment Due.count"] = (statusDelta["Payment Due.count"] || 0) + 1;
      statusDelta["Payment Due.totalBill"] = (statusDelta["Payment Due.totalBill"] || 0) + contribution.totalBill;
      statusDelta["Payment Due.totalPaid"] = (statusDelta["Payment Due.totalPaid"] || 0) + contribution.totalPaid;
      statusDelta["Payment Due.totalDiscount"] = (statusDelta["Payment Due.totalDiscount"] || 0) + contribution.totalDiscount;
    }

    const dRef = dayDocRef(order.createdAt);
    if (dRef) {
      const path = dRef.path;
      if (!dailyAccum[path]) dailyAccum[path] = { ref: dRef, count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0, cancelledCount: 0, cancelledBill: 0 };
      if (st === "Cancelled") {
        dailyAccum[path].cancelledCount += 1;
        dailyAccum[path].cancelledBill  += totalBill;
      } else {
        dailyAccum[path].count         += 1;
        dailyAccum[path].totalBill     += totalBill;
        dailyAccum[path].totalPaid     += totalPaid;
        dailyAccum[path].totalDiscount += (order.finalDiscount || 0);
      }
    }

    return { id: order.id, stripped };
  });

  // Write missing ordersList docs in batches of 20
  const BATCH_SIZE = 20;
  for (let i = 0; i < missing.length; i += BATCH_SIZE) {
    const batch = firestore.batch();
    missing.slice(i, i + BATCH_SIZE).forEach(({ id, stripped }) => {
      batch.set(firestore.collection("ordersList").doc(id), stripped, { merge: true });
    });
    await batch.commit();
    if (onProgress) onProgress(Math.min(i + BATCH_SIZE, missing.length));
  }

  // Update summaries
  const summaryBatch = firestore.batch();
  const summaryUpdate = {};
  Object.entries(statusDelta).forEach(([k, v]) => { summaryUpdate[k] = inc(v); });
  if (Object.keys(summaryUpdate).length > 0) summaryBatch.update(SUMMARY_DOC(), summaryUpdate);
  Object.values(dailyAccum).forEach(({ ref, ...nums }) => {
    const d = {};
    Object.entries(nums).forEach(([k, v]) => { if (v !== 0) d[k] = inc(v); });
    if (Object.keys(d).length > 0) summaryBatch.set(ref, d, { merge: true });
  });
  await summaryBatch.commit();

  return missing.length;
};

// The manually-seeded "TEST Case ..." orders end up with `createdAt` stored
// as a plain string (e.g. `new Date().toISOString()`) instead of a Firestore
// Timestamp. Firestore's orderBy sorts a field by value TYPE first, and
// strings sort after (are considered "greater than") timestamps — so in a
// `.orderBy("createdAt", "desc")` query, every string-typed createdAt
// bubbles to the very top regardless of what date it actually represents.
// Every affected order shares an `orderNote` starting with "TEST Case", so
// this targets just those docs via a prefix range query instead of scanning
// the entire (tens-of-thousands-doc) orders/ordersList collections, and
// converts createdAt to a Timestamp holding the same date/time — fixing the
// sort order without changing what date is shown.
export const fixOrderCreatedAtTypes = async (onProgress) => {
  let fixedCount = 0;

  const fixCollection = async (collectionName) => {
    const snapshot = await firestore
      .collection(collectionName)
      .where("orderNote", ">=", "TEST Case")
      .where("orderNote", "<", "TEST Case")
      .get();
    if (snapshot.empty) return;

    const batch = firestore.batch();
    let batchHasWrites = false;
    snapshot.docs.forEach((doc) => {
      const createdAt = doc.data().createdAt;
      if (typeof createdAt === "string") {
        const parsed = new Date(createdAt);
        if (!isNaN(parsed.getTime())) {
          batch.update(doc.ref, {
            createdAt: firebase.firestore.Timestamp.fromDate(parsed),
          });
          batchHasWrites = true;
          fixedCount += 1;
        }
      }
    });
    if (batchHasWrites) await batch.commit();
    if (onProgress) onProgress(fixedCount);
  };

  await fixCollection("orders");
  await fixCollection("ordersList");

  return fixedCount;
};

// Some ordersList docs are missing createdAt entirely — older writes (before
// bulkUpdateOrderStatus/updateMultipleOrderwithOrderTo/pickListFields were
// fixed to always stamp it) could create or touch an ordersList doc with
// only a couple of fields, never createdAt. Such a doc is invisible to every
// date-ordered list query (Firestore excludes docs missing an orderBy field)
// but still gets counted by "Recalculate Order Stats" (buildOrderSummary,
// which scans by document id instead), which is why the stats pill can stay
// wrong even right after a recalculation. This scans ordersList by document
// id (unaffected by the missing-field issue) and backfills createdAt from
// the corresponding orders/{id} doc wherever it's missing.
export const backfillMissingCreatedAt = async (onProgress) => {
  let fixedCount = 0;
  let lastDoc = null;
  const BATCH_SIZE = 200;

  while (true) {
    let q = firestore
      .collection("ordersList")
      .orderBy(firebase.firestore.FieldPath.documentId())
      .limit(BATCH_SIZE);
    if (lastDoc) q = q.startAfter(lastDoc);
    const snapshot = await q.get();
    if (snapshot.empty) break;

    const missing = snapshot.docs.filter((doc) => !doc.data().createdAt);
    if (missing.length > 0) {
      const orderSnaps = await Promise.all(
        missing.map((doc) => firestore.doc(`orders/${doc.id}`).get())
      );
      const batch = firestore.batch();
      missing.forEach((doc, i) => {
        const createdAt =
          orderSnaps[i].data()?.createdAt || firebase.firestore.FieldValue.serverTimestamp();
        batch.update(doc.ref, { createdAt });
        fixedCount += 1;
      });
      await batch.commit();
    }

    lastDoc = snapshot.docs[snapshot.docs.length - 1];
    if (onProgress) onProgress(fixedCount);
    if (snapshot.docs.length < BATCH_SIZE) break;
  }

  return fixedCount;
};

// ───────────────────────────────────────────────────────────────────────────

export const getRecentOrders = async (count = 5, startTs = null, endTs = null) => {
  try {
    let queryRef = firestore.collection("ordersList");
    if (startTs != null && endTs != null) {
      queryRef = queryRef
        .where("createdAt", ">=", firebase.firestore.Timestamp.fromMillis(startTs))
        .where("createdAt", "<=", firebase.firestore.Timestamp.fromMillis(endTs));
    }
    const snapshot = await queryRef.orderBy("createdAt", "desc").limit(count).get();
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching recent orders:", error);
    return [];
  }
};

export const updateAllUsers = async () => {
  const collection = await firestore.collection("users").get();
  collection.forEach((doc) => {
    doc.ref.update({
      ...doc.data(),
      imageUrl:
        "https://firebasestorage.googleapis.com/v0/b/globalbuybd-auth.appspot.com/o/users%2Fprofile.png?alt=media&token=810e25f1-8cd9-43b0-b49d-41f2236588cb",
    });
  });
};

export const deleteOrder = async (id) => {
  const orderRef = firestore.doc(`orders/${id}`);
  try {
    await orderRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleOrder = async (id) => {
  const orderRef = firestore.doc(`orders/${id}`);
  try {
    const order = await orderRef.get();
    return order.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getSingleCampaign = async (id) => {
  const orderRef = firestore.doc(`campaigns/${id}`);
  try {
    const order = await orderRef.get();
    return order.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleAttribute = async (id) => {
  const orderRef = firestore.doc(`attributes/${id}`);
  try {
    const order = await orderRef.get();
    return order.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

const SUMMARY_DOC = () => firestore.doc("summaries/orderStatsByStatus");
const inc = (n) => firebase.firestore.FieldValue.increment(n);

const buildSummaryDelta = (old, updated) => {
  const oldStatus = old.orderStatus || "";
  const newStatus = updated.orderStatus || oldStatus;
  // Accumulate as plain numbers first, keyed by "<status>.<field>" — when
  // oldStatus === newStatus (the common case: editing an order without
  // changing its status), the old.field:contributes/new.field:contributes
  // both land on the SAME key and must net together (e.g. count: -1 + 1 = 0)
  // rather than the second FieldValue.increment() sentinel silently
  // overwriting the first, which was inflating every status bucket's count
  // and totals on every single edit regardless of whether status changed.
  const numeric = {};
  const add = (status, field, amount) => {
    if (!status || !amount) return;
    const key = `${status}.${field}`;
    numeric[key] = (numeric[key] || 0) + amount;
  };
  if (oldStatus) {
    add(oldStatus, "count", -1);
    add(oldStatus, "totalBill", -(old.totalBill || 0));
    add(oldStatus, "totalPaid", -(old.totalPaid || 0));
    add(oldStatus, "totalDiscount", -(old.finalDiscount || 0));
  }
  if (newStatus) {
    add(newStatus, "count", 1);
    add(newStatus, "totalBill", updated.totalBill || 0);
    add(newStatus, "totalPaid", updated.totalPaid || 0);
    add(newStatus, "totalDiscount", updated.finalDiscount || 0);
  }

  const oldContribution = paymentDueContribution(old, oldStatus);
  const newContribution = paymentDueContribution(updated, newStatus);
  if (oldContribution) {
    add("Payment Due", "count", -1);
    add("Payment Due", "totalBill", -oldContribution.totalBill);
    add("Payment Due", "totalPaid", -oldContribution.totalPaid);
    add("Payment Due", "totalDiscount", -oldContribution.totalDiscount);
  }
  if (newContribution) {
    add("Payment Due", "count", 1);
    add("Payment Due", "totalBill", newContribution.totalBill);
    add("Payment Due", "totalPaid", newContribution.totalPaid);
    add("Payment Due", "totalDiscount", newContribution.totalDiscount);
  }

  const delta = {};
  Object.entries(numeric).forEach(([key, value]) => {
    if (value !== 0) delta[key] = inc(value);
  });
  return delta;
};

// Daily summary helpers — one doc per calendar day in summaries/orderStats_YYYY_MM_DD
const dayDocRef = (createdAt) => {
  if (!createdAt) return null;
  const d = createdAt.toDate ? createdAt.toDate() : new Date(Number(createdAt));
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return firestore.doc(`summaries/orderStats_${y}_${mo}_${dy}`);
};

const getDayKeys = (startTs, endTs) => {
  const keys = [];
  const d = new Date(startTs);
  d.setHours(0, 0, 0, 0);
  const end = new Date(endTs);
  while (d <= end) {
    keys.push(
      `orderStats_${d.getFullYear()}_${String(d.getMonth()+1).padStart(2,"0")}_${String(d.getDate()).padStart(2,"0")}`
    );
    d.setDate(d.getDate() + 1);
  }
  return keys;
};

// Computes flat numeric delta for a daily doc when an order's list fields change
const buildDailyDocDelta = (old, updated) => {
  const n = { count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0, cancelledCount: 0, cancelledBill: 0 };
  const oldCancelled = old.orderStatus === "Cancelled";
  const newCancelled = updated.orderStatus === "Cancelled";
  if (oldCancelled) { n.cancelledCount -= 1; n.cancelledBill -= (old.totalBill     || 0); }
  else              { n.count -= 1; n.totalBill -= (old.totalBill || 0); n.totalPaid -= (old.totalPaid || 0); n.totalDiscount -= (old.finalDiscount || 0); }
  if (newCancelled) { n.cancelledCount += 1; n.cancelledBill += (updated.totalBill     || 0); }
  else              { n.count += 1; n.totalBill += (updated.totalBill || 0); n.totalPaid += (updated.totalPaid || 0); n.totalDiscount += (updated.finalDiscount || 0); }
  const delta = {};
  Object.entries(n).forEach(([k, v]) => { if (v !== 0) delta[k] = inc(v); });
  return delta;
};

// Monthly sourcing stats — one doc per month in sourcingMonthStats/{month},
// incrementally updated by every function that touches a sourcing invoice's
// financial fields (see uploadSourcing/updateSourcing/deleteSourcing/
// handleSourcingCashIn/handleSourcingCashOut/handleSourcingAgentPay).
const monthStatsRef = (month) => firestore.doc(`sourcingMonthStats/${month}`);

// Builds a nested delta object (not dotted-string keys) since `agent` is a
// free-text employee name and can contain characters like "." that Firestore
// would otherwise parse as a field-path separator in an object key.
const buildMonthStatsDelta = (agent, deltas) => {
  const flat = {};
  const byAgent = {};
  Object.entries(deltas).forEach(([key, value]) => {
    if (!value) return;
    flat[key] = inc(value);
    if (agent) byAgent[key] = inc(value);
  });
  return agent && Object.keys(byAgent).length > 0
    ? { ...flat, byAgent: { [agent]: byAgent } }
    : flat;
};

const sumRefundArray = (arr) =>
  (arr || []).reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

const pickListFields = (data) => {
  const fields = {};
  // createdAt must always be stamped whenever the source object has it —
  // an ordersList doc missing this field is invisible to every date-ordered
  // list query (Firestore excludes docs lacking an orderBy field), even
  // though a full rescan (which orders by document id) still counts it,
  // which is what let the stats pill drift from the live list even right
  // after a "Recalculate".
  ["orderStatus", "orderNote", "subTotal", "deliveryCharge",
    "discountApplied", "finalDiscount", "payments", "paymentStatus",
    "refundedAmount", "createdAt"].forEach((f) => {
    if (data[f] !== undefined) fields[f] = data[f];
  });
  if (data.couponApplied !== undefined) {
    fields.couponApplied = data.couponApplied
      ? { discount: data.couponApplied.discount || 0 }
      : null;
  }
  if (data.courier !== undefined) {
    fields.courier = data.courier
      ? {
          pathao: data.courier.pathao || null,
          consignment: data.courier.consignment
            ? {
                consignment_id: data.courier.consignment.consignment_id || null,
                status: data.courier.consignment.status || null,
              }
            : null,
          consignment_id: data.courier.consignment_id || null,
        }
      : null;
  }
  if (data.currentUser !== undefined) {
    fields.currentUser = {
      displayName: (data.currentUser && data.currentUser.displayName) || "Guest",
    };
  }
  if (data.deliveryAddress !== undefined) {
    fields.deliveryAddress = data.deliveryAddress;
  }
  if (data.orders !== undefined) {
    fields.orders = (data.orders || []).map((item) => ({
      name: (item.product && item.product.name) || item.name || "",
      image:
        item.productImage ||
        item.selectedVariation?.pictures?.[0] ||
        item.product?.pictures?.[0] ||
        "",
      quantity: item.quantity || 1,
    }));
  }
  // Recompute totalPaid whenever payments array is present in the update
  if (data.payments !== undefined) {
    fields.totalPaid = (data.payments || []).reduce(
      (s, p) => s + (parseInt(p.amount) || 0),
      0,
    );
  }
  // Recompute totalBill when all components are available (full orderObj path)
  const billKeys = ["subTotal", "deliveryCharge", "discountApplied", "finalDiscount"];
  if (billKeys.every((k) => data[k] !== undefined)) {
    fields.totalBill =
      (data.subTotal || 0) +
      (data.deliveryCharge || 0) -
      (data.discountApplied || 0) -
      (data.couponApplied ? data.couponApplied.discount || 0 : 0) -
      (data.finalDiscount || 0);
  }
  return fields;
};

export const updateOrder = async (orderObj) => {
  const orderRef = firestore.doc(`orders/${orderObj.id}`);
  const listRef  = firestore.doc(`ordersList/${orderObj.id}`);
  const listFields = pickListFields(orderObj);
  const oldSnap = await listRef.get();
  const old = oldSnap.data() || {};
  const updated = { ...old, ...listFields };
  const batch = firestore.batch();
  batch.update(orderRef, orderObj);
  if (Object.keys(listFields).length > 0) {
    batch.set(listRef, { ...listFields, ...paymentDueFields(updated) }, { merge: true });
    try { batch.update(SUMMARY_DOC(), buildSummaryDelta(old, updated)); } catch (_) {}
    const dRef = dayDocRef(old.createdAt);
    if (dRef) {
      const dailyDelta = buildDailyDocDelta(old, updated);
      if (Object.keys(dailyDelta).length > 0) batch.set(dRef, dailyDelta, { merge: true });
    }
  }
  await batch.commit();
  if (updated.orderStatus && updated.orderStatus !== old.orderStatus) {
    await syncTransactionStatuses([
      { orderId: orderObj.id, orderStatus: updated.orderStatus },
    ]);
  }
  const updatedSnapshot = await orderRef.get();
  return updatedSnapshot.data();
};

// Atomic batch: update multiple orders (payments only) + create cash-in entries + one expense entry.
// All writes succeed or all fail together.
// `ordersToUpdate` items: { updatedOrder, patch, dedupe, cashIn }
//   - updatedOrder: full in-memory order (used only to compute pickListFields/summary deltas, never written as-is)
//   - patch: small object actually written to orders/{id}
//   - dedupe: { type: "payment", transactionId } | { type: "orderStatus", value } — used to detect
//     and skip consignments whose effect was already applied by an earlier, partially-committed attempt
//   - cashIn: optional dailyCashIn doc to write alongside this order (omitted for cancel-only items)
export const applyPaymentBatch = async ({ ordersToUpdate, expense }) => {
  // Pre-read ordersList docs — used both for summary/daily deltas and as the
  // idempotency source of truth (see dedupe below).
  const listSnaps = await Promise.all(
    ordersToUpdate.map(({ updatedOrder }) =>
      firestore.doc(`ordersList/${updatedOrder.id}`).get()
    )
  );

  // Aggregate numeric deltas to avoid multiple batch.update() calls on the same doc
  const summaryNum = {};
  const statusChanges = [];
  const dailyNums = {};

  // Collect all writes as plain ops first — Firestore caps a single batch at
  // 500 writes AND ~10MB total payload, and a large Steadfast payment (200+
  // consignments) needs ~3 writes per consignment (orders + ordersList +
  // dailyCashIn), so it must be split across multiple batches instead of one.
  const ops = [];
  let appliedCount = 0;
  let skippedCount = 0;

  ordersToUpdate.forEach(({ updatedOrder, patch, dedupe, cashIn }, i) => {
    const old = listSnaps[i].data() || {};

    // Idempotency guard: if a prior attempt already committed this exact
    // consignment's effect (e.g. a chunk succeeded, then a later chunk failed
    // and the admin retried with a stale client-side preview), skip it
    // entirely rather than re-appending a duplicate payment or re-writing an
    // already-correct orderStatus. This is checked against live Firestore
    // data (listSnaps), not client state, so it's safe regardless of *why*
    // the retry happened.
    if (dedupe) {
      if (dedupe.type === "payment") {
        const already = (old.payments || []).some(
          (p) => String(p.transactionId) === String(dedupe.transactionId)
        );
        if (already) { skippedCount++; return; }
      }
      if (dedupe.type === "orderStatus" && old.orderStatus === dedupe.value) {
        skippedCount++; return;
      }
    }
    appliedCount++;

    const listFields = pickListFields(updatedOrder);
    const oldStatus = old.orderStatus || "";
    const newStatus =
      listFields.orderStatus !== undefined ? listFields.orderStatus : oldStatus;
    if (newStatus && newStatus !== oldStatus) {
      statusChanges.push({ orderId: updatedOrder.id, orderStatus: newStatus });
    }
    const bill = old.totalBill || 0;
    const disc = old.finalDiscount || 0;
    const oldPaid = old.totalPaid || 0;
    const newPaid =
      listFields.totalPaid !== undefined ? listFields.totalPaid : oldPaid;

    if (oldStatus && newStatus && oldStatus !== newStatus) {
      // Status transition (e.g. Steadfast reconciliation moving an order out
      // of "On the way" to "Delivered"/"Partial Delivered"/"Cancelled"): move
      // the whole bucket, not just totalPaid. This used to only shift
      // totalPaid onto the OLD status, leaving count/totalBill/totalDiscount
      // stuck on the old bucket forever and never crediting the new one —
      // that's why "On the way" only ever grew and "Partial Delivered"
      // stayed stuck low relative to the live ordersList count.
      summaryNum[`${oldStatus}.count`] = (summaryNum[`${oldStatus}.count`] || 0) - 1;
      summaryNum[`${oldStatus}.totalBill`] = (summaryNum[`${oldStatus}.totalBill`] || 0) - bill;
      summaryNum[`${oldStatus}.totalPaid`] = (summaryNum[`${oldStatus}.totalPaid`] || 0) - oldPaid;
      summaryNum[`${oldStatus}.totalDiscount`] = (summaryNum[`${oldStatus}.totalDiscount`] || 0) - disc;

      summaryNum[`${newStatus}.count`] = (summaryNum[`${newStatus}.count`] || 0) + 1;
      summaryNum[`${newStatus}.totalBill`] = (summaryNum[`${newStatus}.totalBill`] || 0) + bill;
      summaryNum[`${newStatus}.totalPaid`] = (summaryNum[`${newStatus}.totalPaid`] || 0) + newPaid;
      summaryNum[`${newStatus}.totalDiscount`] = (summaryNum[`${newStatus}.totalDiscount`] || 0) + disc;
    } else {
      const status = oldStatus || newStatus;
      const paidDelta = newPaid - oldPaid;
      if (status && paidDelta !== 0) {
        summaryNum[`${status}.totalPaid`] = (summaryNum[`${status}.totalPaid`] || 0) + paidDelta;
      }
    }

    const oldContribution = paymentDueContribution(old, oldStatus);
    const newContribution = paymentDueContribution(updatedOrder, newStatus);
    Object.assign(listFields, paymentDueFields(updatedOrder, newStatus));
    if (oldContribution) {
      summaryNum["Payment Due.count"] = (summaryNum["Payment Due.count"] || 0) - 1;
      summaryNum["Payment Due.totalBill"] = (summaryNum["Payment Due.totalBill"] || 0) - oldContribution.totalBill;
      summaryNum["Payment Due.totalPaid"] = (summaryNum["Payment Due.totalPaid"] || 0) - oldContribution.totalPaid;
      summaryNum["Payment Due.totalDiscount"] = (summaryNum["Payment Due.totalDiscount"] || 0) - oldContribution.totalDiscount;
    }
    if (newContribution) {
      summaryNum["Payment Due.count"] = (summaryNum["Payment Due.count"] || 0) + 1;
      summaryNum["Payment Due.totalBill"] = (summaryNum["Payment Due.totalBill"] || 0) + newContribution.totalBill;
      summaryNum["Payment Due.totalPaid"] = (summaryNum["Payment Due.totalPaid"] || 0) + newContribution.totalPaid;
      summaryNum["Payment Due.totalDiscount"] = (summaryNum["Payment Due.totalDiscount"] || 0) + newContribution.totalDiscount;
    }

    const dRef = dayDocRef(old.createdAt);
    if (dRef) {
      const key = dRef.path;
      if (!dailyNums[key]) {
        dailyNums[key] = {
          ref: dRef,
          count: 0,
          totalBill: 0,
          totalPaid: 0,
          totalDiscount: 0,
          cancelledCount: 0,
          cancelledBill: 0,
        };
      }
      const nd = dailyNums[key];
      if (oldStatus && newStatus && oldStatus !== newStatus) {
        const oldCancelled = oldStatus === "Cancelled";
        const newCancelled = newStatus === "Cancelled";
        if (oldCancelled) {
          nd.cancelledCount -= 1;
          nd.cancelledBill -= bill;
        } else {
          nd.count -= 1;
          nd.totalBill -= bill;
          nd.totalPaid -= oldPaid;
          nd.totalDiscount -= disc;
        }
        if (newCancelled) {
          nd.cancelledCount += 1;
          nd.cancelledBill += bill;
        } else {
          nd.count += 1;
          nd.totalBill += bill;
          nd.totalPaid += newPaid;
          nd.totalDiscount += disc;
        }
      } else {
        const paidDelta = newPaid - oldPaid;
        if (paidDelta !== 0) nd.totalPaid += paidDelta;
      }
    }

    // Only write the fields that actually changed — sending the full order
    // back (product data, images, addresses, etc.) for every consignment is
    // what blows past Firestore's 10MB per-request payload limit on large
    // payments.
    ops.push({ type: "update", ref: firestore.doc(`orders/${updatedOrder.id}`), data: patch || updatedOrder });
    if (Object.keys(listFields).length > 0) {
      ops.push({ type: "set", ref: firestore.doc(`ordersList/${updatedOrder.id}`), data: listFields, options: { merge: true } });
    }
    if (cashIn) {
      ops.push({ type: "set", ref: firestore.doc(`dailyCashIn/${cashIn.id}`), data: cashIn });
    }
  });

  const summaryUpdate = {};
  Object.entries(summaryNum).forEach(([k, v]) => { if (v !== 0) summaryUpdate[k] = inc(v); });
  if (Object.keys(summaryUpdate).length > 0) {
    try { ops.push({ type: "update", ref: SUMMARY_DOC(), data: summaryUpdate }); } catch (_) {}
  }

  Object.values(dailyNums).forEach(
    ({ ref, count, totalBill, totalPaid, totalDiscount, cancelledCount, cancelledBill }) => {
      const delta = {};
      if (count !== 0) delta.count = inc(count);
      if (totalBill !== 0) delta.totalBill = inc(totalBill);
      if (totalPaid !== 0) delta.totalPaid = inc(totalPaid);
      if (totalDiscount !== 0) delta.totalDiscount = inc(totalDiscount);
      if (cancelledCount !== 0) delta.cancelledCount = inc(cancelledCount);
      if (cancelledBill !== 0) delta.cancelledBill = inc(cancelledBill);
      if (Object.keys(delta).length > 0) ops.push({ type: "set", ref, data: delta, options: { merge: true } });
    },
  );

  ops.push({ type: "set", ref: firestore.doc(`dailyExpenses/${expense.id}`), data: expense });

  // Chunk on whichever limit hits first — op count (Firestore's hard 500 cap)
  // or estimated byte size (Firestore's ~10MB per-request cap). Byte size is
  // measured in actual UTF-8 bytes via TextEncoder, not JS string `.length`
  // (UTF-16 code units) — this app routinely has Bengali text in customer
  // names/addresses/notes, which encodes to ~3 bytes per character in UTF-8
  // but only counts as 1 toward `.length`, so `.length`-based estimates can
  // undercount the real wire payload by up to ~3x. Thresholds are kept well
  // under Firestore's hard limits to leave headroom for protobuf/index
  // overhead beyond raw JSON bytes.
  const MAX_OPS_PER_CHUNK = 250;
  const MAX_BYTES_PER_CHUNK = 4 * 1024 * 1024; // 4MB
  const textEncoder = new TextEncoder();
  const estimateBytes = (data) => {
    try { return textEncoder.encode(JSON.stringify(data)).length; } catch (_) { return 0; }
  };

  const chunks = [];
  let current = [];
  let currentBytes = 0;
  ops.forEach((op) => {
    const size = estimateBytes(op.data);
    if (current.length > 0 && (current.length >= MAX_OPS_PER_CHUNK || currentBytes + size > MAX_BYTES_PER_CHUNK)) {
      chunks.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(op);
    currentBytes += size;
  });
  if (current.length > 0) chunks.push(current);

  for (const chunkOps of chunks) {
    const batch = firestore.batch();
    chunkOps.forEach((op) => {
      if (op.type === "update") batch.update(op.ref, op.data);
      else batch.set(op.ref, op.data, op.options);
    });
    await batch.commit();
  }

  await syncTransactionStatuses(statusChanges);
  return { appliedCount, skippedCount };
};

export const updateMultipleOrder = async (ids, orderStatus) =>
  bulkUpdateOrderStatus(ids, orderStatus);

export const updateMultipleOrderwithOrderTo = async (ids, orderStatus, orderTo) => {
  const oldSnaps = await Promise.all(ids.map((id) => firestore.doc(`ordersList/${id}`).get()));

  // See the matching comment in bulkUpdateOrderStatus — some ordersList docs
  // are missing createdAt, which makes them invisible to date-ordered list
  // queries but still counted by a full rescan. Backfill it here.
  const missingCreatedAtIds = ids.filter((id, i) => !oldSnaps[i].data()?.createdAt);
  const backfillCreatedAt = {};
  if (missingCreatedAtIds.length > 0) {
    const orderSnaps = await Promise.all(
      missingCreatedAtIds.map((id) => firestore.doc(`orders/${id}`).get())
    );
    missingCreatedAtIds.forEach((id, i) => {
      backfillCreatedAt[id] =
        orderSnaps[i].data()?.createdAt || firebase.firestore.FieldValue.serverTimestamp();
    });
  }

  const batch = firestore.batch();
  const numDelta = {};
  const dailyNumDeltas = {};

  ids.forEach((id, i) => {
    batch.update(firestore.doc(`orders/${id}`), { orderStatus, orderTo });
    const listPatch = {
      orderStatus,
      ...paymentDueFields(oldSnaps[i].data() || {}, orderStatus),
    };
    if (backfillCreatedAt[id]) listPatch.createdAt = backfillCreatedAt[id];
    batch.set(firestore.doc(`ordersList/${id}`), listPatch, { merge: true });

    const old = oldSnaps[i].data() || {};
    const oldStatus = old.orderStatus || "";
    const bill = old.totalBill || 0, paid = old.totalPaid || 0, disc = old.finalDiscount || 0;

    // Only touch the counters when the status is actually changing — see
    // bulkUpdateOrderStatus for why the increment must be gated the same as
    // the decrement instead of firing unconditionally.
    if (oldStatus !== orderStatus) {
      if (oldStatus) {
        numDelta[`${oldStatus}.count`]         = (numDelta[`${oldStatus}.count`]         || 0) - 1;
        numDelta[`${oldStatus}.totalBill`]     = (numDelta[`${oldStatus}.totalBill`]     || 0) - bill;
        numDelta[`${oldStatus}.totalPaid`]     = (numDelta[`${oldStatus}.totalPaid`]     || 0) - paid;
        numDelta[`${oldStatus}.totalDiscount`] = (numDelta[`${oldStatus}.totalDiscount`] || 0) - disc;
      }
      numDelta[`${orderStatus}.count`]         = (numDelta[`${orderStatus}.count`]         || 0) + 1;
      numDelta[`${orderStatus}.totalBill`]     = (numDelta[`${orderStatus}.totalBill`]     || 0) + bill;
      numDelta[`${orderStatus}.totalPaid`]     = (numDelta[`${orderStatus}.totalPaid`]     || 0) + paid;
      numDelta[`${orderStatus}.totalDiscount`] = (numDelta[`${orderStatus}.totalDiscount`] || 0) + disc;

      const oldContribution = paymentDueContribution(old, oldStatus);
      const newContribution = paymentDueContribution(old, orderStatus);
      if (oldContribution) {
        numDelta["Payment Due.count"] = (numDelta["Payment Due.count"] || 0) - 1;
        numDelta["Payment Due.totalBill"] = (numDelta["Payment Due.totalBill"] || 0) - oldContribution.totalBill;
        numDelta["Payment Due.totalPaid"] = (numDelta["Payment Due.totalPaid"] || 0) - oldContribution.totalPaid;
        numDelta["Payment Due.totalDiscount"] = (numDelta["Payment Due.totalDiscount"] || 0) - oldContribution.totalDiscount;
      }
      if (newContribution) {
        numDelta["Payment Due.count"] = (numDelta["Payment Due.count"] || 0) + 1;
        numDelta["Payment Due.totalBill"] = (numDelta["Payment Due.totalBill"] || 0) + newContribution.totalBill;
        numDelta["Payment Due.totalPaid"] = (numDelta["Payment Due.totalPaid"] || 0) + newContribution.totalPaid;
        numDelta["Payment Due.totalDiscount"] = (numDelta["Payment Due.totalDiscount"] || 0) + newContribution.totalDiscount;
      }

      const dRef = dayDocRef(old.createdAt);
      if (dRef) {
        const path = dRef.path;
        if (!dailyNumDeltas[path]) dailyNumDeltas[path] = { ref: dRef, n: { count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0, cancelledCount: 0, cancelledBill: 0 } };
        const nd = dailyNumDeltas[path].n;
        const oldCancelled = oldStatus === "Cancelled";
        const newCancelled = orderStatus === "Cancelled";
        if (oldCancelled) { nd.cancelledCount -= 1; nd.cancelledBill -= bill; }
        else              { nd.count -= 1; nd.totalBill -= bill; nd.totalPaid -= paid; nd.totalDiscount -= disc; }
        if (newCancelled) { nd.cancelledCount += 1; nd.cancelledBill += bill; }
        else              { nd.count += 1; nd.totalBill += bill; nd.totalPaid += paid; nd.totalDiscount += disc; }
      }
    }
  });

  const summaryUpdate = {};
  Object.entries(numDelta).forEach(([k, v]) => { if (v !== 0) summaryUpdate[k] = inc(v); });
  if (Object.keys(summaryUpdate).length > 0) {
    try { batch.update(SUMMARY_DOC(), summaryUpdate); } catch (_) {}
  }

  Object.values(dailyNumDeltas).forEach(({ ref, n }) => {
    const delta = {};
    Object.entries(n).forEach(([k, v]) => { if (v !== 0) delta[k] = inc(v); });
    if (Object.keys(delta).length > 0) batch.set(ref, delta, { merge: true });
  });

  await batch.commit();
  await syncTransactionStatuses(
    ids
      .filter((id, i) => (oldSnaps[i].data() || {}).orderStatus !== orderStatus)
      .map((orderId) => ({ orderId, orderStatus })),
  );
};

// paymet management
export const getAllPayments = async () => {
  const paymentsCollectionRef = firestore.collection("payments");
  try {
    const payments = await paymentsCollectionRef.get();
    const paymentsArray = [];
    payments.forEach((doc) => {
      paymentsArray.push({ uid: doc.id, ...doc.data() });
    });
    return paymentsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deletePayment = async (orderId) => {
  const paymentRef = firestore.doc(`payments/${orderId}`);
  try {
    await paymentRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updatePaymentStatus = async (paymentObj) => {
  const paymentRef = firestore.doc(`payments/${paymentObj.orderId}`);
  const payment = await paymentRef.get();
  var actualPayment = payment
    .data()
    .payments.find((payment) => payment.paymentId == paymentObj.paymentId);
  const orderId = actualPayment.orderId;
  const orderRef = firestore.doc(`orders/${orderId}`);
  const order = await orderRef.get();
  const userId = await order.data().userId;
  const userRef = firestore.doc(`users/${userId}`);
  const user = await userRef.get();
  var exactPayment = user
    .data()
    .paymentsArray.find((payment) => payment.paymentId == paymentObj.paymentId);
  exactPayment.paymentStatus = paymentObj.paymentStatus;
  var otherPayments = user
    .data()
    .paymentsArray.filter(
      (payment) => payment.paymentId !== paymentObj.paymentId,
    );

  await userRef.update({ paymentsArray: [exactPayment, ...otherPayments] });
  const updatedPaymentObj = payment
    .data()
    .payments.find((payment) => payment.paymentId == paymentObj.paymentId);
  updatedPaymentObj.paymentStatus = paymentObj.paymentStatus;
  const newPaymentsArray = payment
    .data()
    .payments.filter((payment) => payment.paymentId !== paymentObj.paymentId);
  try {
    await paymentRef.update({
      ...payment.data(),
      payments: [...newPaymentsArray, updatedPaymentObj],
    });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updateOrderAmount = async (paymentObj) => {
  try {
    const orderRef = firestore.doc(`orders/${paymentObj.orderId}`);
    const order = await orderRef.get();
    const newPaymentStatus = {
      ...order.data().paymentStatus,
      due:
        parseInt(order.data().paymentStatus.total) -
        (parseInt(order.data().paymentStatus.paid) + parseInt(paymentObj.amount)),
      paid:
        parseInt(order.data().paymentStatus.paid) + parseInt(paymentObj.amount),
    };
    const updateData = {
      ...order.data(),
      status:
        order.data().status === "order_pending"
          ? "payment_approved"
          : order.data().status,
      paymentStatus: newPaymentStatus,
    };
    const listSnap = await firestore.doc(`ordersList/${paymentObj.orderId}`).get();
    const listData = listSnap.data() || {};
    const currentStatus = listData.orderStatus || "Processing";
    const oldTotalPaid = listData.totalPaid || 0;
    const newTotalPaid = parseInt(newPaymentStatus.paid) || 0;
    const deltaPaid = newTotalPaid - oldTotalPaid;
    const batch = firestore.batch();
    batch.update(orderRef, updateData);
    const updatedListData = { ...listData, paymentStatus: newPaymentStatus, totalPaid: newTotalPaid };
    batch.set(
      firestore.doc(`ordersList/${paymentObj.orderId}`),
      {
        paymentStatus: newPaymentStatus,
        totalPaid: newTotalPaid,
        ...paymentDueFields(updatedListData, currentStatus),
      },
      { merge: true },
    );
    const oldContribution = paymentDueContribution(listData, currentStatus);
    const newContribution = paymentDueContribution(updatedListData, currentStatus);
    const paymentDueUpdate = {};
    const addPD = (field, amount) => {
      if (!amount) return;
      const key = `Payment Due.${field}`;
      paymentDueUpdate[key] = (paymentDueUpdate[key] || 0) + amount;
    };
    if (oldContribution) {
      addPD("count", -1);
      addPD("totalBill", -oldContribution.totalBill);
      addPD("totalPaid", -oldContribution.totalPaid);
      addPD("totalDiscount", -oldContribution.totalDiscount);
    }
    if (newContribution) {
      addPD("count", 1);
      addPD("totalBill", newContribution.totalBill);
      addPD("totalPaid", newContribution.totalPaid);
      addPD("totalDiscount", newContribution.totalDiscount);
    }
    const summaryPatch = { [`${currentStatus}.totalPaid`]: inc(deltaPaid) };
    Object.entries(paymentDueUpdate).forEach(([k, v]) => { summaryPatch[k] = inc(v); });
    try { batch.update(SUMMARY_DOC(), summaryPatch); } catch (_) {}
    if (currentStatus !== "Cancelled") {
      const dRef = dayDocRef(listData.createdAt);
      if (dRef) batch.set(dRef, { totalPaid: inc(deltaPaid) }, { merge: true });
    }
    await batch.commit();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// distribute order to managers
export const orderProductsFromChina = async (orderIdArray, orderTo) => {
  const adminsCollectionRef = firestore.collection("admins");
  orderIdArray.forEach(async (orderId) => {
    const orderRef = firestore.doc(`orders/${orderId}`);
    try {
      const order = await orderRef.get();
      if (!order.data().orderTo) {
        try {
          const admins = await adminsCollectionRef.get();
          admins.forEach((doc) => {
            var adminRef = firestore.doc(`admins/${doc.id}`);
            if (doc.data().name == orderTo) {
              adminRef.update({
                ...doc.data(),
                pending_orders: [...doc.data().pending_orders, orderId],
              });
              return;
            }
          });
        } catch (error) {
          notifyError(error.message || String(error));
        }
      } else {
        console.log(
          `${orderId} is already ordered to another supplier.check ordered item`,
        );
      }
    } catch (error) {
      console.log(error);
    }
  });
};

export const productToOrder = async (productsArray) => {
  productsArray.forEach((product) => {
    const productToOrderRef = firestore.doc(`productToOrder/${product.id}`);
    try {
      productToOrderRef.set(product);
    } catch (error) {
      notifyError(error.message || String(error));
    }
  });
};

// admins
export const getAllAdmins = async () => {
  const adminsCollectionRef = firestore.collection("admins");
  try {
    const admins = await adminsCollectionRef.get();
    const adminsArray = [];
    admins.forEach((doc) => {
      adminsArray.push({ adminId: doc.id, ...doc.data() });
    });
    return adminsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const rechargeAdmin = async (adminIdArray, balance) => {
  adminIdArray.forEach(async (adminId) => {
    const adminRef = firestore.doc(`admins/${adminId}`);
    try {
      const admin = await adminRef.get();
      var total_balance = parseInt(admin.data().balance) + parseInt(balance);
      await adminRef.update({
        ...admin.data(),
        balance: admin.data().balance
          ? parseInt(admin.data().balance) + parseInt(balance)
          : parseInt(balance),
        remaining_balance: total_balance - parseInt(admin.data().used_balance),
      });
    } catch (error) {
      notifyError(error.message || String(error));
    }
  });
};

export const updateProfileImage = async (imgUrl, id) => {
  const adminRef = firestore.doc(`admins/${id}`);
  try {
    const admin = await adminRef.get();
    await adminRef.update({ ...admin.data(), image: imgUrl });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const setRmbPrice = async (taka) => {
  const currencyRef = firestore.doc(`Currency/taka`);
  let rmbRate;
  if (taka) {
    try {
      const currency = await currencyRef.get();
      if (currency.data()) {
        await currencyRef.update({ ...currency.data(), taka });
        rmbRate = taka;
        return rmbRate;
      } else {
        await currencyRef.set({ taka: taka });
        rmbRate = taka;
        return rmbRate;
      }
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    try {
      const currency = await currencyRef.get();
      rmbRate = currency.data().taka;
      return rmbRate;
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }
};

export const getAllProductRequests = async (status) => {
  const reqeustsCollectionRef = firestore
    .collection("bookingRequest")
    .where("status", "==", status);

  try {
    const requests = await reqeustsCollectionRef.get();
    const requestsArray = [];
    requests.forEach((doc) => {
      requestsArray.push(doc.data());
    });
    return requestsArray.sort((a, b) => b.time - a.time);
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllShipmentRequests = async (status) => {
  const reqeustsCollectionRef = firestore
    .collection("shipForMe")
    .where("status", "==", status);

  try {
    const requests = await reqeustsCollectionRef.get();
    const requestsArray = [];
    requests.forEach((doc) => {
      requestsArray.push(doc.data());
    });
    return requestsArray.sort((a, b) => b.time - a.time);
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const updateProductRequest = async (requestObj) => {
  const reqeustRef = firestore.doc(`bookingRequest/${requestObj.bookingId}`);

  try {
    await reqeustRef.update({
      ...requestObj,
    });
    const request = await reqeustRef.get();
    return request.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const updateProduct = async (productObj) => {
  const productRef = firestore.doc(`aliproducts/${productObj.id}`);

  try {
    await productRef.update({
      ...productObj,
    });
    const product = await productRef.get();
    return product.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const updateProductInventory = async (productObj) => {
  const productRef = firestore.doc(`products/${productObj.productId}`);
  let product = await productRef.get();
  console.log(product.data());
  console.log(productObj);
  try {
    if (productObj.selectedVariation) {
      await productRef.update({
        displayedVariations: product
          .data()
          .displayedVariations.map((variation) => {
            if (variation.id == productObj.selectedVariation.id) {
              console.log(variation.id);
              console.log(productObj.selectedVariation.id);
              return {
                ...variation,
                inventory:
                  parseInt(variation.inventory) - parseInt(productObj.quantity),
              };
            } else {
              return variation;
            }
          }),
      });
    } else {
      await productRef.update({
        inventory: product.data().inventory
          ? product.data().inventory - productObj.quantity
          : 0,
      });
    }

    const updated = await productRef.get();
    if (updated.exists) {
      try {
        await firestore
          .collection("productList")
          .doc(productObj.productId)
          .set(
            stripProductForList({ id: productObj.productId, ...updated.data() }),
            { merge: true },
          );
      } catch (e) {
        console.error("productList sync failed", e);
      }
    }
    return product.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const updateReview = async (reviewObj) => {
  const productRef = firestore.doc(`products/${reviewObj.productId}`);
  const product = await productRef.get();

  try {
    await productRef.update({
      ...product.data(),
      reviews: product.data().reviews.map((review) => {
        if (review.id == reviewObj.id) {
          return reviewObj;
        } else {
          return review;
        }
      }),
    });
    const updatedProduct = await productRef.get();
    return updatedProduct.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const updateShipmentRequest = async (requestObj) => {
  const reqeustRef = firestore.doc(`shipForMe/${requestObj.bookingId}`);

  try {
    await reqeustRef.update({
      ...requestObj,
    });
    const request = await reqeustRef.get();
    return request.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllPaymentRequest = async () => {
  const paymentRequestCollectionRef = firestore
    .collection("paymentRequest")
    .where("status", "==", "pending");

  try {
    const paymentRequest = await paymentRequestCollectionRef.get();
    const paymentRequestArray = [];
    paymentRequest.forEach((doc) => {
      paymentRequestArray.push(doc.data());
    });
    console.log(paymentRequestArray);
    return paymentRequestArray.sort((a, b) => b.time - a.time);
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllPaymentRequestOrder = async () => {
  const paymentRequestCollectionRef = firestore
    .collection("paymentRequestApi")
    .where("status", "==", "pending");

  try {
    const paymentRequest = await paymentRequestCollectionRef.get();
    const paymentRequestArray = [];
    paymentRequest.forEach((doc) => {
      paymentRequestArray.push(doc.data());
    });
    console.log(paymentRequestArray);
    return paymentRequestArray.sort((a, b) => b.time - a.time);
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updatePaymentRequestStatus = async (paymentRequestObj) => {
  console.log(paymentRequestObj);
  const paymentRequestRef = firestore.doc(
    `paymentRequest/${paymentRequestObj.paymentId}`,
  );
  const snapshot = await paymentRequestRef.get();
  if (snapshot.exists) {
    try {
      paymentRequestObj.productRequestArray.forEach(async (productRequest) => {
        if (productRequest.shipFrom) {
          const bookingRequestRef = firestore.doc(
            `shipForMe/${productRequest.bookingId}`,
          );
          const bookingRequest = await bookingRequestRef.get();
          await bookingRequestRef.update({
            paymentStatus: paymentRequestObj.status,
            status:
              paymentRequestObj.status === "paid"
                ? "Paid"
                : bookingRequest.data().status,
          });
        } else {
          const bookingRequestRef = firestore.doc(
            `bookingRequest/${productRequest.bookingId}`,
          );
          const bookingRequest = await bookingRequestRef.get();
          await bookingRequestRef.update({
            paymentStatus: paymentRequestObj.status,
            status:
              paymentRequestObj.status === "paid"
                ? "Paid"
                : bookingRequest.data().status,
          });
        }
      });
      await paymentRequestRef.update({
        status: paymentRequestObj.status,
      });
      const updatedPaymentRequestObj = await paymentRequestRef.get();
      return updatedPaymentRequestObj.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    return null;
  }
};
export const updatePaymentRequestOrderStatus = async (paymentRequestObj) => {
  console.log(paymentRequestObj);
  const paymentRequestRef = firestore.doc(
    `paymentRequestApi/${paymentRequestObj.paymentId}`,
  );
  const snapshot = await paymentRequestRef.get();
  if (snapshot.exists) {
    try {
      for (let i = 0; i < paymentRequestObj.pendingOrders.length; i++) {
        const element = paymentRequestObj.pendingOrders[i];
        const orderRef = firestore.doc(`ordersApi/${element.orderId}`);

        const order = await orderRef.get();
        await orderRef.update({
          orderStatus: paymentRequestObj.status,
          paymentStatus:
            paymentRequestObj.status == "approved"
              ? "paid"
              : paymentRequestObj.status,
        });
      }
      await paymentRequestRef.update({
        status: paymentRequestObj.status,
      });
      const updatedPaymentRequestObj = await paymentRequestRef.get();
      return updatedPaymentRequestObj.data();
    } catch (error) {
      notifyError(error.message || String(error));
      return null;
    }
  } else {
    return null;
  }
};

const normalizeOrderApiDoc = (data) => {
  if (!data) return data;
  const tsMs = data.createdAt
    ? data.createdAt.toMillis()
    : data.createdAt
      ? data.createdAt.toMillis()
      : data.date
        ? parseInt(data.date)
        : data.time || 0;
  const orderedDate =
    data.orderedDate ||
    (data.createdAt
      ? data.createdAt.toDate().toLocaleDateString("en-GB")
      : data.createdAt
        ? data.createdAt.toDate().toLocaleDateString("en-GB")
        : "");
  return {
    ...data,
    orderStatus: data.orderStatus || "",
    orderTotal:
      data.orderTotal !== undefined
        ? data.orderTotal
        : data.subTotal || data.total || 0,
    localShipping:
      data.localShipping !== undefined
        ? data.localShipping
        : data.deliveryCharge || 0,
    time: data.time || tsMs,
    orderedDate,
    deliveryAddress:
      data.deliveryAddress ||
      (data.shippingAddress
        ? {
            name:
              data.shippingAddress.fullName || data.shippingAddress.name || "",
            mobileNo: data.shippingAddress.mobileNo || "",
            address: data.shippingAddress.address || "",
            district: data.shippingAddress.district || "",
            division: data.shippingAddress.division || "",
          }
        : null),
  };
};

export const getAllOrdersApi = async (orderStatus, startDate, endDate) => {
  let ordersCollectionRef;

  // "YYYY-MM-DD" alone parses as UTC midnight; the explicit time makes it the
  // admin's local midnight so the range covers whole local days.
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);
  const startTs = firebase.firestore.Timestamp.fromDate(start);
  const endTs = firebase.firestore.Timestamp.fromDate(end);

  if (orderStatus === "Picked") {
    console.log("fetching picked orders");
    ordersCollectionRef = firestore
      .collection("ordersApi")
      .where("agentStatus", "==", "Picked")
      .where("orderStatus", "==", "pending")
      .where("createdAt", ">=", startTs)
      .where("createdAt", "<=", endTs);
  } else if (orderStatus === "") {
    console.log("fetching all orders");
    ordersCollectionRef = firestore
      .collection("ordersApi")
      .where("createdAt", ">=", startTs)
      .where("createdAt", "<=", endTs);
  } else {
    console.log(`fetching ${orderStatus} orders`);
    ordersCollectionRef = firestore
      .collection("ordersApi")
      .where("orderStatus", "==", orderStatus)
      .where("createdAt", ">=", startTs)
      .where("createdAt", "<=", endTs);
  }

  try {
    const orders = await ordersCollectionRef.get();
    const ordersArray = [];
    orders.forEach((doc) => {
      ordersArray.push(normalizeOrderApiDoc(doc.data()));
    });
    return ordersArray;
  } catch (error) {
    console.error("getAllOrdersApi error:", error);
    notifyError(error.message || String(error));
    return [];
  }
};

export const updateAllOrdersApi = () => {
  firestore
    .collection("ordersApi")
    .get()
    .then((snapshot) => {
      snapshot.forEach((doc) => {
        doc.ref.update({
          createdAt: firebase.firestore.Timestamp.fromMillis(doc.data().time),
        });
      });
    });
};

export const updateOrdersWithTrackingNo = async (orders) => {
  try {
    const batch = firebase.firestore().batch();
    orders.forEach((order) => {
      const orderRef = firebase
        .firestore()
        .collection("ordersApi")
        .doc(`${order.orderId}`);
      batch.update(orderRef, { trackingNo: order.trackingNo });
    });
    await batch.commit();
    return orders;
  } catch (err) {
    console.log(err);
    return [];
  }
};
export const getAllRooms = async () => {
  const roomsCollectionRef = firestore.collection("rooms");

  try {
    const rooms = await roomsCollectionRef.get();
    const roomsArray = [];
    rooms.forEach((doc) => {
      roomsArray.push(doc.data());
    });
    return roomsArray.sort((a, b) => b.time - a.time);
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const readAllMessage = async (roomId) => {
  const roomRef = firestore.doc(`rooms/${roomId}`);
  const room = await roomRef.get();
  roomRef.update({
    messages: room.data().messages.map((message) => {
      if (message.user._id == roomId) {
        return { ...message, sent: true, received: true };
      } else {
        return message;
      }
    }),
    lastMessage: { ...room.data().lastMessage, sent: true, received: true },
  });
};

// Atomically changes one product's (or one variation's) stock by `delta`
// and returns the stock right after the change — the value every
// transaction's remainingStock must record.
//
// Stock used to be changed by reading the product, computing
// "inventory ± qty" in the browser, and writing the whole product back.
// Two writers overlapping (two checkouts, a checkout and an admin edit, a
// burst of duplicate orders) both read the same number, so one change was
// silently lost: the shelf count drifted up and consecutive report rows
// showed the same remaining stock. A Firestore transaction re-reads and
// retries on conflict, so every change lands.
//
// soldDelta defaults to -delta (selling 2 → inventory -2, totalSold +2).
// Returns null if the product (or variation) no longer exists.
export const adjustProductStock = async ({
  productId,
  variationId = null,
  delta,
  soldDelta = -delta,
}) => {
  const ref = firestore.doc(`products/${productId}`);
  const result = await firestore.runTransaction(async (t) => {
    const snap = await t.get(ref);
    if (!snap.exists) return null;
    const product = snap.data();
    const bump = (n, by) => (parseInt(n) || 0) + by;
    if (variationId && product.displayedVariations?.length > 0) {
      let remaining = null;
      const displayedVariations = product.displayedVariations.map((v) => {
        if (String(v.id) !== String(variationId)) return v;
        remaining = bump(v.inventory, delta);
        return {
          ...v,
          inventory: remaining,
          totalSold: Math.max(0, bump(v.totalSold, soldDelta)),
        };
      });
      if (remaining === null) return null;
      t.update(ref, { displayedVariations });
      return { remaining, product: { ...product, id: productId, displayedVariations } };
    }
    const remaining = bump(product.inventory, delta);
    const totalSold = Math.max(0, bump(product.totalSold, soldDelta));
    t.update(ref, { inventory: remaining, totalSold });
    return {
      remaining,
      product: { ...product, id: productId, inventory: remaining, totalSold },
    };
  });
  if (result) {
    try {
      await firestore
        .collection("productList")
        .doc(productId)
        .set(stripProductForList(result.product), { merge: true });
    } catch (e) {
      console.error("productList sync failed", e);
    }
  }
  return result;
};

export const updateSingleProduct = async (product) => {
  const productRef = firestore.doc(`products/${product.id}`);
  console.log(product);
  try {
    await productRef.update({ ...product });
    const productSnapshot = await productRef.get();
    if (productSnapshot.exists) {
      try {
        await firestore
          .collection("productList")
          .doc(product.id)
          .set(
            stripProductForList({ id: product.id, ...productSnapshot.data() }),
            { merge: true },
          );
      } catch (e) {
        console.error("productList sync failed", e);
      }
    }
    return productSnapshot.data();
  } catch (error) {
    notifyError(error.message);
    console.log(error);
  }
};
export const updateAdminStatus = async (adminObj) => {
  const adminRef = firestore.doc(`admins/${adminObj.adminId}`);
  try {
    const snapShot = await adminRef.get();
    console.log(snapShot.data());
    await adminRef.update({
      status: adminObj.status,
      activeStatus: adminObj.activeStatus,
    });
    const updatedSnapShot = await adminRef.get();
    return { ...updatedSnapShot.data(), adminId: updatedSnapShot.id };
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const updateOrderApi = async (order) => {
  const orderRef = firestore.doc(`ordersApi/${order.orderId}`);
  const snapShot = await orderRef.get();
  if (snapShot.exists) {
    try {
      await orderRef.update({ ...order });
      const updatedOrder = await orderRef.get();
      return updatedOrder.data();
    } catch (error) {
      console.log("error creating orders", error.message);
      return;
    }
  } else {
    notifyError("No order was found to be updated");
  }
};

export const getCurrency = async () => {
  const currencyRef = firestore.doc("Currency/taka");
  try {
    const currency = await currencyRef.get();
    return currency.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updateCurrency = async ({ taka, shippingFee }) => {
  const currencyRef = firestore.doc("Currency/taka");
  try {
    await currencyRef.set({ taka, shippingFee }, { merge: true });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const makePayment = async (
  total,
  invoicesToPay,
  currentUser,
  admin,
  parcelsArray,
  paymentMethod,
) => {
  try {
    const res = await firestore.runTransaction(async (t) => {
      //first create a payment object
      const paymentObj = {
        paymentId: Math.floor(Math.random() * Date.now()),
        paidAt: new Date().toLocaleDateString("en-US").replaceAll("/", "-"),
        amount: total,
        paymentMethod,
        paidInvoice: [...invoicesToPay],
        approvedBy: admin.name,
      };

      // for transaction all reads should be done before all writes
      const paymentDayRef = firestore.doc(`paymentDays/${paymentObj.paidAt}`);
      const paymentDay = await t.get(paymentDayRef);
      const paymentHistoryRef = firestore.doc(
        `paymentHistory/${paymentObj.paymentId}`,
      );
      const paymentHistory = await t.get(paymentHistoryRef);
      // updatating the status invoiceStatus=Paid in parcelArray in admin
      parcelsArray.forEach(async (parcel) => {
        const orderRef = firestore.doc(`orders/${parcel.parcelId}`);
        await t.update(orderRef, {
          ...parcel,
          invoiceStatus: "Paid",
        });
      });

      // make all writes
      // make a payment in paymentdays
      console.log(paymentDay.data());
      const day = getDay();
      if (!paymentDay.exists) {
        t.set(paymentDayRef, {
          date: paymentObj.paidAt,
          total: total,
          day,
        });
      } else {
        t.update(paymentDayRef, {
          total: paymentDay.data().total + total,
        });
      }
      console.log(paymentDay.data());

      // make a payment in paymentHistory

      console.log(paymentHistory.data());
      if (!paymentHistory.exists) {
        t.set(paymentHistoryRef, {
          Email: currentUser
            ? currentUser.email && currentUser.email
            : admin.email,
          Name: currentUser
            ? currentUser.displayName && currentUser.displayName
            : admin.name,
          Id: currentUser ? currentUser.userId : "admin",
          uid: currentUser ? currentUser.uid : admin.adminId,
          Mobile: currentUser
            ? currentUser.mobileNo
              ? currentUser.mobileNo
              : ""
            : admin.mobileNo,
          ...paymentObj,
          day,
        });
      } else {
        notifyError("Your paymentId already exist. please try again later.");
      }
      console.log(paymentHistory.data());

      const newArray = parcelsArray.map((parcel) => {
        return { ...parcel, invoiceStatus: "Paid" };
      });

      console.log(parcelsArray[0]);
      console.log(newArray[0]);
      return newArray;
    });
    return res;
  } catch (e) {
    console.log("Transaction failure:", e);
    notifyError("Transaction failure");
    return [];
  }
};

const getDay = () => {
  const t = new Date();
  const dayInDigit = t.getDay();
  let day;
  if (dayInDigit == 0) {
    day = "Sunday";
  }
  if (dayInDigit == 1) {
    day = "Monday";
  }
  if (dayInDigit == 2) {
    day = "Tuesday";
  }
  if (dayInDigit == 3) {
    day = "Wednesday";
  }
  if (dayInDigit == 4) {
    day = "Thursday";
  }
  if (dayInDigit == 5) {
    day = "Friday";
  }
  if (dayInDigit == 6) {
    day = "Saturday";
  }
  return day;
};

export const getMultipleOrders = async (parcelIdArray) => {
  const ordersArray = [];
  for (let i = 0; i < parcelIdArray.length; i++) {
    const orderRef = firestore.doc(`orders/${parcelIdArray[i]}`);
    const snapShot = await orderRef.get();
    ordersArray.push(snapShot.data());
  }
  return ordersArray;
};

export const getAllBrands = async () => {
  const productsCollectionRef = firestore.collection("brands");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadBrand = async (productObj) => {
  const productRef = firestore.doc(`brands/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a brand with similar id");
  }
};
export const uploadfreeShipping = async (value) => {
  const productRef = firestore.doc(`freeShipping/freeShipping`);
  const snapShot = await productRef.get();

  if (!snapShot.exists) {
    try {
      await productRef.set({
        value: value,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    try {
      await productRef.update({
        value: value,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }
};

export const updateBrand = async (productObj) => {
  const productRef = firestore.doc(`brands/${productObj.id}`);
  const product = await productRef.get();
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteBrand = async (id, parentId) => {
  const productRef = firestore.doc(`brands/${id}`);
  const childBrandsRef = firestore
    .collection(`brands`)
    .where("parentBrand", "==", id);
  const childBrands = await childBrandsRef.get();
  try {
    childBrands.forEach(async (doc) => {
      const productRef = firestore.doc(`brands/${doc.data().id}`);
      await productRef.update({
        parentBrand: parentId,
      });
    });
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllCoupons = async () => {
  const productsCollectionRef = firestore.collection("coupons");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadCoupon = async (productObj) => {
  const productRef = firestore.doc(`coupons/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a coupon with similar id");
  }
};

export const updateCoupon = async (productObj) => {
  const productRef = firestore.doc(`coupons/${productObj.id}`);
  const product = await productRef.get();
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteCoupon = async (id) => {
  const productRef = firestore.doc(`coupons/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCategories = async () => {
  const productsCollectionRef = firestore.collection("categories");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllHomeScreenCategories = async () => {
  const productsCollectionRef = firestore
    .collection("categories")
    .where("homePage", "==", true);

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// Homescreen order lives in categories/{id}.homePosition. Rewrites every
// category so the homescreen ones are numbered exactly 1..n (no gaps, no
// duplicates, always numbers) and every other category has
// homePosition: false. If `placeId` is on the homescreen it's moved to
// `wantedPosition` (clamped to 1..n) and the rest shift around it, keeping
// their relative order. The old per-save +1/-1/swap updates ran un-awaited,
// compared string positions as text ("10" < "9"), and never cleared the
// position of a category taken off the homescreen — that's how duplicate and
// leftover positions (e.g. two categories at 10) crept in.
const normalizeHomePositions = async (placeId = null, wantedPosition = null) => {
  const snap = await firestore.collection("categories").get();
  const all = snap.docs.map((d) => ({ ...d.data(), id: d.id, ref: d.ref }));
  const byPosition = (a, b) =>
    (Number(a.homePosition) || Infinity) - (Number(b.homePosition) || Infinity) ||
    String(a.name || "").localeCompare(String(b.name || ""));
  const home = all
    .filter((c) => c.homePage === true && c.id !== placeId)
    .sort(byPosition);
  const placed = all.find((c) => c.id === placeId);
  if (placed && placed.homePage === true) {
    const wanted = Number(wantedPosition) || home.length + 1;
    const index = Math.min(Math.max(wanted - 1, 0), home.length);
    home.splice(index, 0, placed);
  }
  const positions = new Map(home.map((c, i) => [c.id, i + 1]));
  const batch = firestore.batch();
  let writes = 0;
  all.forEach((c) => {
    const want = positions.has(c.id) ? positions.get(c.id) : false;
    if (c.homePosition !== want) {
      batch.update(c.ref, { homePosition: want });
      writes++;
    }
  });
  if (writes > 0) await batch.commit();
};

// Keeps homeProducts (the homescreen's product rows) in step with whether a
// category is on the homescreen: adds its 10 newest products, or removes its
// products once it's taken off.
const syncHomeProducts = async (categoryId, onHome) => {
  if (onHome) {
    const products = await firestore
      .collection("products")
      .where("checkedValues", "array-contains", categoryId)
      .orderBy("id", "desc")
      .limit(10)
      .get();
    await Promise.all(
      products.docs.map(async (doc) => {
        const ref = firestore.doc(`homeProducts/${doc.data().id}`);
        const existing = await ref.get();
        if (!existing.exists) await ref.set({ ...doc.data(), categoryId });
      }),
    );
  } else {
    const products = await firestore
      .collection("homeProducts")
      .where("checkedValues", "array-contains", categoryId)
      .get();
    await Promise.all(products.docs.map((doc) => doc.ref.delete()));
  }
};

export const uploadCategory = async (productObj) => {
  const productRef = firestore.doc(`categories/${productObj.id}`);
  const snapShot = await productRef.get();
  if (snapShot.exists) {
    notifyError("there is already a category with similar id");
    return;
  }
  try {
    const { file, homePosition, ...rest } = productObj;
    const onHome = rest.homePage === true;
    // Position is assigned by normalizeHomePositions, never written raw.
    await productRef.set({ ...rest, homePage: onHome, homePosition: false });
    if (onHome) {
      await normalizeHomePositions(productObj.id, homePosition);
      await syncHomeProducts(productObj.id, true);
    }
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updateCategory = async (productObj) => {
  const productRef = firestore.doc(`categories/${productObj.id}`);
  try {
    const before = (await productRef.get()).data() || {};
    const { file, homePosition, ...rest } = productObj;
    const onHome = rest.homePage === true;
    await productRef.update({ ...rest, homePage: onHome });
    // Only renumber when the homescreen is actually affected, so editing a
    // category's name/image doesn't rewrite every other category.
    if (onHome || before.homePage === true || before.homePosition) {
      await normalizeHomePositions(productObj.id, onHome ? homePosition : null);
    }
    if (onHome !== (before.homePage === true)) {
      await syncHomeProducts(productObj.id, onHome);
    }
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteCategory = async (productObj, parentId) => {
  const productRef = firestore.doc(`categories/${productObj.id}`);
  try {
    const childCategories = await firestore
      .collection("categories")
      .where("parentCategory", "==", productObj.id)
      .get();
    await Promise.all(
      childCategories.docs.map((doc) => doc.ref.update({ parentCategory: parentId })),
    );
    await productRef.delete();
    if (productObj.homePage === true || productObj.homePosition) {
      // Close the gap it leaves on the homescreen.
      await normalizeHomePositions();
      await syncHomeProducts(productObj.id, false);
    }
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllBanners = async () => {
  const productsCollectionRef = firestore.collection("banners");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadBanner = async (productObj) => {
  const productRef = firestore.doc(`banners/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (productObj.secondBanner) {
    const collectionRef = firestore
      .collection(`banners`)
      .where("secondBanner", "==", true);
    const collection = await collectionRef.get();
    collection.forEach(async (doc) => {
      const bannerRef = firestore.doc(`banners/${doc.data().id}`);
      await bannerRef.update({
        secondBanner: false,
      });
    });
  }
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a category with similar id");
  }
};

export const updateBanner = async (productObj) => {
  const productRef = firestore.doc(`banners/${productObj.id}`);
  const product = await productRef.get();
  if (productObj.secondBanner) {
    const collectionRef = firestore
      .collection(`banners`)
      .where("secondBanner", "==", true);
    const collection = await collectionRef.get();
    collection.forEach(async (doc) => {
      const bannerRef = firestore.doc(`banners/${doc.data().id}`);
      await bannerRef.update({
        secondBanner: false,
      });
    });
  }
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteBanner = async (id) => {
  const productRef = firestore.doc(`banners/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllTags = async () => {
  const productsCollectionRef = firestore.collection("tags");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadTag = async (productObj) => {
  const productRef = firestore.doc(`tags/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a tag with similar id");
  }
};

export const updateTag = async (productObj) => {
  const productRef = firestore.doc(`tags/${productObj.id}`);

  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteTag = async (id) => {
  const productRef = firestore.doc(`tags/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllAttributes = async () => {
  const productsCollectionRef = firestore.collection("attributes");
  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadAttribute = async (productObj) => {
  const productRef = firestore.doc(`attributes/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
        enableVisibility: true,
        enableVariations: true,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a attribute with similar id");
  }
};

export const updateAttribute = async (productObj) => {
  const productRef = firestore.doc(`attributes/${productObj.id}`);
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteAttribute = async (id) => {
  const attrRef = firestore.doc(`attributes/${id}`);
  const termsRef = firestore
    .collection(`attributeTerms`)
    .where("parentId", "==", id);
  const terms = await termsRef.get();
  try {
    await attrRef.delete();
    terms.forEach(async (doc) => {
      const termRef = firestore.doc(`attributes/${doc.data().id}`);
      await termRef.delete();
    });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllAttributeTerms = async (id) => {
  const productsCollectionRef = firestore
    .collection("attributeTerms")
    .where("parentId", "==", id);
  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    // attribute term chole gele nicher 2 line comment out korte hobe tahole thik hoye jabe
    // const attrRef = firestore.doc(`attributes/${id}`);
    // await attrRef.update({
    //   terms: productsArray,
    // });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadAttributeTerm = async (productObj) => {
  const termRef = firestore.doc(`attributeTerms/${productObj.id}`);
  const attrRef = firestore.doc(`attributes/${productObj.parentId}`);
  const snapShot = await termRef.get();
  const attr = await attrRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (!snapShot.exists) {
    try {
      await termRef.set({
        ...newProductObj,
      });
      await attrRef.update({
        terms: [...attr.data().terms, newProductObj],
      });
      const updatedSnapShot = await termRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a terms with similar id");
  }
};

export const fetchAllProducts = async (chunks) => {
  // Try matching by Firestore document ID (the primary key Algolia uses for objectID)
  const byDocId = (
    await Promise.all(
      chunks.map((chunk) =>
        firestore
          .collection("products")
          .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
          .get()
          .then((qs) => qs.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
          .catch(() => []),
      ),
    )
  ).flat();

  if (byDocId.length > 0) return byDocId;

  // Fallback: some products store a custom id field that Algolia indexed instead
  const byIdField = (
    await Promise.all(
      chunks.map((chunk) =>
        firestore
          .collection("products")
          .where("id", "in", chunk)
          .get()
          .then((qs) => qs.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
          .catch(() => []),
      ),
    )
  ).flat();

  return byIdField;
};

export const updateAttributeTerm = async (productObj) => {
  const termRef = firestore.doc(`attributeTerms/${productObj.id}`);
  const attrRef = firestore.doc(`attributes/${productObj.parentId}`);
  const attr = await attrRef.get();
  const newTerms = attr.data().terms.map((term) => {
    if (term.id == productObj.id) {
      return productObj;
    } else {
      return term;
    }
  });
  try {
    delete productObj.file;
    await termRef.update({ ...productObj });
    await attrRef.update({
      terms: newTerms,
    });
    const updatedSnapShot = await termRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteAttributeTerm = async (id, parentId) => {
  const termRef = firestore.doc(`attributeTerms/${id}`);
  const attrRef = firestore.doc(`attributes/${parentId}`);
  const attr = await attrRef.get();
  const newTerms = attr.data().terms.filter((term) => term.id !== id);
  try {
    await termRef.delete();
    await attrRef.update({
      terms: newTerms,
    });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCampaigns = async () => {
  const productsCollectionRef = firestore.collection("campaigns");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadCampaign = async (productObj) => {
  const productRef = firestore.doc(`campaigns/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (productObj.secondBanner) {
    const collectionRef = firestore
      .collection(`campaigns`)
      .where("secondBanner", "==", true);
    const collection = await collectionRef.get();
    collection.forEach(async (doc) => {
      const bannerRef = firestore.doc(`campaigns/${doc.data().id}`);
      await bannerRef.update({
        secondBanner: false,
      });
    });
  }
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a category with similar id");
  }
};

export const updateCampaign = async (productObj) => {
  const productRef = firestore.doc(`campaigns/${productObj.id}`);
  const product = await productRef.get();
  if (productObj.secondBanner) {
    const collectionRef = firestore
      .collection(`campaigns`)
      .where("secondBanner", "==", true);
    const collection = await collectionRef.get();
    collection.forEach(async (doc) => {
      const bannerRef = firestore.doc(`campaigns/${doc.data().id}`);
      await bannerRef.update({
        secondBanner: false,
      });
    });
  }
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteCampaign = async (id) => {
  const productRef = firestore.doc(`campaigns/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllAnnouncements = async () => {
  const productsCollectionRef = firestore.collection("announcements");

  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadAnnouncement = async (productObj) => {
  const productRef = firestore.doc(`announcements/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (productObj.secondBanner) {
    const collectionRef = firestore
      .collection(`announcements`)
      .where("secondBanner", "==", true);
    const collection = await collectionRef.get();
    collection.forEach(async (doc) => {
      const bannerRef = firestore.doc(`announcements/${doc.data().id}`);
      await bannerRef.update({
        secondBanner: false,
      });
    });
  }
  if (!snapShot.exists) {
    try {
      await productRef.set({
        ...newProductObj,
      });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a category with similar id");
  }
};

export const updateAnnouncement = async (productObj) => {
  const productRef = firestore.doc(`announcements/${productObj.id}`);
  const product = await productRef.get();
  if (productObj.visible) {
    const productsCollectionRef = firestore.collection("announcements");
    const products = await productsCollectionRef.get();
    products.forEach(async (doc) => {
      const annoucementRef = firestore.doc(`announcements/${doc.data().id}`);
      await annoucementRef.update({
        ...doc.data(),
        visible: false,
      });
    });
  }
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteAnnouncement = async (id) => {
  const productRef = firestore.doc(`announcements/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleMonthlyExpense = async (month) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getProcessedSteadfastPaymentIds = async () => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("category", "==", "Delivery charge");
  try {
    const expenses = await expensesCollectionRef.get();
    const ids = new Set();
    expenses.forEach((doc) => {
      const data = doc.data();
      if (data.subCategory) ids.add(String(data.subCategory));
    });
    return ids;
  } catch (error) {
    notifyError(error.message || String(error));
    return new Set();
  }
};

// Caches the last known Steadfast /payments page number so the payments list
// page doesn't need to re-search a wide fixed range every load — Steadfast's
// page count only grows over time, so this is used as a starting point to
// cheaply detect how far it's grown since last time.
const STEADFAST_META_DOC = () => firestore.doc("meta/steadfastPayments");

export const getSteadfastLastPage = async () => {
  try {
    const snap = await STEADFAST_META_DOC().get();
    const data = snap.data();
    return (data && data.lastPage) || 0;
  } catch (error) {
    notifyError(error.message || String(error));
    return 0;
  }
};

export const updateSteadfastLastPage = async (lastPage) => {
  try {
    await STEADFAST_META_DOC().set({ lastPage }, { merge: true });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllMonthlyExpense = async () => {
  const expensesCollectionRef = firestore.collection("monthlyExpense");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllPendingExpensesByDay = async (day) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("date", "==", day)
    .where("status", "==", "pending");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// Categories whose monthly summary is read back without filtering by
// subCategory (see getAllMonthly()'s query branching) — must be keyed by
// month+category only wherever a categoryMonthlyExpense doc id is built,
// here included. subCategory for these is a free-typed, per-transaction
// note (lot number, product name, promo description), so including it in
// the doc id would create a new "duplicate" month row every time someone
// typed a different note, instead of merging into one.
const SUBCATEGORY_AGNOSTIC_CATEGORIES = [
  "BUY PRODUCTS", "Boosting", "SHIPPING", "ORDERS", "TRANSPORT", "COURIER", "INVEST",
];

// Firestore hard-caps a WriteBatch at 500 operations. approveExpensesBatch /
// approveCashInsBatch refuse anything larger; a day with more pending items
// than fits goes through approveEntriesInChunks, where each chunk is atomic
// on its own but the day as a whole may end up partly approved.
const APPROVAL_BATCH_OP_LIMIT = 450;

// Walks backward from `date` (up to 365 days) to find the most recent prior
// cashSummary doc, used only to seed previousCash/remainingBalance if
// today's own cashSummary/{date} doc doesn't exist yet.
//
// Iteration `i` probes exactly `day - i`, so the first probe is the
// immediately preceding day. An earlier version built `day - i` and *then*
// subtracted another 86400000ms, which made the first probe `day - 2` and so
// skipped yesterday entirely — every doc created through this path seeded its
// previousCash from two days back, permanently breaking the
// previousCash -> remainingBalance chain from that day forward.
async function findLastCashSummarySnap(day, month, year) {
  for (let i = 1; i < 365; i++) {
    const previousDate = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day) - i,
    );
    const previousDayRef = firestore.doc(
      `cashSummary/${previousDate.toLocaleDateString("en-GB").replaceAll("/", "-")}`,
    );
    const previousDaySnap = await previousDayRef.get();
    if (previousDaySnap.exists) return previousDaySnap;
  }
  return null;
}

// Reads everything the cash-summary side of an approval batch needs, ahead
// of any writes: today's own cashSummary/{date} doc, the most recent prior
// day (as a previousCash seed if today's doc doesn't exist yet), and every
// day between `date` and today that already has a doc (so the balance
// change can be propagated forward through the days already on record).
async function readCashSummaryContext(date) {
  const [day, month, year] = date.split("/");
  const approvalDate = new Date(year, parseInt(month) - 1, day);
  const today = new Date();
  const diffDays = Math.ceil(Math.abs(today - approvalDate) / (1000 * 60 * 60 * 24));

  const cashSummaryRef = firestore.doc(`cashSummary/${date.replaceAll("/", "-")}`);
  const [cashSummarySnap, lastDaySnap] = await Promise.all([
    cashSummaryRef.get(),
    findLastCashSummarySnap(day, month, year),
  ]);

  const forwardDayRefs = [];
  if (diffDays > 0) {
    for (let i = 1; i <= diffDays; i++) {
      const dateObj = new Date(year, parseInt(month) - 1, parseInt(day) + i);
      forwardDayRefs.push(
        firestore.doc(`cashSummary/${dateObj.toLocaleDateString("en-GB").replaceAll("/", "-")}`),
      );
    }
  }
  const forwardDaySnaps = await Promise.all(forwardDayRefs.map((ref) => ref.get()));

  return { cashSummaryRef, cashSummarySnap, lastDaySnap, forwardDayRefs, forwardDaySnaps };
}

// Queues the cashSummary writes (today's doc + forward propagation) onto an
// already-open batch. `sign` is +1 for cash-in (adds to totals/balance) or
// -1 for cash-out (subtracts) — mirrors updateCashSummaryCashIn/Out's math.
// Deltas are applied with FieldValue.increment rather than by writing back a
// value recomputed from the snapshot we read earlier. The read-then-write
// version lost writes whenever two approvals overlapped: both read the same
// pre-write balance and the second commit silently discarded the first. The
// snapshots are still read, but only to decide *which* docs to touch — the
// arithmetic happens server-side.
function queueCashSummaryWrites(batch, ctx, { month, date, total, sign, totalsField }) {
  const { cashSummaryRef, cashSummarySnap, lastDaySnap, forwardDayRefs, forwardDaySnaps } = ctx;

  if (cashSummarySnap.exists) {
    batch.update(cashSummaryRef, {
      [totalsField]: inc(total),
      remainingBalance: inc(sign * total),
    });
  } else {
    // Creating the day's doc: these are absolute seed values, not deltas.
    const lastDayRemaining = lastDaySnap ? parseInt(lastDaySnap.data().remainingBalance) || 0 : 0;
    batch.set(cashSummaryRef, {
      month: cashSummaryMonthFor(date, month),
      date,
      previousCash: lastDayRemaining,
      totalCashIns: totalsField === "totalCashIns" ? total : 0,
      totalCashOuts: totalsField === "totalCashOuts" ? total : 0,
      remainingBalance: lastDayRemaining + sign * total,
    });
  }

  forwardDayRefs.forEach((ref, i) => {
    if (forwardDaySnaps[i].exists) {
      batch.update(ref, {
        previousCash: inc(sign * total),
        remainingBalance: inc(sign * total),
      });
    }
  });
}

// The day and the amount the cash summary must move by, derived from the
// freshly-read entry docs rather than from whatever the browser computed.
//
// The approval batches already re-read every doc precisely because component
// state can't be trusted, but the cash-summary write still took the caller's
// `total` — a figure summed in the component from possibly-stale Redux state
// with a bare parseInt, so one non-numeric amount turned the whole thing into
// NaN. The monthly rollups meanwhile used the re-read amounts, so the two
// could disagree about the very same approval.
//
// A batch spanning more than one date is refused rather than silently landing
// every entry's cash on the first item's day.
function resolveCashSummaryFacts(resolved, fallbackDate) {
  const dates = [...new Set(resolved.map((e) => e.date).filter(Boolean))];
  if (dates.length > 1) {
    return {
      error: `These entries are dated ${dates.join(", ")}. Approve one day at a time so each day's cash summary stays correct.`,
    };
  }
  return {
    date: dates[0] || fallbackDate,
    total: resolved.reduce((sum, e) => sum + (parseInt(e.amount) || 0), 0),
  };
}

// Applies the compensating cashSummary movement when an ALREADY-APPROVED
// entry is edited or deleted. Without this, deleting an approved 5000Tk
// expense left the day's totals — and every later day's previousCash and
// remainingBalance — permanently over-counted, with nothing anywhere to
// correct it.
//
// `sign` is the entry's own direction (-1 for dailyExpenses, +1 for
// dailyCashIn); a reversal is just the approval's own math with a negated
// amount, so `queueCashSummaryWrites` is reused as-is.
//
// `docWrite` (optional) is the entry's own delete/update, queued into the
// first batch so the common single-date case stays atomic.
//
// Each affected date commits in its own batch. When an edit moves an entry to
// a different date the two dates' forward ranges overlap, and a WriteBatch
// keeps only the last queued write per document path — merging them into one
// batch would silently drop one of the two increments.
async function adjustCashSummaryForApprovedEntry({
  before,
  after,
  sign,
  totalsField,
  docWrite,
}) {
  const deltas = new Map();
  const addDelta = (date, month, delta) => {
    if (!date || !delta) return;
    const existing = deltas.get(date);
    if (existing) existing.delta += delta;
    else deltas.set(date, { month, delta });
  };

  addDelta(before.date, before.month, -(parseInt(before.amount) || 0));
  if (after) addDelta(after.date, after.month, parseInt(after.amount) || 0);

  let pendingDocWrite = docWrite;

  for (const [date, { month, delta }] of deltas) {
    const ctx = await readCashSummaryContext(date);

    // Nothing was ever recorded for this day, so there is nothing to take
    // back. (Adding to a day with no doc yet is fine — it gets created.)
    if (!ctx.cashSummarySnap.exists && delta < 0) continue;

    const batch = firestore.batch();
    if (pendingDocWrite) {
      pendingDocWrite(batch);
      pendingDocWrite = null;
    }
    queueCashSummaryWrites(batch, ctx, {
      month,
      date,
      total: delta,
      sign,
      totalsField,
    });
    await batch.commit();
  }

  // No cash-summary day needed touching (e.g. an edit that didn't change the
  // amount), so the entry's own write still has to happen.
  if (pendingDocWrite) {
    const batch = firestore.batch();
    pendingDocWrite(batch);
    await batch.commit();
  }
}

/* ------------------------------------------------------------------ *
 * Cash summary rebuild
 *
 * cashSummary docs are a stored, incrementally-patched aggregate: only the
 * approval batches ever write them, and nothing recomputes them. So any
 * missed or mis-seeded delta (a deleted approved entry, an edited amount, a
 * lost concurrent write) is permanent and every later day inherits it,
 * because previousCash is chained forward rather than derived.
 *
 * recalculateCashSummaryFrom re-derives the whole chain from the actual
 * approved entries, so the admin has a way back to correct numbers.
 * ------------------------------------------------------------------ */

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Nothing before this may be rebuilt — earlier months are treated as closed
// books whose opening balances may have been reconciled by hand.
const CASH_SUMMARY_REBUILD_FLOOR = new Date(2026, 3, 1); // 1 April 2026
export const CASH_SUMMARY_REBUILD_FLOOR_MONTH = "April,2026";

// SALARY expenses carry the *salary* month in `month`, not the month they were
// actually paid in, so scanning only the months in range would miss a salary
// paid in June against May's payroll. Entries are fetched over a widened
// window and then bucketed strictly by their `date` field; over-fetching is
// harmless, under-fetching would silently drop cash.
const CASH_SUMMARY_REBUILD_LOOKBACK_MONTHS = 12;

// "April,2026" -> Date(2026, 3, 1). Null if it isn't a month id.
const parseMonthId = (monthId) => {
  const [monthName, year] = String(monthId || "").split(",");
  const monthIndex = MONTH_NAMES.indexOf(String(monthName || "").trim());
  const parsedYear = parseInt(year, 10);
  if (monthIndex < 0 || !parsedYear) return null;
  return new Date(parsedYear, monthIndex, 1);
};

const monthIdFromDate = (dateObj) =>
  `${MONTH_NAMES[dateObj.getMonth()]},${dateObj.getFullYear()}`;

// The canonical "DD/MM/YYYY" every cash doc is keyed by. Buckets are keyed off
// this rather than off the raw stored string so a stray unpadded "6/8/2026"
// folds into the same day as "06/08/2026" instead of spawning a duplicate row
// and a cashSummary/6-8-2026 doc that no other query would ever find.
const formatDayId = (dateObj) => dateObj.toLocaleDateString("en-GB");

// "06/08/2026" (en-GB, the format every cash doc stores) -> Date.
// Returns null rather than a rolled-over date for junk like "31/02/2026".
const parseEntryDate = (dateStr) => {
  const [day, month, year] = String(dateStr || "").split("/");
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (!d || !m || !y) return null;
  const dateObj = new Date(y, m - 1, d);
  if (
    dateObj.getDate() !== d ||
    dateObj.getMonth() !== m - 1 ||
    dateObj.getFullYear() !== y
  ) {
    return null;
  }
  return dateObj;
};

// The month a cashSummary doc must be filed under — always the month of its
// own `date`, never the `month` field carried on the entries being approved.
//
// An entry's `month` is not the month it was paid in: for SALARY it is the
// payroll month (components/expense/daily-expense.js), and for everything else
// it is built from `new Date()` rather than the entry's chosen date, so a
// back-dated entry still carries the current month. Storing that verbatim
// filed the day under a month that contradicted its own date, and since
// getSingleMonthlyCashSummary queries `where("month","==",month)` the day then
// vanished from its real month's page and appeared under the wrong one.
//
// `fallbackMonth` is used only if the date string can't be parsed at all.
const cashSummaryMonthFor = (date, fallbackMonth) => {
  const dateObj = parseEntryDate(date);
  return dateObj ? monthIdFromDate(dateObj) : fallbackMonth;
};

// Verifies one day's cashSummary against the entries it is supposed to
// summarise, and corrects it if they disagree. Runs after every approval, so
// the daily stats can't quietly drift away from the actual cash in / cash out
// entries of that day.
//
// Only the day's totals are checked. `previousCash` is deliberately left
// alone — that's chain state, protected by the seeding fix and repairable
// with the Recalculate button on the month page.
//
// The corrected values are written as derived absolutes rather than deltas,
// which makes this idempotent: run it twice and the second run is a no-op.
// The trade-off is that a second approval committing between this function's
// read and its write could be overwritten. With a handful of admins making
// deliberate approvals that's unlikely, and the next approval — or a
// Recalculate — converges back to the entries.
export const reconcileCashSummaryDay = async (date) => {
  const dateObj = parseEntryDate(date);
  if (!dateObj) return { healed: false, reason: "unparseable-date" };

  try {
    // Two equality clauses on (date, status) — the same query shape
    // getAllPendingExpensesByDay already uses, so no new index is needed.
    const sumApproved = async (collectionName) => {
      const snap = await firestore
        .collection(collectionName)
        .where("date", "==", date)
        .where("status", "==", "approved")
        .get();
      let sum = 0;
      snap.forEach((doc) => {
        sum += parseInt(doc.data().amount) || 0;
      });
      return sum;
    };

    const [expectedOut, expectedIn] = await Promise.all([
      sumApproved("dailyExpenses"),
      sumApproved("dailyCashIn"),
    ]);

    const cashSummaryRef = firestore.doc(
      `cashSummary/${date.replaceAll("/", "-")}`,
    );
    const snap = await cashSummaryRef.get();

    if (!snap.exists) {
      // Nothing recorded and nothing to record.
      if (!expectedIn && !expectedOut) return { healed: false };
      // A day with approved entries but no doc — let the month rebuild seed
      // its opening balance rather than guessing a chain position here.
      return { healed: false, reason: "missing-day-doc", expectedIn, expectedOut };
    }

    const data = snap.data();
    const storedIn = parseInt(data.totalCashIns) || 0;
    const storedOut = parseInt(data.totalCashOuts) || 0;
    const previousCash = parseInt(data.previousCash) || 0;
    const storedRemaining = parseInt(data.remainingBalance) || 0;
    const month = cashSummaryMonthFor(date, data.month);

    const newRemaining = previousCash + expectedIn - expectedOut;
    const deltaRemaining = newRemaining - storedRemaining;

    if (
      storedIn === expectedIn &&
      storedOut === expectedOut &&
      !deltaRemaining &&
      data.month === month
    ) {
      return { healed: false, expectedIn, expectedOut };
    }

    // Reuse the approval path's forward-day reads so later days shift with
    // the correction and the running balance stays continuous.
    const ctx = await readCashSummaryContext(date);

    const writes = [
      {
        ref: cashSummaryRef,
        data: {
          month,
          totalCashIns: expectedIn,
          totalCashOuts: expectedOut,
          remainingBalance: newRemaining,
        },
      },
    ];

    if (deltaRemaining) {
      ctx.forwardDayRefs.forEach((ref, i) => {
        if (ctx.forwardDaySnaps[i].exists) {
          writes.push({
            ref,
            data: {
              previousCash: inc(deltaRemaining),
              remainingBalance: inc(deltaRemaining),
            },
          });
        }
      });
    }

    // Reconciling a long-past day can touch more forward days than a single
    // WriteBatch allows. Like the month rebuild, this is a repair pass with no
    // all-or-nothing requirement, so chunked commits are fine — the day's own
    // correction goes first, and re-running converges anything left behind.
    for (let i = 0; i < writes.length; i += APPROVAL_BATCH_OP_LIMIT) {
      const batch = firestore.batch();
      writes
        .slice(i, i + APPROVAL_BATCH_OP_LIMIT)
        .forEach((w) => batch.update(w.ref, w.data));
      await batch.commit();
    }

    return {
      healed: true,
      date,
      expectedIn,
      expectedOut,
      storedIn,
      storedOut,
      deltaRemaining,
    };
  } catch (error) {
    // A failed self-check must never fail the approval that just succeeded.
    console.error("Error reconciling cash summary day:", error);
    return { healed: false, reason: "reconcile-failed", message: error.message };
  }
};

// Whether the rebuild button should be offered for a given month id.
export const canRecalculateCashSummary = (monthId) => {
  const start = parseMonthId(monthId);
  return !!start && start.getTime() >= CASH_SUMMARY_REBUILD_FLOOR.getTime();
};

// Rebuilds every cashSummary day from `startMonthId` through today, deriving
// each day's totals from the approved dailyExpenses / dailyCashIn entries
// dated that day and re-chaining previousCash / remainingBalance forward from
// the balance standing at the end of the month before.
//
// Days with approved activity but no cashSummary doc get one created; days
// with a doc but no activity are zeroed and simply carry the balance across.
// Idempotent — running it twice writes nothing the second time.
export const recalculateCashSummaryFrom = async (startMonthId) => {
  const start = parseMonthId(startMonthId);
  if (!start) {
    return { success: false, message: `"${startMonthId}" is not a valid month.` };
  }
  if (start.getTime() < CASH_SUMMARY_REBUILD_FLOOR.getTime()) {
    return {
      success: false,
      message: `Cash summary can only be rebuilt from ${CASH_SUMMARY_REBUILD_FLOOR_MONTH} onward.`,
    };
  }

  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (start.getTime() > end.getTime()) {
    return { success: false, message: `${startMonthId} is in the future.` };
  }

  try {
    // The opening balance is taken as-is from the last day on record before
    // this month — earlier months are not touched.
    const lastSnap = await findLastCashSummarySnap(
      1,
      start.getMonth() + 1,
      start.getFullYear(),
    );
    const openingCash = lastSnap
      ? parseInt(lastSnap.data().remainingBalance) || 0
      : 0;

    const scanMonths = [];
    const cursor = new Date(
      start.getFullYear(),
      start.getMonth() - CASH_SUMMARY_REBUILD_LOOKBACK_MONTHS,
      1,
    );
    while (cursor.getTime() <= end.getTime()) {
      scanMonths.push(monthIdFromDate(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const inRange = (dateObj) =>
      dateObj.getTime() >= start.getTime() && dateObj.getTime() <= end.getTime();

    // canonical day id -> running per-day bucket
    const days = new Map();
    const bucketFor = (dateObj) => {
      const dayId = formatDayId(dateObj);
      if (!days.has(dayId)) {
        days.set(dayId, {
          date: dayId,
          dateObj,
          totalCashIns: 0,
          totalCashOuts: 0,
        });
      }
      return days.get(dayId);
    };

    const collectEntries = async (collectionName, totalsField) => {
      const snaps = await Promise.all(
        scanMonths.map((monthId) =>
          firestore.collection(collectionName).where("month", "==", monthId).get(),
        ),
      );
      snaps.forEach((snap) =>
        snap.forEach((doc) => {
          const entry = doc.data();
          // Cash only actually moves on approval; pending entries are shown on
          // the day view but must not land in the day's totals.
          if (entry.status !== "approved") return;
          const dateObj = parseEntryDate(entry.date);
          if (!dateObj || !inRange(dateObj)) return;
          bucketFor(dateObj)[totalsField] += parseInt(entry.amount) || 0;
        }),
      );
    };

    // Existing docs are loaded so a day that no longer has any approved entry
    // gets zeroed instead of keeping stale totals forever.
    const loadExistingDays = async () => {
      const existing = new Map();
      const snaps = await Promise.all(
        scanMonths.map((monthId) =>
          firestore.collection("cashSummary").where("month", "==", monthId).get(),
        ),
      );
      snaps.forEach((snap) =>
        snap.forEach((doc) => {
          const data = doc.data();
          const dateObj = parseEntryDate(
            data.date || String(doc.id).replaceAll("-", "/"),
          );
          if (!dateObj || !inRange(dateObj)) return;
          existing.set(formatDayId(dateObj), data);
          bucketFor(dateObj);
        }),
      );
      return existing;
    };

    const [existing] = await Promise.all([
      loadExistingDays(),
      collectEntries("dailyExpenses", "totalCashOuts"),
      collectEntries("dailyCashIn", "totalCashIns"),
    ]);

    const ordered = [...days.values()].sort((a, b) => a.dateObj - b.dateObj);

    let running = openingCash;
    let daysCreated = 0;
    let daysUnchanged = 0;
    const writes = [];

    ordered.forEach((day) => {
      const previousCash = running;
      const remainingBalance =
        previousCash + day.totalCashIns - day.totalCashOuts;
      running = remainingBalance;

      const rebuilt = {
        month: monthIdFromDate(day.dateObj),
        date: day.date,
        previousCash,
        totalCashIns: day.totalCashIns,
        totalCashOuts: day.totalCashOuts,
        remainingBalance,
      };

      const current = existing.get(day.date);
      if (!current) {
        daysCreated += 1;
        writes.push(rebuilt);
        return;
      }

      const unchanged =
        current.month === rebuilt.month &&
        (parseInt(current.previousCash) || 0) === previousCash &&
        (parseInt(current.totalCashIns) || 0) === day.totalCashIns &&
        (parseInt(current.totalCashOuts) || 0) === day.totalCashOuts &&
        (parseInt(current.remainingBalance) || 0) === remainingBalance;

      if (unchanged) daysUnchanged += 1;
      else writes.push(rebuilt);
    });

    // Chunked commits: this is a repair pass, so partial progress is safe and
    // simply resumable by clicking again — unlike an approval, it has no
    // all-or-nothing requirement.
    for (let i = 0; i < writes.length; i += APPROVAL_BATCH_OP_LIMIT) {
      const batch = firestore.batch();
      writes.slice(i, i + APPROVAL_BATCH_OP_LIMIT).forEach((rebuilt) => {
        batch.set(
          firestore.doc(`cashSummary/${rebuilt.date.replaceAll("/", "-")}`),
          rebuilt,
        );
      });
      await batch.commit();
    }

    return {
      success: true,
      daysScanned: ordered.length,
      daysWritten: writes.length,
      daysCreated,
      daysUnchanged,
      firstDate: ordered.length ? ordered[0].date : null,
      lastDate: ordered.length ? ordered[ordered.length - 1].date : null,
      openingCash,
      closingCash: running,
    };
  } catch (error) {
    notifyError(error.message || String(error));
    return { success: false, message: error.message || String(error) };
  }
};

// Approves every pending item in `expenses` (all dailyExpenses docs for one
// day) as a single atomic operation, together with the day's cash summary —
// either everything commits, or (on any failure) nothing does.
//
// Replaces the old per-item approveExpense(expense): that version built and
// committed its own independent firestore.batch() per item, so a failure
// partway through a day's worth of approvals left a mixed approved/pending
// state, and multiple same-day expenses landing on the same aggregate doc
// (e.g. three SHIPPING expenses all resolving to
// categoryMonthlyExpense/{month}-SHIPPING) would silently lose all but the
// last one's contribution if naively queued as separate batch.update()
// calls against the same doc — WriteBatch keeps only the last queued write
// per document path, it doesn't accumulate. This version groups by target
// document and sums deltas before queuing any write, so that can't happen.
// `date`/`month` are only fallbacks — the day and amount the cash summary
// moves by are derived from the re-read docs (see resolveCashSummaryFacts).
// Callers may still pass a `total`; it is deliberately ignored.
export const approveExpensesBatch = async (
  expenses,
  { month, date },
  { reconcile = true } = {},
) => {
  if (!expenses || expenses.length === 0) {
    return { success: true, approvedIds: [] };
  }

  try {
    // Phase A — read every expense doc fresh (don't trust component state),
    // and bail out before queuing anything if any item isn't actually
    // pending anymore (e.g. approved from a second tab).
    const expenseRefs = expenses.map((e) => firestore.doc(`dailyExpenses/${e.id}`));
    const expenseSnaps = await Promise.all(expenseRefs.map((ref) => ref.get()));

    for (let i = 0; i < expenseSnaps.length; i++) {
      if (!expenseSnaps[i].exists || expenseSnaps[i].data().status !== "pending") {
        return {
          success: false,
          reason: "stale-item",
          message: `"${expenses[i].subCategory || expenses[i].note || expenses[i].id}" is no longer pending — nothing was approved.`,
          approvedIds: [],
        };
      }
    }
    const resolved = expenseSnaps.map((snap) => ({ id: snap.id, ...snap.data() }));

    // The cash summary moves by what the documents actually say, on the day
    // the documents actually carry — not by the caller's figures.
    const cashFacts = resolveCashSummaryFacts(resolved, date);
    if (cashFacts.error) {
      return {
        success: false,
        reason: "mixed-dates",
        message: cashFacts.error,
        approvedIds: [],
      };
    }

    // Phase B — group by target document, summing deltas so multiple items
    // hitting the same aggregate doc combine instead of clobbering.
    const targets = new Map();
    const addTarget = (ref, amount, kind, meta) => {
      const key = ref.path;
      if (!targets.has(key)) targets.set(key, { ref, deltaAmount: 0, kind, meta });
      const entry = targets.get(key);
      entry.deltaAmount += amount;
      entry.meta = meta; // last-contributing-expense wins, same as sequential commits did in practice
    };

    resolved.forEach((expense) => {
      const amount = parseInt(expense.amount) || 0;

      const categoryMonthlyExpenseId = SUBCATEGORY_AGNOSTIC_CATEGORIES.includes(expense.category)
        ? `${expense.month}-${expense.category}`
        : `${expense.month}-${expense.category}-${String(expense.subCategory).replaceAll("/", "_")}`;
      addTarget(
        firestore.doc(`categoryMonthlyExpense/${categoryMonthlyExpenseId}`),
        amount,
        expense.category === "SALARY" ? "categoryMonthlyExpenseSalary" : "categoryMonthlyExpense",
        {
          id: categoryMonthlyExpenseId,
          category: expense.category,
          subCategory: expense.subCategory,
          month: expense.month,
        },
      );

      if (expense.category === "SALARY") {
        addTarget(firestore.doc(`monthlySalary/${expense.month}`), amount, "monthlySalary", {
          month: expense.month,
        });
      } else if (expense.category === "LOAN" || expense.category === "MONTHLY INSTALLMENT") {
        const customerCollection =
          expense.category === "LOAN" ? "customerLoans" : "customerInstallments";
        addTarget(
          firestore.doc(`${customerCollection}/${expense.uid}`),
          amount,
          "customer",
          { customer: expense.subCategory, uid: expense.uid },
        );
      }

      addTarget(firestore.doc(`monthlyExpense/${expense.month}`), amount, "monthlyExpense", {
        id: expense.month,
      });
    });

    // Phase C — read each unique target doc exactly once.
    const targetEntries = Array.from(targets.values());
    const targetSnaps = await Promise.all(targetEntries.map((t) => t.ref.get()));

    // Cash-summary reads (backward seed lookup + today's doc + forward
    // propagation chain) — also all reads, no writes yet.
    const cashSummaryCtx = await readCashSummaryContext(cashFacts.date);

    const estimatedOps =
      resolved.length + targetEntries.length + 1 + cashSummaryCtx.forwardDayRefs.length;
    if (estimatedOps > APPROVAL_BATCH_OP_LIMIT) {
      return {
        success: false,
        reason: "too-many-items",
        message: `Too many pending items to approve atomically at once (${estimatedOps} operations). Please approve in smaller groups.`,
        approvedIds: [],
      };
    }

    // Phase D — queue writes.
    const batch = firestore.batch();

    resolved.forEach((expense) => {
      batch.update(firestore.doc(`dailyExpenses/${expense.id}`), { status: "approved" });
    });

    targetEntries.forEach((entry, i) => {
      const snap = targetSnaps[i];
      const existingAmount = snap.exists ? parseInt(snap.data().amount) || 0 : 0;
      const newAmount = existingAmount + entry.deltaAmount;

      if (entry.kind === "categoryMonthlyExpense" || entry.kind === "categoryMonthlyExpenseSalary") {
        if (snap.exists) {
          const update = { amount: newAmount };
          if (entry.kind === "categoryMonthlyExpenseSalary") {
            update.paid = parseInt(snap.data().salary) === newAmount;
          }
          batch.update(entry.ref, update);
        } else {
          batch.set(entry.ref, {
            id: entry.meta.id,
            amount: newAmount,
            category: entry.meta.category,
            subCategory: entry.meta.subCategory,
            month: entry.meta.month,
            paid: false,
            salary: 0,
          });
        }
      } else if (entry.kind === "monthlySalary") {
        if (snap.exists) {
          batch.update(entry.ref, { amount: newAmount });
        } else {
          batch.set(entry.ref, { month: entry.meta.month, amount: newAmount });
        }
      } else if (entry.kind === "customer") {
        if (snap.exists) {
          batch.update(entry.ref, { amount: newAmount, customer: entry.meta.customer });
        } else {
          batch.set(entry.ref, {
            customer: entry.meta.customer,
            amount: newAmount,
            uid: entry.meta.uid,
          });
        }
      } else if (entry.kind === "monthlyExpense") {
        if (snap.exists) {
          batch.update(entry.ref, { amount: newAmount });
        } else {
          batch.set(entry.ref, { id: entry.meta.id, amount: newAmount });
        }
      }
    });

    // Phase E — fold in the cash-summary update (cash-out subtracts from
    // remainingBalance, matching updateCashSummaryCashOut's convention).
    queueCashSummaryWrites(batch, cashSummaryCtx, {
      month,
      date: cashFacts.date,
      total: cashFacts.total,
      sign: -1,
      totalsField: "totalCashOuts",
    });

    // Phase F — commit. Everything above was reads/in-memory aggregation
    // only, so a failure here leaves Firestore completely untouched.
    await batch.commit();

    // Phase G — the day is now committed; verify it against its own entries
    // and correct it if anything is off, so the cash summary can never drift
    // away from the entries it is supposed to summarise.
    // Skipped for all but the last chunk of a chunked approval — see
    // approveEntriesInChunks.
    const reconciled = reconcile ? await reconcileCashSummaryDay(cashFacts.date) : null;

    return {
      success: true,
      approvedIds: resolved.map((e) => e.id),
      reconciled,
    };
  } catch (error) {
    console.error("Error approving expenses:", error);
    return {
      success: false,
      reason: "batch-commit-failure",
      message: error.message || "Failed to approve expenses — nothing was changed.",
      approvedIds: [],
    };
  }
};

export const getAllPendingExpenses = async () => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("status", "==", "pending");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteExpense = async (expenseId) => {
  const expenseRef = firestore.doc(`dailyExpenses/${expenseId}`);
  const snapShot = await expenseRef.get();
  if (!snapShot.exists) return;
  const before = snapShot.data();
  try {
    // A pending entry never moved any cash, so it can just go. An approved one
    // has to give its amount back to the day's totals and to every later day's
    // running balance.
    if (before.status === "approved") {
      await adjustCashSummaryForApprovedEntry({
        before,
        after: null,
        sign: -1,
        totalsField: "totalCashOuts",
        docWrite: (batch) => batch.delete(expenseRef),
      });
    } else {
      await expenseRef.delete();
    }
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updateExpense = async (expenseObj) => {
  const expenseRef = firestore.doc(`dailyExpenses/${expenseObj.id}`);
  try {
    const snapShot = await expenseRef.get();
    const before = snapShot.exists ? snapShot.data() : null;

    if (before && before.status === "approved") {
      // expenseObj may be partial, so the post-edit entry is the merge.
      await adjustCashSummaryForApprovedEntry({
        before,
        after: { ...before, ...expenseObj },
        sign: -1,
        totalsField: "totalCashOuts",
        docWrite: (batch) => batch.update(expenseRef, { ...expenseObj }),
      });
    } else {
      await expenseRef.update({ ...expenseObj });
    }

    const updatedSnapShot = await expenseRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadExpense = async (expenseObj) => {
  const expenseRef = firestore.doc(`dailyExpenses/${expenseObj.id}`);
  const snapShot = await expenseRef.get();
  if (!snapShot.exists) {
    try {
      await expenseRef.set({
        ...expenseObj,
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await expenseRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a expense with similar id,try again later");
  }
};

export const getAllExpenses = async (day) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("date", "==", day);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllOffices = async () => {
  const officesCollectionRef = firestore.collection("offices");
  try {
    const offices = await officesCollectionRef.get();
    const officesArray = [];
    offices.forEach((doc) => {
      officesArray.push(doc.data());
    });
    return officesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadOffice = async (officeObj) => {
  const officeRef = firestore.doc(`offices/${officeObj.officeId}`);
  const snapShot = await officeRef.get();
  if (!snapShot.exists) {
    try {
      await officeRef.set({
        ...officeObj,
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await officeRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "there is already a office with similar name, please change the country name and try again",
    );
  }
};

export const updateOffice = async (officeObj) => {
  const officeRef = firestore.doc(`offices/${officeObj.officeId}`);
  try {
    await officeRef.update({ ...officeObj });
    const snapShot = await officeRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteOffice = async (officeId) => {
  const officeRef = firestore.doc(`offices/${officeId}`);
  const snapShot = await officeRef.get();
  console.log(snapShot.data());
  try {
    await officeRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleCashSummary = async () => {
  const date = new Date();
  const cashSummaryRef = firestore.doc(
    `cashSummary/${date.toLocaleDateString("en-GB").replaceAll("/", "-")}`,
  );
  const cashSummary = await cashSummaryRef.get();
  if (cashSummary.exists) {
    return cashSummary.data();
  } else {
    // Seed from the most recent prior day. This used to be a hand-rolled copy
    // of findLastCashSummarySnap's loop that disagreed with it by one day, so
    // a doc's previousCash depended on which code path happened to create it
    // first. One implementation now, so they can't drift apart again.
    const [day, month, year] = date.toLocaleDateString("en-GB").split("/");
    const lastDaySnap = await findLastCashSummarySnap(day, month, year);
    const lastDayRemaining = lastDaySnap
      ? parseInt(lastDaySnap.data().remainingBalance) || 0
      : 0;
    await cashSummaryRef.set({
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      previousCash: lastDayRemaining,
      totalCashIns: 0,
      totalCashOuts: 0,
      remainingBalance: lastDayRemaining,
    });
    const updatedSnapShot = await cashSummaryRef.get();
    return updatedSnapShot.data();
  }
};

export const uploadCashIn = async (cashInObj) => {
  const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);
  const snapShot = await cashInRef.get();
  if (!snapShot.exists) {
    try {
      await cashInRef.set({
        ...cashInObj,
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await cashInRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a cash in with similar id,try again later");
  }
};

export const updateCashIn = async (cashInObj) => {
  const cashInRef = firestore.doc(`dailyCashIn/${cashInObj.id}`);
  try {
    const snapShot = await cashInRef.get();
    const before = snapShot.exists ? snapShot.data() : null;

    if (before && before.status === "approved") {
      await adjustCashSummaryForApprovedEntry({
        before,
        after: { ...before, ...cashInObj },
        sign: 1,
        totalsField: "totalCashIns",
        docWrite: (batch) => batch.update(cashInRef, { ...cashInObj }),
      });
    } else {
      await cashInRef.update({ ...cashInObj });
    }

    const updatedSnapShot = await cashInRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllDocumentExpressRates = async () => {
  const expressRatesDocumentsCollectionRef = firestore.collection(
    "expressRatesDocuments",
  );
  try {
    const expressRatesDocuments =
      await expressRatesDocumentsCollectionRef.get();
    const expressRatesDocumentsArray = [];
    expressRatesDocuments.forEach((doc) => {
      expressRatesDocumentsArray.push(doc.data());
    });
    return expressRatesDocumentsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllEmployees = async () => {
  const employeesCollectionRef = firestore.collection("employees");
  try {
    const employees = await employeesCollectionRef.get();
    const employeesArray = [];
    employees.forEach((doc) => {
      employeesArray.push(doc.data());
    });
    return employeesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllMonthlyCashSummary = async () => {
  const expensesCollectionRef = firestore.collection("cashSummary");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllMonthlyCashIn = async () => {
  const expensesCollectionRef = firestore.collection("monthlyCashIn");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllMonthlySalary = async () => {
  const expensesCollectionRef = firestore.collection(`monthlySalary`);

  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleCategoryProducts = async (categories, startAfter) => {
  let productsCollectionRef;
  console.log(startAfter);
  if (startAfter) {
    productsCollectionRef = firestore
      .collection("products")
      .where("checkedValues", "array-contains-any", categories)
      .orderBy("name")
      .startAfter(startAfter);
  } else {
    productsCollectionRef = firestore
      .collection("products")
      .where("checkedValues", "array-contains-any", categories)
      .orderBy("name");
  }
  const products = await productsCollectionRef.get();
  const lastProduct = products.docs[products.docs.length - 1];

  try {
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return { productsArray, lastProduct };
  } catch (error) {
    notifyError(error.message || String(error));
    console.log(error);
  }
};

export const getSingleBrandProducts = async (brandId, startAfter) => {
  let productsCollectionRef;
  console.log(startAfter);
  if (startAfter) {
    productsCollectionRef = firestore
      .collection("products")
      .where("checkedValues2", "array-contains", brandId)
      .orderBy("id", "desc")
      .startAfter(startAfter);
  } else {
    productsCollectionRef = firestore
      .collection("products")
      .where("checkedValues2", "array-contains", brandId)
      .orderBy("id", "desc");
  }
  const products = await productsCollectionRef.get();
  const lastProduct = products.docs[products.docs.length - 1];
  try {
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return { productsArray, lastProduct };
  } catch (error) {
    notifyError(error.message || String(error));
    console.log(error);
  }
};
export const getSingleCampaignProducts = async (campaignId, startAfter) => {
  let productsCollectionRef;
  console.log(startAfter);
  if (startAfter) {
    productsCollectionRef = firestore
      .collection("products")
      .where("selectedCampaign", "==", campaignId)

      .startAfter(startAfter);
  } else {
    productsCollectionRef = firestore
      .collection("products")
      .where("selectedCampaign", "==", campaignId);
  }
  const products = await productsCollectionRef.get();
  const lastProduct = products.docs[products.docs.length - 1];
  try {
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return { productsArray, lastProduct };
  } catch (error) {
    notifyError(error.message || String(error));
    console.log(error);
  }
};

export const getAllMonthly = async (category, subCategory) => {
  let expensesCollectionRef = null;
  if (
    category == "BUY PRODUCTS" ||
    category == "Boosting" ||
    category == "SHIPPING" ||
    category == "ORDERS" ||
    category == "TRANSPORT" ||
    category == "COURIER" ||
    category == "INVEST"
  ) {
    expensesCollectionRef = firestore
      .collection(`categoryMonthlyExpense`)
      .where("category", "==", category);
  } else {
    expensesCollectionRef = firestore
      .collection(`categoryMonthlyExpense`)
      .where("category", "==", category)
      .where("subCategory", "==", subCategory);
  }

  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleMonthlyCashSummary = async (month) => {
  const expensesCollectionRef = firestore
    .collection("cashSummary")
    .where("month", "==", month);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleMonthlyCashIn = async (month) => {
  const expensesCollectionRef = firestore
    .collection("dailyCashIn")
    .where("month", "==", month);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleMonthly = async (month, category, subCategory) => {
  if (category === "INVEST") {
    const expensesCollectionRef = firestore
      .collection("dailyExpenses")
      .where("month", "==", month)
      .where("category", "==", category)
      .where("subCategory", "==", subCategory);
    const cashInCollectionRef = firestore
      .collection("dailyCashIn")
      .where("month", "==", month)
      .where("category", "==", category)
      .where("subCategory", "==", subCategory);
    try {
      const expenses = await expensesCollectionRef.get();
      const cashIns = await cashInCollectionRef.get();
      const expensesArray = [];
      expenses.forEach((doc) => {
        expensesArray.push(doc.data());
      });

      cashIns.forEach((doc) => {
        expensesArray.push(doc.data());
      });
      return expensesArray.sort((a, b) => a.id - b.id);
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else if (
    category == "REFUND" ||
    category == "BUY PRODUCTS" ||
    category == "Boosting" ||
    category == "SHIPPING" ||
    category == "TRANSPORT"
  ) {
    const expensesCollectionRef = firestore
      .collection("dailyExpenses")
      .where("month", "==", month)
      .where("category", "==", category);

    try {
      const expenses = await expensesCollectionRef.get();
      const expensesArray = [];
      expenses.forEach((doc) => {
        expensesArray.push(doc.data());
      });
      return expensesArray;
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else if (category == "ORDERS") {
    const expensesCollectionRef = firestore
      .collection("dailyCashIn")
      .where("month", "==", month)
      .where("category", "==", category);

    try {
      const expenses = await expensesCollectionRef.get();
      const expensesArray = [];
      expenses.forEach((doc) => {
        expensesArray.push(doc.data());
      });
      return expensesArray;
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    const expensesCollectionRef = firestore
      .collection("dailyExpenses")
      .where("month", "==", month)
      .where("category", "==", category)
      .where("subCategory", "==", subCategory);
    try {
      const expenses = await expensesCollectionRef.get();
      const expensesArray = [];
      expenses.forEach((doc) => {
        expensesArray.push(doc.data());
      });
      return expensesArray;
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }
};

export const getSingleMonthlySalary = async (month, category) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month)
    .where("category", "==", category);

  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getSingleMonthlyLoanCashIn = async (month, category) => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("month", "==", month)
    .where("category", "==", category);

  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getSingleMonthlyLoanCashOut = async (month, category) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month)
    .where("category", "==", category);

  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllPendingCashInByDay = async (day) => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("date", "==", day)
    .where("status", "==", "pending");
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllPendingCashIns = async () => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("status", "==", "pending");
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllLoansCashOuts = async () => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("category", "==", "LOAN");
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllLoansCashIns = async () => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("category", "==", "LOAN");
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllLoansCashOutCustomer = async (customer) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("category", "==", "LOAN")
    .where("uid", "==", customer);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleCustomerLoan = async (customer) => {
  const loanRef = firestore.doc(`customerLoans/${customer}`);
  try {
    const customerLoan = await loanRef.get();
    return customerLoan.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllInstallmentsCashOutCustomer = async (customer) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("category", "==", "MONTHLY INSTALLMENT")
    .where("uid", "==", customer);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllLoansCashInCustomer = async (customer) => {
  const cashInsCollectionRef = firestore
    .collection("dailyCashIn")
    .where("category", "==", "LOAN")
    .where("uid", "==", customer);
  try {
    const cashIns = await cashInsCollectionRef.get();
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push(doc.data());
    });
    return cashInsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteCashIn = async (cashId) => {
  const cashInRef = firestore.doc(`dailyCashIn/${cashId}`);
  const snapShot = await cashInRef.get();
  if (!snapShot.exists) return;
  const before = snapShot.data();
  try {
    if (before.status === "approved") {
      await adjustCashSummaryForApprovedEntry({
        before,
        after: null,
        sign: 1,
        totalsField: "totalCashIns",
        docWrite: (batch) => batch.delete(cashInRef),
      });
    } else {
      await cashInRef.delete();
    }
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteEmployee = async (employeeId) => {
  const employeeRef = firestore.doc(`employees/${employeeId}`);
  const snapShot = await employeeRef.get();
  console.log(snapShot.data());
  try {
    await employeeRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCustomerLoans = async () => {
  const usersCollectionRef = firestore.collection("customerLoans");
  try {
    const users = await usersCollectionRef.get();
    const usersArray = [];
    users.forEach((doc) => {
      // console.log(doc.id, " => ", doc.data());
      usersArray.push({ uid: doc.id, ...doc.data() });
    });
    return usersArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCustomerInstallments = async () => {
  const usersCollectionRef = firestore.collection("customerInstallments");
  try {
    const users = await usersCollectionRef.get();
    const usersArray = [];
    users.forEach((doc) => {
      // console.log(doc.id, " => ", doc.data());
      usersArray.push({ uid: doc.id, ...doc.data() });
    });
    return usersArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCnfs = async () => {
  const cnfsCollectionRef = firestore.collection("cnfs");
  try {
    const cnfs = await cnfsCollectionRef.get();
    const cnfsArray = [];
    cnfs.forEach((doc) => {
      cnfsArray.push(doc.data());
    });
    return cnfsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadCnf = async (cnfObj) => {
  const cnfRef = firestore.doc(`cnfs/${cnfObj.cnfId}`);
  const snapShot = await cnfRef.get();
  if (!snapShot.exists) {
    try {
      await cnfRef.set({
        ...cnfObj,
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await cnfRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "there is already a cnf with similar name, please change the name and try again",
    );
  }
};

export const updateCnf = async (cnfObj) => {
  const cnfRef = firestore.doc(`cnfs/${cnfObj.cnfId}`);
  try {
    await cnfRef.update({ ...cnfObj });
    const snapShot = await cnfRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteCnf = async (cnfId) => {
  const cnfRef = firestore.doc(`cnfs/${cnfId}`);
  const snapShot = await cnfRef.get();
  console.log(snapShot.data());
  try {
    await cnfRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadCnfBill = async (billObj) => {
  const batch = firestore.batch();
  const cnfBillRef = firestore.doc(`cnfBills/${billObj.id}`);
  const cnfBillMonthRef = firestore.doc(`cnfBillMonths/${billObj.month}`);

  // Get the documents
  const cnfBillSnapShot = await cnfBillRef.get();
  const cnfBillMonthSnapShot = await cnfBillMonthRef.get();

  // Check if the bill document exists
  if (!cnfBillSnapShot.exists) {
    try {
      // Set the bill document
      batch.set(cnfBillRef, {
        ...billObj,
      });
      console.log(cnfBillSnapShot.data());
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a bill with similar id, please try again later");
  }

  // Check if the bill month document exists
  if (!cnfBillMonthSnapShot.exists) {
    // Set the bill month document
    batch.set(cnfBillMonthRef, { month: billObj.month });
  }

  // Commit the batch
  await batch.commit();

  return true;
};

export const getAllMonthsCnfBill = async (month) => {
  const monthRef = firestore.doc(`cnfBillMonths/${month}`);
  const monthdata = await monthRef.get();

  if (!monthdata.exists) {
    await monthRef.set({
      month: month,
    });
  }

  const monthsCollectionRef = firestore.collection(`cnfBillMonths`);
  try {
    const expenses = await monthsCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCnfBills = async (month, name) => {
  const cnfsCollectionRef = firestore
    .collection("cnfBills")
    .where("month", "==", month)
    .where("cnf", "==", name);
  try {
    const cnfs = await cnfsCollectionRef.get();
    const cnfsArray = [];
    cnfs.forEach((doc) => {
      cnfsArray.push(doc.data());
    });
    return cnfsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCnfExpenses = async (month, name) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("month", "==", month)
    .where("subCategory", "==", name);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCnfBillsAllMonths = async (name) => {
  const cnfsCollectionRef = firestore
    .collection("cnfBills")
    .where("cnf", "==", name);
  try {
    const cnfs = await cnfsCollectionRef.get();
    const cnfsArray = [];
    cnfs.forEach((doc) => {
      cnfsArray.push(doc.data());
    });
    return cnfsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCnfExpensesAllMonths = async (name) => {
  const expensesCollectionRef = firestore
    .collection("dailyExpenses")
    .where("subCategory", "==", name);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updateEmployee = async (employeeObj) => {
  const employeeRef = firestore.doc(`employees/${employeeObj.employeeId}`);
  try {
    await employeeRef.update({ ...employeeObj });
    const snapShot = await employeeRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const updateSalary = async (employee) => {
  const categoryMonthlyExpenseRef = firestore.doc(
    `categoryMonthlyExpense/${employee.id}`,
  );
  const categoryMonthlyExpnese = await categoryMonthlyExpenseRef.get();
  await categoryMonthlyExpenseRef.update({
    ...categoryMonthlyExpnese.data(),
    salary: employee.salary,
  });
  const updatedSnapShot = await categoryMonthlyExpenseRef.get();
  return updatedSnapShot.data();
};

export const uploadEmployee = async (employeeObj) => {
  const employeeRef = firestore.doc(`employees/${employeeObj.employeeId}`);
  const snapShot = await employeeRef.get();
  if (!snapShot.exists) {
    try {
      await employeeRef.set({
        ...employeeObj,
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await employeeRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "there is already a employee with similar name, please change the name and try again",
    );
  }
};

export const getAllFunds = async () => {
  const expensesCollectionRef = firestore
    .collection(`dailyExpenses`)
    .where("category", "==", "FUND");

  const cashInCollectionRef = firestore
    .collection(`dailyCashIn`)
    .where("category", "==", "FUND");

  try {
    const expenses = await expensesCollectionRef.get();
    const cashIns = await cashInCollectionRef.get();
    const expensesArray = [];
    const cashInsArray = [];
    cashIns.forEach((doc) => {
      cashInsArray.push({ ...doc.data(), type: "cashIn" });
    });
    expenses.forEach((doc) => {
      expensesArray.push({ ...doc.data(), type: "expense" });
    });
    let mergedArray = [...expensesArray, ...cashInsArray];
    return mergedArray.sort((a, b) => parseInt(a.id) - parseInt(b.id));
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllCashIns = async (day) => {
  const expensesCollectionRef = firestore
    .collection("dailyCashIn")
    .where("date", "==", day);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};
export const getAllCashInsInvest = async (name) => {
  const expensesCollectionRef = firestore
    .collection("dailyCashIn")
    .where("subCategory", "==", name);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });
    return expensesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const createMonth = async () => {
  const date = new Date();
  const monthRef = firestore.doc(`months/${getMonthName()}`);
  const month = await monthRef.get();
  if (month.exists) {
    let monthsArray = [];
    const monthsCollectionRef = firestore.collection("months");
    const monthsCollection = await monthsCollectionRef.get();
    monthsCollection.forEach((doc) => {
      monthsArray.push(doc.data());
    });
    return monthsArray;
  } else {
    await monthRef.set({
      month: getMonthName(),
    });
    let monthsArray = [];
    const monthsCollectionRef = firestore.collection("months");
    const monthsCollection = await monthsCollectionRef.get();
    monthsCollection.forEach((doc) => {
      monthsArray.push(doc.data());
    });
    return monthsArray;
  }
};
export const getMonthName = () => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const d = new Date();
  return `${monthNames[d.getMonth()]},${d.getFullYear()}`;
};

export const getAllSourcingMonthStats = async () => {
  try {
    const snapshot = await firestore.collection("sourcingMonthStats").get();
    const statsArray = [];
    snapshot.forEach((doc) => {
      statsArray.push(doc.data());
    });
    return statsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// Idempotently creates the current month's stats doc (all zero) so it's
// visible in the months list even before its first invoice.
export const ensureCurrentMonthStats = async () => {
  const ref = monthStatsRef(getMonthName());
  try {
    const snap = await ref.get();
    if (!snap.exists) {
      await ref.set({
        month: getMonthName(),
        invoiceCount: 0,
        totalBill: 0,
        totalPaid: 0,
        totalBuyingCost: 0,
        totalAgentProfit: 0,
        totalProfit: 0,
        totalZapaiProfit: 0,
        totalRefundGiven: 0,
        totalRefundTaken: 0,
        byAgent: {},
      });
    }
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// One-time/manual backfill — full scan of the `sourcing` collection,
// grouped by month + agent, overwriting (not incrementing) each
// sourcingMonthStats doc. Not part of any normal page-load path; run once
// to seed historical months that predate this feature.
export const recomputeSourcingMonthStats = async () => {
  try {
    const snapshot = await firestore.collection("sourcing").get();
    const byMonth = {};
    snapshot.forEach((doc) => {
      const s = doc.data();
      if (!s.month) return;
      if (!byMonth[s.month]) {
        byMonth[s.month] = {
          month: s.month,
          invoiceCount: 0,
          totalBill: 0,
          totalPaid: 0,
          totalBuyingCost: 0,
          totalAgentProfit: 0,
          totalProfit: 0,
          totalZapaiProfit: 0,
          totalRefundGiven: 0,
          totalRefundTaken: 0,
          byAgent: {},
        };
      }
      const bucket = byMonth[s.month];
      const addTo = (target) => {
        target.invoiceCount += 1;
        target.totalBill += parseFloat(s.totalBill) || 0;
        target.totalPaid += parseFloat(s.totalPaid) || 0;
        target.totalBuyingCost += parseFloat(s.buyingCost) || 0;
        target.totalAgentProfit += parseFloat(s.agentPaid) || 0;
        target.totalProfit += parseFloat(s.finalProfit) || 0;
        target.totalZapaiProfit += parseFloat(s.algProfit) || 0;
        target.totalRefundGiven += sumRefundArray(s.givenRefund);
        target.totalRefundTaken +=
          sumRefundArray(s.receivedRefund) * (parseFloat(s.buyRate) || 0);
      };
      addTo(bucket);
      if (s.agent) {
        if (!bucket.byAgent[s.agent]) {
          bucket.byAgent[s.agent] = {
            invoiceCount: 0,
            totalBill: 0,
            totalPaid: 0,
            totalBuyingCost: 0,
            totalAgentProfit: 0,
            totalProfit: 0,
            totalZapaiProfit: 0,
            totalRefundGiven: 0,
            totalRefundTaken: 0,
          };
        }
        addTo(bucket.byAgent[s.agent]);
      }
    });
    const batch = firestore.batch();
    Object.values(byMonth).forEach((doc) => {
      batch.set(monthStatsRef(doc.month), doc);
    });
    await batch.commit();
    return Object.values(byMonth);
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// Approves every pending item in `expenses` (all dailyCashIn docs for one
// day) as a single atomic operation, together with the day's cash summary —
// same rationale and grouping strategy as approveExpensesBatch above (see
// its comment); mirrors the old approveCashIn's category branches exactly
// (LOAN subtracts from the customer's loan balance — intentional, cash
// coming in reduces what they owe; ORDERS accumulates into
// categoryMonthlyExpense; anything else only touches monthlyCashIn).
// Same contract as approveExpensesBatch: a passed `total` is ignored in
// favour of the amount summed from the re-read docs.
export const approveCashInsBatch = async (
  expenses,
  { month, date },
  { reconcile = true } = {},
) => {
  if (!expenses || expenses.length === 0) {
    return { success: true, approvedIds: [] };
  }

  try {
    // Phase A
    const expenseRefs = expenses.map((e) => firestore.doc(`dailyCashIn/${e.id}`));
    const expenseSnaps = await Promise.all(expenseRefs.map((ref) => ref.get()));

    for (let i = 0; i < expenseSnaps.length; i++) {
      if (!expenseSnaps[i].exists || expenseSnaps[i].data().status !== "pending") {
        return {
          success: false,
          reason: "stale-item",
          message: `"${expenses[i].subCategory || expenses[i].note || expenses[i].id}" is no longer pending — nothing was approved.`,
          approvedIds: [],
        };
      }
    }
    const resolved = expenseSnaps.map((snap) => ({ id: snap.id, ...snap.data() }));

    // Same as approveExpensesBatch: the cash summary moves by what the
    // documents say, on the day the documents carry.
    const cashFacts = resolveCashSummaryFacts(resolved, date);
    if (cashFacts.error) {
      return {
        success: false,
        reason: "mixed-dates",
        message: cashFacts.error,
        approvedIds: [],
      };
    }

    // Phase B — group by target document, summing deltas.
    const targets = new Map();
    const addTarget = (ref, amount, kind, meta) => {
      const key = ref.path;
      if (!targets.has(key)) targets.set(key, { ref, deltaAmount: 0, kind, meta });
      const entry = targets.get(key);
      entry.deltaAmount += amount;
      entry.meta = meta;
    };

    resolved.forEach((expense) => {
      const amount = parseInt(expense.amount) || 0;

      if (expense.category === "LOAN") {
        addTarget(
          firestore.doc(`customerLoans/${expense.uid}`),
          -amount, // cash in reduces the loan balance
          "customer",
          { customer: expense.subCategory, uid: expense.uid },
        );
      } else if (expense.category === "ORDERS") {
        const categoryMonthlyExpenseId = `${expense.month}-${expense.category}`;
        addTarget(
          firestore.doc(`categoryMonthlyExpense/${categoryMonthlyExpenseId}`),
          amount,
          "categoryMonthlyExpense",
          {
            id: categoryMonthlyExpenseId, // fixed: was previously written including subCategory, which never matched this doc's actual (subCategory-agnostic) key
            category: expense.category,
            subCategory: expense.subCategory,
            month: expense.month,
          },
        );
      }

      addTarget(firestore.doc(`monthlyCashIn/${expense.month}`), amount, "monthlyExpense", {
        id: expense.month,
      });
    });

    // Phase C
    const targetEntries = Array.from(targets.values());
    const targetSnaps = await Promise.all(targetEntries.map((t) => t.ref.get()));

    const cashSummaryCtx = await readCashSummaryContext(cashFacts.date);

    const estimatedOps =
      resolved.length + targetEntries.length + 1 + cashSummaryCtx.forwardDayRefs.length;
    if (estimatedOps > APPROVAL_BATCH_OP_LIMIT) {
      return {
        success: false,
        reason: "too-many-items",
        message: `Too many pending items to approve atomically at once (${estimatedOps} operations). Please approve in smaller groups.`,
        approvedIds: [],
      };
    }

    // Phase D
    const batch = firestore.batch();

    resolved.forEach((expense) => {
      batch.update(firestore.doc(`dailyCashIn/${expense.id}`), { status: "approved" });
    });

    targetEntries.forEach((entry, i) => {
      const snap = targetSnaps[i];
      const existingAmount = snap.exists ? parseInt(snap.data().amount) || 0 : 0;
      const newAmount = existingAmount + entry.deltaAmount;

      if (entry.kind === "categoryMonthlyExpense") {
        if (snap.exists) {
          batch.update(entry.ref, { amount: newAmount });
        } else {
          batch.set(entry.ref, {
            id: entry.meta.id,
            amount: newAmount,
            category: entry.meta.category,
            subCategory: entry.meta.subCategory,
            month: entry.meta.month,
            paid: false,
            salary: 0,
          });
        }
      } else if (entry.kind === "customer") {
        if (snap.exists) {
          batch.update(entry.ref, { amount: newAmount, customer: entry.meta.customer });
        } else {
          batch.set(entry.ref, {
            customer: entry.meta.customer,
            amount: newAmount,
            uid: entry.meta.uid,
          });
        }
      } else if (entry.kind === "monthlyExpense") {
        if (snap.exists) {
          batch.update(entry.ref, { amount: newAmount });
        } else {
          batch.set(entry.ref, { id: entry.meta.id, amount: newAmount });
        }
      }
    });

    // Phase E — cash-in adds to remainingBalance (opposite sign from
    // approveExpensesBatch's cash-out), matching updateCashSummaryCashIn.
    queueCashSummaryWrites(batch, cashSummaryCtx, {
      month,
      date: cashFacts.date,
      total: cashFacts.total,
      sign: 1,
      totalsField: "totalCashIns",
    });

    // Phase F
    await batch.commit();

    // Phase G — verify the committed day against its own entries.
    // Skipped for all but the last chunk of a chunked approval — see
    // approveEntriesInChunks.
    const reconciled = reconcile ? await reconcileCashSummaryDay(cashFacts.date) : null;

    return {
      success: true,
      approvedIds: resolved.map((e) => e.id),
      reconciled,
    };
  } catch (error) {
    console.error("Error approving cash-ins:", error);
    return {
      success: false,
      reason: "batch-commit-failure",
      message: error.message || "Failed to approve cash-ins — nothing was changed.",
      approvedIds: [],
    };
  }
};

// Items per atomic commit when approving a whole day. Cash-outs cost up to
// ~3 writes per item (the item + its category/salary/customer rollups), so
// 100 stays well under APPROVAL_BATCH_OP_LIMIT; a chunk that still doesn't
// fit (e.g. a long forward-propagation chain) is halved and retried.
const APPROVAL_CHUNK_SIZE = 100;

// Approves a day's pending entries in consecutive chunks, each one committed
// atomically by approveExpensesBatch/approveCashInsBatch together with its
// own share of the cash-summary movement — so after every chunk the stored
// day totals match exactly the entries approved so far.
//
// Not all-or-nothing across chunks: if one fails, the earlier chunks stay
// approved and the rest stay pending. The result reports how far it got
// (`approvedIds` vs `total`), and re-running with the still-pending entries
// finishes the job. `onProgress(done, total)` fires after each commit.
export const approveEntriesInChunks = async (
  type,
  items,
  { month, date },
  onProgress,
) => {
  const approveBatch = type === "expense" ? approveExpensesBatch : approveCashInsBatch;
  const total = items.length;
  const approvedIds = [];
  let chunkSize = APPROVAL_CHUNK_SIZE;
  let i = 0;

  if (onProgress) onProgress(0, total);

  while (i < total) {
    const chunk = items.slice(i, i + chunkSize);
    const isLast = i + chunk.length >= total;
    const result = await approveBatch(chunk, { month, date }, { reconcile: isLast });

    if (!result.success) {
      if (result.reason === "too-many-items" && chunkSize > 1) {
        chunkSize = Math.max(1, Math.floor(chunkSize / 2));
        continue;
      }
      // Earlier chunks skipped the day check; run it once for what did land.
      const reconciled = approvedIds.length
        ? await reconcileCashSummaryDay(items[0].date || date)
        : null;
      return { ...result, success: false, approvedIds, total, reconciled };
    }

    approvedIds.push(...result.approvedIds);
    i += chunk.length;
    if (onProgress) onProgress(approvedIds.length, total);

    if (isLast) {
      return { success: true, approvedIds, total, reconciled: result.reconciled };
    }
  }

  return { success: true, approvedIds, total, reconciled: null };
};

export const createSalaryMonth = async (month, employee) => {
  const categoryMonthlyExpenseRef = firestore.doc(
    `categoryMonthlyExpense/${month}-SALARY-${employee.name}`,
  );
  const categoryMonthlyExpnese = await categoryMonthlyExpenseRef.get();
  if (!categoryMonthlyExpnese.exists) {
    await categoryMonthlyExpenseRef.set({
      id: `${month}-SALARY-${employee.name}`,
      amount: 0,
      category: "SALARY",
      subCategory: employee.name,
      month: month,
      paid: false,
      salary: 0,
    });
  }
};

export const getMonth = async (employee, month) => {
  const expensesCollectionRef = firestore
    .collection(`categoryMonthlyExpense`)
    .where("category", "==", "SALARY")
    .where("subCategory", "==", employee);
  try {
    const expenses = await expensesCollectionRef.get();
    const expensesArray = [];
    expenses.forEach((doc) => {
      expensesArray.push(doc.data());
    });

    const unPaidMonths = expensesArray.filter((expense) => !expense.paid);
    return unPaidMonths;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// updateCashSummaryCashOut/CashIn were folded into approveExpensesBatch/
// approveCashInsBatch above (see queueCashSummaryWrites/readCashSummaryContext)
// so the whole approval + cash-summary update commits as one atomic batch —
// they had exactly one caller each (the approve action) and are no longer
// called standalone.

export const getAllScreenShot = async () => {
  const cnfsCollectionRef = firestore.collection("screenshots");
  try {
    const cnfs = await cnfsCollectionRef.get();
    const cnfsArray = [];
    cnfs.forEach((doc) => {
      cnfsArray.push(doc.data());
    });
    return cnfsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadScreenShot = async (cnfObj) => {
  const cnfRef = firestore.doc(`screenshots/${cnfObj.id}`);
  const snapShot = await cnfRef.get();
  if (!snapShot.exists) {
    try {
      await cnfRef.set({
        ...cnfObj,
      });
      console.log(snapShot.data());
      const uploadedSnapShot = await cnfRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "there is already a cnf with similar name, please change the name and try again",
    );
  }
};

export const updateScreenShot = async (cnfObj) => {
  const cnfRef = firestore.doc(`screenshots/${cnfObj.id}`);
  try {
    await cnfRef.update({ ...cnfObj });
    const snapShot = await cnfRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteScreenShot = async (cnfId) => {
  const cnfRef = firestore.doc(`screenshots/${cnfId}`);
  const snapShot = await cnfRef.get();
  console.log(snapShot.data());
  try {
    await cnfRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllTransactions = async (productId, month) => {
  const employeesCollectionRef = firestore
    .collection("transactions")
    .where("productId", "==", productId)
    .where("month", "==", month);
  try {
    const employees = await employeesCollectionRef.get();
    const employeesArray = [];
    employees.forEach((doc) => {
      employeesArray.push(doc.data());
    });
    return employeesArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllTransactionsAllTime = async (productId) => {
  try {
    const snap = await firestore
      .collection("transactions")
      .where("productId", "==", productId)
      .get();
    return snap.docs.map((doc) => doc.data());
  } catch (error) {
    notifyError(error.message || String(error));
    return [];
  }
};

const updateProductStats = async (productId, soldDelta, revenueDelta) => {
  try {
    const statsRef = firestore.doc(`productStats/${productId}`);
    await statsRef.set(
      {
        productId,
        totalSold: firebase.firestore.FieldValue.increment(soldDelta),
        totalRevenue: firebase.firestore.FieldValue.increment(revenueDelta || 0),
      },
      { merge: true },
    );
  } catch (error) {
    console.error("productStats update failed:", error);
  }
};

export const uploadTransaction = async (transactionObj) => {
  const transactionRef = firestore.doc(`transactions/${transactionObj.id}`);
  const snapShot = await transactionRef.get();
  if (!snapShot.exists) {
    try {
      await transactionRef.set({
        ...transactionObj,
      });

      if (transactionObj.productId) {
        if (transactionObj.fundType === "Selling") {
          await updateProductStats(
            transactionObj.productId,
            parseInt(transactionObj.sellStock) || 0,
            parseInt(transactionObj.totalBill) || 0,
          );
        } else if (
          transactionObj.fundType === "Buying" &&
          (transactionObj.orderStatus === "Cancelled" ||
            transactionObj.purchasePrice === "Partial delivered")
        ) {
          const qty = parseInt(transactionObj.productStock) || 0;
          const revenue =
            transactionObj.orderStatus === "Cancelled"
              ? -(parseInt(transactionObj.totalCost) || 0)
              : 0;
          await updateProductStats(transactionObj.productId, -qty, revenue);
        }
      }

      const uploadedSnapShot = await transactionRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "there is already a transaction with similar name, please change the name and try again",
    );
  }
};

// Consumes purchase lots (transactions with fundType "Buying") oldest-first to
// determine the actual cost of goods sold for a sale of `qtyNeeded` units.
// Lots are matched on productId (+ variationId for variable products) and
// ordered by their (time-based) id, since lots have no createdAt timestamp field.
export const consumeFifoCogs = async ({ productId, variationId, qtyNeeded }) => {
  const needed = parseInt(qtyNeeded) || 0;
  if (!productId || needed <= 0) {
    return { totalCogs: 0, breakdown: [], shortfall: needed };
  }

  const candidatesSnap = await firestore
    .collection("transactions")
    .where("productId", "==", productId)
    .where("fundType", "==", "Buying")
    .get();

  const candidateLots = candidatesSnap.docs
    .map((doc) => ({ id: doc.id, ref: doc.ref, ...doc.data() }))
    .filter((lot) =>
      variationId ? lot.variationId === variationId : !lot.variationId,
    )
    .filter((lot) => (parseInt(lot.remainingQty) || 0) > 0)
    .sort((a, b) => Number(a.id) - Number(b.id));

  if (candidateLots.length === 0) {
    return { totalCogs: 0, breakdown: [], shortfall: needed };
  }

  try {
    return await firestore.runTransaction(async (t) => {
      // all reads must happen before any writes
      const freshLots = await Promise.all(
        candidateLots.map((lot) => t.get(lot.ref)),
      );

      let qtyStillNeeded = needed;
      let totalCogs = 0;
      const breakdown = [];

      for (let i = 0; i < freshLots.length && qtyStillNeeded > 0; i++) {
        const snap = freshLots[i];
        if (!snap.exists) continue;
        const lotData = snap.data();
        const available = parseInt(lotData.remainingQty) || 0;
        if (available <= 0) continue;

        const qtyTaken = Math.min(available, qtyStillNeeded);
        const perUnitPrice = parseFloat(lotData.perUnitPrice) || 0;

        totalCogs += qtyTaken * perUnitPrice;
        breakdown.push({ lotId: snap.id, qty: qtyTaken, perUnitPrice });
        qtyStillNeeded -= qtyTaken;

        t.update(snap.ref, { remainingQty: available - qtyTaken });
      }

      return { totalCogs, breakdown, shortfall: qtyStillNeeded };
    });
  } catch (error) {
    notifyError(error.message || String(error));
    return { totalCogs: 0, breakdown: [], shortfall: needed };
  }
};

// Reverses a previous consumeFifoCogs call (e.g. on order cancellation) by
// restoring qty back onto the exact lots it was originally taken from.
export const restoreFifoCogs = async (cogsBreakdown) => {
  if (!Array.isArray(cogsBreakdown) || cogsBreakdown.length === 0) return;

  try {
    await firestore.runTransaction(async (t) => {
      const refs = cogsBreakdown.map((entry) =>
        firestore.doc(`transactions/${entry.lotId}`),
      );
      const snaps = await Promise.all(refs.map((ref) => t.get(ref)));

      snaps.forEach((snap, i) => {
        if (!snap.exists) return;
        const entry = cogsBreakdown[i];
        const current = parseInt(snap.data().remainingQty) || 0;
        t.update(snap.ref, {
          remainingQty: current + (parseInt(entry.qty) || 0),
        });
      });
    });
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

// Shared per-order-line-item FIFO application, used by every place an order
// can transition to "Delivered": single-order submit, bulk status change,
// and Steadfast payment reconciliation. Mutates nothing itself — returns the
// updated line items (with costPrice/cogsBreakdown attached) plus the total
// COGS for the order, for the caller to persist alongside its own writes.
export const applyFifoCogsToOrderLineItems = async (lineItems) => {
  const updatedItems = [];
  let orderTotalCogs = 0;

  for (const item of lineItems || []) {
    const qty = parseInt(item.quantity) || 0;
    if (!item.productId || qty <= 0) {
      updatedItems.push(item);
      continue;
    }

    const { totalCogs, breakdown, shortfall } = await consumeFifoCogs({
      productId: item.productId,
      variationId: item.selectedVariation?.id,
      qtyNeeded: qty,
    });

    orderTotalCogs += totalCogs;
    updatedItems.push({
      ...item,
      costPrice: totalCogs,
      cogsBreakdown: breakdown,
      cogsShortfallQty: shortfall,
    });
  }

  return { orders: updatedItems, totalCogs: orderTotalCogs };
};

// Reverses applyFifoCogsToOrderLineItems for an order being cancelled or
// decreased after cogsApplied was already set.
export const restoreFifoCogsForOrderLineItems = async (lineItems) => {
  for (const item of lineItems || []) {
    if (Array.isArray(item.cogsBreakdown) && item.cogsBreakdown.length > 0) {
      await restoreFifoCogs(item.cogsBreakdown);
    }
  }
};

const getLineItemUnitPrice = (order) => {
  if (order.selectedVariation?.id) {
    return order.selectedVariation.salePrice == 0
      ? order.selectedVariation.price
      : order.selectedVariation.salePrice;
  }
  if (order.product) {
    return order.product.salePrice == 0
      ? order.product.price
      : order.product.salePrice;
  }
  return 0;
};

// The one restock path for a cancelled order — used by the manual cancel in
// 1688-taobao-orders/new.js (handleSubmit) and by Steadfast courier
// reconciliation. Restores stock/FIFO lots and logs a "Stock In / Cancelled"
// transaction per line item. Each line's restock is keyed by a deterministic
// transaction id (the "steadfast_" prefix is historical — keep it so both
// paths dedupe against each other), checked before any write, so calling
// this more than once for the same order is a safe no-op.
export const restoreProductStockForCancelledOrder = async (
  orderObj,
  { cancelNote = "", adminName = "" } = {}
) => {
  const lineItems = orderObj.orders || [];
  for (let i = 0; i < lineItems.length; i++) {
    const order = lineItems[i];
    if (!order?.productId) continue;

    const transactionId = `steadfast_cancel_${orderObj.id}_${i}`;
    const transactionRef = firestore.doc(`transactions/${transactionId}`);
    const existing = await transactionRef.get();
    if (existing.exists) continue;

    const product = await getSingleProduct(order.productId);
    if (!product) continue;

    if (Array.isArray(order.cogsBreakdown) && order.cogsBreakdown.length > 0) {
      await restoreFifoCogs(order.cogsBreakdown);
    }

    const unitPrice = getLineItemUnitPrice(order);
    const totalCost = parseInt(unitPrice || 0) * parseInt(order.quantity || 0);
    const date = new Date();

    const restocked = await adjustProductStock({
      productId: product.id,
      variationId:
        product.displayedVariations?.length > 0
          ? order.selectedVariation?.id || null
          : null,
      delta: parseInt(order.quantity) || 0,
    });
    if (!restocked) continue;
    const remainingStock = restocked.remaining;

    await uploadTransaction({
      id: transactionId,
      fundType: "Buying",
      subCategory: `Order Id:${orderObj.id}`,
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      purchasePrice: "Cancelled by admin",
      cancelNote,
      orderStatus: "Cancelled",
      totalCost,
      perUnitPrice: unitPrice,
      expenseBy: adminName,
      unEditable: true,
      productId: product.id,
      productStock: order.quantity,
      remainingStock,
      productName: product.name,
      productImage: product.pictures?.[0],
      variationId: order.selectedVariation?.id,
    });
  }
  await updateTransactionOrderStatus({
    orderId: orderObj.id,
    orderStatus: "Cancelled",
  });
};

export const backfillProductStats = async () => {
  const snap = await firestore.collection("transactions").get();
  const stats = {};

  snap.docs.forEach((doc) => {
    const t = doc.data();
    if (!t.productId) return;
    if (!stats[t.productId])
      stats[t.productId] = { totalSold: 0, totalRevenue: 0 };

    if (t.fundType === "Selling") {
      stats[t.productId].totalSold += parseInt(t.sellStock) || 0;
      stats[t.productId].totalRevenue += parseInt(t.totalBill) || 0;
    } else if (
      t.fundType === "Buying" &&
      (t.orderStatus === "Cancelled" ||
        t.purchasePrice === "Partial delivered")
    ) {
      stats[t.productId].totalSold -= parseInt(t.productStock) || 0;
      if (t.orderStatus === "Cancelled") {
        stats[t.productId].totalRevenue -= parseInt(t.totalCost) || 0;
      }
    }
  });

  const batch = firestore.batch();
  Object.entries(stats).forEach(([productId, data]) => {
    const ref = firestore.doc(`productStats/${productId}`);
    batch.set(ref, { productId, ...data });
  });
  await batch.commit();
};

export const getAllProductStats = async () => {
  const snap = await firestore
    .collection("productStats")
    .orderBy("totalSold", "desc")
    .get();
  return snap.docs.map((doc) => doc.data());
};

export const updateTransaction = async (transactionObj) => {
  const transactionRef = firestore.doc(`transactions/${transactionObj.id}`);
  try {
    await transactionRef.update({ ...transactionObj });
    const snapShot = await transactionRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteTransaction = async (id) => {
  const transactionRef = firestore.doc(`transactions/${id}`);
  const snapShot = await transactionRef.get();
  console.log(snapShot.data());
  try {
    await transactionRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const addCreatedAtInBatches = async () => {
  const batchSize = 20;
  let lastDoc = null;
  let totalUpdated = 0;
  let hasMore = true;

  while (hasMore) {
    let query = firestore
      .collection("orders")
      .orderBy(firebase.firestore.FieldPath.documentId())
      .limit(batchSize);

    if (lastDoc) {
      query = query.startAfter(lastDoc);
    }

    const snapshot = await query.get();
    if (snapshot.empty) {
      console.log("✅ No more documents to update.");
      break;
    }
    const batch = firestore.batch();
    let updateCount = 0;

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (!data.createdAt) {
        const docRef = firestore.collection("orders").doc(docSnap.id);
        batch.update(docRef, {
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        updateCount++;
      }
    });

    if (updateCount > 0) {
      await batch.commit();
      totalUpdated += updateCount;
      console.log(`✅ Updated ${updateCount} documents in this batch.`);
    } else {
      console.log(`ℹ️ No updates needed in this batch.`);
    }

    lastDoc = snapshot.docs[snapshot.docs.length - 1];
    hasMore = snapshot.size === batchSize;
  }

  console.log(`🎉 Finished. Total documents updated: ${totalUpdated}`);
};

export const getAllPartials = async () => {
  const productsCollectionRef = firestore.collection("partial-payments");
  try {
    const products = await productsCollectionRef.get();
    const productsArray = [];
    products.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadPartial = async (productObj) => {
  const productsRef = firestore.collection("partial-payments");
  const products = await productsRef.get();
  products.forEach(async (doc) => {
    const proRef = firestore.doc(`partial-payments/${doc.id}`);
    await proRef.delete();
  });
  const productRef = firestore.doc(`partial-payments/${productObj.id}`);
  const snapShot = await productRef.get();
  const newProductObj = { ...productObj, file: "" };
  if (!snapShot.exists) {
    try {
      await productRef.set({ ...newProductObj });
      const updatedSnapShot = await productRef.get();
      return updatedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError("there is already a partial payment with similar id");
  }
};

export const updatePartial = async (productObj) => {
  const productRef = firestore.doc(`partial-payments/${productObj.id}`);
  try {
    delete productObj.file;
    await productRef.update({ ...productObj });
    const updatedSnapShot = await productRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deletePartial = async (id) => {
  const productRef = firestore.doc(`partial-payments/${id}`);
  try {
    await productRef.delete();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const get1688PaymentsByDateRange = async (
  startDate,
  endDate,
  linkStatus,
  rmbStatus,
) => {
  // Local midnight, not UTC — see getAllOrdersApi.
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);
  const startTs = firebase.firestore.Timestamp.fromDate(start);
  const endTs = firebase.firestore.Timestamp.fromDate(end);
  let query = firebase
    .firestore()
    .collection("paymentLinks1688")
    .where("createdAt", ">=", startTs)
    .where("createdAt", "<=", endTs);
  if (linkStatus && linkStatus.trim() !== "") {
    query = query.where("linkStatus", "==", linkStatus);
  }
  if (rmbStatus && rmbStatus.trim() !== "") {
    query = query.where("rmbStatus", "==", rmbStatus);
  }
  const snapshot = await query.get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getSinglePayment1688 = async (paymentId) => {
  try {
    const ref = firestore.doc(`paymentLinks1688/${paymentId}`);
    const snap = await ref.get();
    return snap.data();
  } catch (error) {
    notifyError(error.message || String(error));
    return null;
  }
};

export const updatepaymentLink1688 = async (
  paymentObj,
  rmbRate,
  totalRmb,
  imageUrl,
) => {
  try {
    const batch = firebase.firestore().batch();
    const paymentLinksRef = firebase
      .firestore()
      .collection("paymentLinks1688")
      .doc(paymentObj.paymentId);
    batch.update(paymentLinksRef, {
      rmbStatus: "Paid",
      rmbRate,
      totalRmbPaid: totalRmb,
      receipt: imageUrl,
    });
    paymentObj.orders.forEach((order) => {
      const orderRef = firebase
        .firestore()
        .collection("ordersApi")
        .doc(`${order.orderId}`);
      batch.update(orderRef, { exchangeRate: rmbRate });
    });
    let date = new Date();
    let expenseObj = {
      id: `${date.getTime().toString()}`,
      category: "RMB",
      subCategory: "1688 Orders",
      month: getMonthName(),
      date: date.toLocaleDateString("en-GB"),
      note: `Payment Id: ${paymentObj.paymentId}, Total Rmb: ${totalRmb}, rmb Rate:${rmbRate}`,
      amount: parseInt(totalRmb * rmbRate),
      receiveBy: "admin",
      status: "pending",
      unEditable: true,
    };
    const expenseRef = firestore.doc(`dailyExpenses/${expenseObj.id}`);
    batch.set(expenseRef, expenseObj);
    await batch.commit();
    return paymentObj;
  } catch (error) {
    return null;
  }
};

export const updatepaymentLink = async (paymentObj) => {
  try {
    const batch = firebase.firestore().batch();
    const paymentLinksRef = firebase
      .firestore()
      .collection("paymentLinks1688")
      .doc(paymentObj.paymentId);
    batch.update(paymentLinksRef, { paymentLink: paymentObj.paymentLink });
    await batch.commit();
    return paymentObj;
  } catch (error) {
    return null;
  }
};

export const updateOrdersWithPaymentLink = async (
  totalRmb,
  orders,
  paymentLink,
) => {
  try {
    const batch = firebase.firestore().batch();
    const paymentLinkId = Date.now().toString();
    const paymentLinksRef = firebase
      .firestore()
      .collection("paymentLinks1688")
      .doc(paymentLinkId);
    batch.set(paymentLinksRef, {
      createdAt: firebase.firestore.Timestamp.now(),
      paymentId: paymentLinkId,
      date: new Date().toLocaleDateString("en-GB"),
      paymentLink,
      totalRmb,
      linkStatus: "Not Paid",
      rmbStatus: "Not Paid",
      orders,
    });
    orders.forEach((order) => {
      const orderRef = firebase
        .firestore()
        .collection("ordersApi")
        .doc(`${order.orderId}`);
      batch.update(orderRef, { paymentLink1688: paymentLink, paymentLinkId });
    });
    await batch.commit();
    return orders;
  } catch (err) {
    console.log(err);
    return [];
  }
};

export const getSingleUser = async (id) => {
  const userRef = firestore.doc(`users/${id}`);
  try {
    const user = await userRef.get();
    return user.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleOrderApi = async (id) => {
  const orderRef = firestore.doc(`ordersApi/${id}`);
  try {
    const order = await orderRef.get();
    return normalizeOrderApiDoc(order.data());
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleProductRequest = async (id) => {
  const orderRef = firestore.doc(`bookingRequest/${id}`);
  try {
    const order = await orderRef.get();
    return order.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

let totalUploaded1688 = 0;

export const upload1688Category = async (category) => {
  const productRef = firestore.doc(`1688Category/${category.categoryId}`);
  const snapShot = await productRef.get();
  if (!snapShot.exists) {
    try {
      productRef.set({
        ...category,
        level1: firebase.firestore.FieldValue.arrayUnion(category.categoryId),
      });
      totalUploaded1688 += 1;
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }
};

export const addToRootCategory = async (category) => {
  const productRef = firestore.doc(`1688Category/${category.parentCateId}`);
  const snapShot = await productRef.get();
  if (snapShot.exists) {
    try {
      productRef.update({
        level2: firebase.firestore.FieldValue.arrayUnion(category.categoryId),
      });
      totalUploaded1688 += 1;
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }
};

export const level3Category = async (category) => {
  const querySnapshot = await firestore
    .collection("1688Category")
    .where("level2", "array-contains", category.parentCateId)
    .get();
  for (let doc of querySnapshot.docs) {
    await doc.ref.update({
      level3: firebase.firestore.FieldValue.arrayUnion(category.categoryId),
    });
  }
  totalUploaded1688 += 1;
};

export const bulkUpdateOrderStatus = async (ids, orderStatus) => {
  const oldSnaps = await Promise.all(ids.map((id) => firestore.doc(`ordersList/${id}`).get()));

  // A handful of legacy writes created ordersList docs without a createdAt
  // field — such docs are invisible to every date-ordered list query (they
  // fail Firestore's orderBy("createdAt") filter entirely) but still get
  // counted by a full rescan (which orders by document id instead), which is
  // why the stats pill can drift even right after a "Recalculate". Backfill
  // it here from the source order whenever we touch one of these.
  const missingCreatedAtIds = ids.filter((id, i) => !oldSnaps[i].data()?.createdAt);
  const backfillCreatedAt = {};
  if (missingCreatedAtIds.length > 0) {
    const orderSnaps = await Promise.all(
      missingCreatedAtIds.map((id) => firestore.doc(`orders/${id}`).get())
    );
    missingCreatedAtIds.forEach((id, i) => {
      backfillCreatedAt[id] =
        orderSnaps[i].data()?.createdAt || firebase.firestore.FieldValue.serverTimestamp();
    });
  }

  const batch = firestore.batch();
  const numDelta = {};
  const dailyNumDeltas = {}; // dayDocPath → { ref, n: { count, totalBill, ... } }

  ids.forEach((id, i) => {
    batch.update(firestore.doc(`orders/${id}`), { orderStatus });
    const listPatch = {
      orderStatus,
      ...paymentDueFields(oldSnaps[i].data() || {}, orderStatus),
    };
    if (backfillCreatedAt[id]) listPatch.createdAt = backfillCreatedAt[id];
    batch.set(firestore.doc(`ordersList/${id}`), listPatch, { merge: true });

    const old = oldSnaps[i].data() || {};
    const oldStatus = old.orderStatus || "";
    const bill = old.totalBill || 0, paid = old.totalPaid || 0, disc = old.finalDiscount || 0;

    // Only touch the counters when the status is actually changing — a
    // redundant call on an order already at the target status (e.g. a
    // re-click on rows that already moved, since the list doesn't remove
    // them after a bulk move) must be a no-op, not an extra +1 with no
    // matching -1 anywhere. Previously the decrement was gated but the
    // increment below always fired, which is what let the target bucket's
    // count/totalBill/totalPaid/totalDiscount silently inflate over time.
    if (oldStatus !== orderStatus) {
      if (oldStatus) {
        numDelta[`${oldStatus}.count`]         = (numDelta[`${oldStatus}.count`]         || 0) - 1;
        numDelta[`${oldStatus}.totalBill`]     = (numDelta[`${oldStatus}.totalBill`]     || 0) - bill;
        numDelta[`${oldStatus}.totalPaid`]     = (numDelta[`${oldStatus}.totalPaid`]     || 0) - paid;
        numDelta[`${oldStatus}.totalDiscount`] = (numDelta[`${oldStatus}.totalDiscount`] || 0) - disc;
      }
      numDelta[`${orderStatus}.count`]         = (numDelta[`${orderStatus}.count`]         || 0) + 1;
      numDelta[`${orderStatus}.totalBill`]     = (numDelta[`${orderStatus}.totalBill`]     || 0) + bill;
      numDelta[`${orderStatus}.totalPaid`]     = (numDelta[`${orderStatus}.totalPaid`]     || 0) + paid;
      numDelta[`${orderStatus}.totalDiscount`] = (numDelta[`${orderStatus}.totalDiscount`] || 0) + disc;

      const oldContribution = paymentDueContribution(old, oldStatus);
      const newContribution = paymentDueContribution(old, orderStatus);
      if (oldContribution) {
        numDelta["Payment Due.count"] = (numDelta["Payment Due.count"] || 0) - 1;
        numDelta["Payment Due.totalBill"] = (numDelta["Payment Due.totalBill"] || 0) - oldContribution.totalBill;
        numDelta["Payment Due.totalPaid"] = (numDelta["Payment Due.totalPaid"] || 0) - oldContribution.totalPaid;
        numDelta["Payment Due.totalDiscount"] = (numDelta["Payment Due.totalDiscount"] || 0) - oldContribution.totalDiscount;
      }
      if (newContribution) {
        numDelta["Payment Due.count"] = (numDelta["Payment Due.count"] || 0) + 1;
        numDelta["Payment Due.totalBill"] = (numDelta["Payment Due.totalBill"] || 0) + newContribution.totalBill;
        numDelta["Payment Due.totalPaid"] = (numDelta["Payment Due.totalPaid"] || 0) + newContribution.totalPaid;
        numDelta["Payment Due.totalDiscount"] = (numDelta["Payment Due.totalDiscount"] || 0) + newContribution.totalDiscount;
      }

      // Daily doc delta — accumulate per day (orders created same day share one doc)
      const dRef = dayDocRef(old.createdAt);
      if (dRef) {
        const path = dRef.path;
        if (!dailyNumDeltas[path]) dailyNumDeltas[path] = { ref: dRef, n: { count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0, cancelledCount: 0, cancelledBill: 0 } };
        const nd = dailyNumDeltas[path].n;
        const oldCancelled = oldStatus === "Cancelled";
        const newCancelled = orderStatus === "Cancelled";
        if (oldCancelled) { nd.cancelledCount -= 1; nd.cancelledBill -= bill; }
        else              { nd.count -= 1; nd.totalBill -= bill; nd.totalPaid -= paid; nd.totalDiscount -= disc; }
        if (newCancelled) { nd.cancelledCount += 1; nd.cancelledBill += bill; }
        else              { nd.count += 1; nd.totalBill += bill; nd.totalPaid += paid; nd.totalDiscount += disc; }
      }
    }
  });

  // Apply status summary update
  const summaryUpdate = {};
  Object.entries(numDelta).forEach(([k, v]) => { if (v !== 0) summaryUpdate[k] = inc(v); });
  if (Object.keys(summaryUpdate).length > 0) {
    try { batch.update(SUMMARY_DOC(), summaryUpdate); } catch (_) {}
  }

  // Apply daily doc updates (one batch.set per distinct creation day)
  Object.values(dailyNumDeltas).forEach(({ ref, n }) => {
    const delta = {};
    Object.entries(n).forEach(([k, v]) => { if (v !== 0) delta[k] = inc(v); });
    if (Object.keys(delta).length > 0) batch.set(ref, delta, { merge: true });
  });

  await batch.commit();
  await syncTransactionStatuses(
    ids
      .filter((id, i) => (oldSnaps[i].data() || {}).orderStatus !== orderStatus)
      .map((orderId) => ({ orderId, orderStatus })),
  );
};

// ── Refund Requests ──────────────────────────────────────────────────────────

export const getRefundRequests = async (status, lastCreatedAt = null, pageSize = 15) => {
  try {
    let query = firestore
      .collection("refund-requests")
      .where("refundStatus", "==", status)
      .orderBy("createdAt", "desc")
      .limit(pageSize);

    if (lastCreatedAt) {
      query = query.startAfter(lastCreatedAt);
    }

    const snapshot = await query.get();
    const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const lastDoc = snapshot.docs[snapshot.docs.length - 1];
    return {
      docs,
      lastCreatedAt: lastDoc ? lastDoc.data().createdAt : null,
      hasMore: snapshot.size === pageSize,
    };
  } catch (error) {
    console.error("getRefundRequests error:", error);
    return { docs: [], lastCreatedAt: null, hasMore: false };
  }
};

export const getAllPendingRefundRequests = async () => {
  try {
    const snapshot = await firestore
      .collection("refund-requests")
      .where("refundStatus", "==", "pending")
      .orderBy("createdAt", "desc")
      .get();
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("getAllPendingRefundRequests error:", error);
    return [];
  }
};

// ── bKash (via the store's API routes in zapai-frontend) ─────────────────────
// Those routes verify the signed-in admin's Firebase ID token and role, so the
// calls carry it as a Bearer token. Local dev talks to the local store server.
const STORE_API_BASE =
  window.location.hostname === "localhost"
    ? "http://localhost:3000"
    : "https://zapai.com.bd";

const callStoreAdminApi = async (path, { method = "GET", body } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You are not signed in.");
  const idToken = await user.getIdToken();
  const res = await fetch(`${STORE_API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: false, message: `Request failed (${res.status})`, ...data };
};

export const isOrderPaidWithBkash = async (orderId) => {
  if (!orderId) return false;
  const snap = await firestore.doc(`orders/${orderId}`).get();
  return (
    snap.exists &&
    (snap.data().payments || []).some((p) => p.method === "bKash")
  );
};

export const refundWithBkash = ({ orderId, amount, reason, refundRequestId }) =>
  callStoreAdminApi("/api/payment/bkash/refund", {
    method: "POST",
    body: { orderId, amount, reason, refundRequestId },
  });

export const getBkashRefundStatus = (orderId) =>
  callStoreAdminApi(`/api/payment/bkash/refund?orderId=${encodeURIComponent(orderId)}`);

export const searchBkashTransaction = (trxID) =>
  callStoreAdminApi(`/api/payment/bkash/search?trxID=${encodeURIComponent(trxID)}`);

export const updateRefundStatus = async (id, status, adminNote = "", refundedAmount = null, orderId = null, extra = {}) => {
  const payload = { refundStatus: status, updatedAt: Date.now(), ...extra };
  if (adminNote) payload.adminNote = adminNote;
  if (status === "approve" && refundedAmount != null) {
    payload.refundedAmount = Number(refundedAmount);
  }
  if (status === "approve" && refundedAmount != null && orderId) {
    const amt = Number(refundedAmount);
    const batch = firestore.batch();
    batch.update(firestore.doc(`refund-requests/${id}`), payload);
    batch.update(firestore.doc(`orders/${orderId}`), { refundedAmount: amt });
    batch.set(firestore.doc(`ordersList/${orderId}`), { refundedAmount: amt }, { merge: true });
    await batch.commit();
  } else {
    await firestore.doc(`refund-requests/${id}`).update(payload);
  }
};

export const buildOrderSummary = async (onProgress) => {
  const KNOWN_STATUSES = [
    "Processing", "Confirmed", "Packing", "On the way",
    "Delivered", "Partial Delivered", "Cancelled",
  ];
  const totals = {};
  KNOWN_STATUSES.forEach((s) => {
    totals[s] = { count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0 };
  });
  totals["Payment Due"] = { count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0 };
  const dailyAcc = {}; // dayDocPath → { ref, count, totalBill, totalPaid, totalDiscount, cancelledCount, cancelledBill }

  let lastDoc = null;
  let total = 0;
  const BATCH_SIZE = 100;
  while (true) {
    let q = firestore
      .collection("ordersList")
      .orderBy(firebase.firestore.FieldPath.documentId())
      .limit(BATCH_SIZE);
    if (lastDoc) q = q.startAfter(lastDoc);
    const snap = await q.get();
    if (snap.empty) break;
    // Backfill/repair the stored paymentDue/dueBill/duePaid fields in the
    // same pass. Missing fields already read as "not due"/0, so only due
    // orders and mismatches are written.
    const flagBatch = firestore.batch();
    let flagWrites = 0;
    snap.docs.forEach((doc) => {
      const d = doc.data();
      const status = d.orderStatus || "Unknown";
      const due = paymentDueFields(d, status);
      if (
        due.paymentDue !== (d.paymentDue === true) ||
        due.dueBill !== (d.dueBill || 0) ||
        due.duePaid !== (d.duePaid || 0)
      ) {
        flagBatch.update(doc.ref, due);
        flagWrites++;
      }
      // Status summary
      if (!totals[status]) totals[status] = { count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0 };
      totals[status].count         += 1;
      totals[status].totalBill     += d.totalBill     || 0;
      totals[status].totalPaid     += d.totalPaid     || 0;
      totals[status].totalDiscount += d.finalDiscount || 0;
      // Payment Due pseudo-bucket
      const contribution = paymentDueContribution(d, status);
      if (contribution) {
        totals["Payment Due"].count += 1;
        totals["Payment Due"].totalBill += contribution.totalBill;
        totals["Payment Due"].totalPaid += contribution.totalPaid;
        totals["Payment Due"].totalDiscount += contribution.totalDiscount;
      }
      // Daily summary
      const dRef = dayDocRef(d.createdAt);
      if (dRef) {
        const key = dRef.path;
        if (!dailyAcc[key]) dailyAcc[key] = { ref: dRef, count: 0, totalBill: 0, totalPaid: 0, totalDiscount: 0, cancelledCount: 0, cancelledBill: 0 };
        const acc = dailyAcc[key];
        if (status === "Cancelled") { acc.cancelledCount += 1; acc.cancelledBill += d.totalBill || 0; }
        else { acc.count += 1; acc.totalBill += d.totalBill || 0; acc.totalPaid += d.totalPaid || 0; acc.totalDiscount += d.finalDiscount || 0; }
      }
    });
    if (flagWrites > 0) await flagBatch.commit();
    total += snap.docs.length;
    lastDoc = snap.docs[snap.docs.length - 1];
    if (onProgress) onProgress(total);
    if (snap.docs.length < BATCH_SIZE) break;
  }

  // Write status summary doc
  await SUMMARY_DOC().set(totals);

  // Write daily docs in chunks of 490 (Firestore batch limit is 500)
  const dailyEntries = Object.values(dailyAcc);
  for (let i = 0; i < dailyEntries.length; i += 490) {
    const chunk = dailyEntries.slice(i, i + 490);
    const batch = firestore.batch();
    chunk.forEach(({ ref, ...data }) => batch.set(ref, data));
    await batch.commit();
  }

  return total;
};

export const countCollection = async (collectionId) => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken();
  const res = await fetch(
    `https://firestore.googleapis.com/v1/projects/zapai-8585e/databases/(default)/documents:runAggregationQuery`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        structuredAggregationQuery: {
          structuredQuery: { from: [{ collectionId }] },
          aggregations: [{ count: {}, alias: "count" }],
        },
      }),
    }
  );
  const data = await res.json();
  return parseInt(data[0]?.result?.aggregateFields?.count?.integerValue || 0);
};

// Live count + sums of ordersList for one orderStatus, via a server-side
// aggregation query (billed ~1 read per 1000 matching docs). Used instead of
// the incrementally-maintained summaries/orderStatsByStatus counters, which
// are written best-effort from many code paths in both repos and drift
// permanently whenever one of those writes is missed or doubled.
const aggregateOrdersListByStatus = async (orderStatus) => {
  // "Payment Due" isn't a stored status — it's the stored paymentDue flag.
  const forPaymentDue = orderStatus === "Payment Due";
  const filter = forPaymentDue
    ? { fieldPath: "paymentDue", value: { booleanValue: true } }
    : { fieldPath: "orderStatus", value: { stringValue: orderStatus } };
  const billField = forPaymentDue ? "dueBill" : "totalBill";
  const paidField = forPaymentDue ? "duePaid" : "totalPaid";
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken();
  const res = await fetch(
    `https://firestore.googleapis.com/v1/projects/zapai-8585e/databases/(default)/documents:runAggregationQuery`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        structuredAggregationQuery: {
          structuredQuery: {
            from: [{ collectionId: "ordersList" }],
            where: {
              fieldFilter: {
                field: { fieldPath: filter.fieldPath },
                op: "EQUAL",
                value: filter.value,
              },
            },
          },
          aggregations: [
            { count: {}, alias: "count" },
            { sum: { field: { fieldPath: billField } }, alias: "totalBill" },
            { sum: { field: { fieldPath: paidField } }, alias: "totalPaid" },
            { sum: { field: { fieldPath: "finalDiscount" } }, alias: "totalDiscount" },
          ],
        },
      }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data?.[0]?.error?.message || data?.error?.message || "Aggregation failed");
  const f = data[0]?.result?.aggregateFields || {};
  const num = (v) => Number(v?.integerValue ?? v?.doubleValue ?? 0);
  return {
    count: num(f.count),
    totalBill: num(f.totalBill),
    totalPaid: num(f.totalPaid),
    totalDiscount: num(f.totalDiscount),
  };
};

export const sumOrdersList = async (startTs = null, endTs = null, orderStatus = null) => {
  const isAllOrders = !orderStatus || orderStatus === "All Orders";
  const hasDateRange = startTs != null && endTs != null;

  // Status tab (incl. Payment Due), no date range → live aggregation over
  // ordersList so the tile always matches the list below it.
  if (!isAllOrders && !hasDateRange) {
    let s = null;
    try {
      s = await aggregateOrdersListByStatus(orderStatus);
    } catch (e) {
      // Needs the composite index ordersList(orderStatus|paymentDue,
      // finalDiscount, totalBill, totalPaid) — fall back to the stored
      // counters rather than showing zeros if it's missing.
      console.error("ordersList aggregation failed, using summary doc", e);
    }
    if (!s) {
      const snap = await SUMMARY_DOC().get();
      s = (snap.data() || {})[orderStatus] || {};
    }
    const totalBill = Math.round(s.totalBill || 0);
    const totalPaid = Math.round(s.totalPaid || 0);
    return {
      orderCount:    s.count        || 0,
      totalBill,
      totalPaid,
      totalDue:      Math.max(0, totalBill - totalPaid),
      totalDiscount: Math.round(s.totalDiscount || 0),
      cancelledCount: 0,
      cancelledBill:  0,
    };
  }

  // Date range → read daily summary docs (N reads where N = number of days in range)
  if (hasDateRange && isAllOrders) {
    const dayKeys = getDayKeys(startTs, endTs);
    const snaps = await Promise.all(dayKeys.map((k) => firestore.doc(`summaries/${k}`).get()));
    let orderCount = 0, cancelledCount = 0;
    let totalBill = 0, totalPaid = 0, totalDiscount = 0, cancelledBill = 0;
    snaps.forEach((snap) => {
      if (!snap.exists) return;
      const d = snap.data();
      orderCount     += d.count          || 0;
      totalBill      += d.totalBill      || 0;
      totalPaid      += d.totalPaid      || 0;
      totalDiscount  += d.totalDiscount  || 0;
      cancelledCount += d.cancelledCount || 0;
      cancelledBill  += d.cancelledBill  || 0;
    });
    totalBill = Math.round(totalBill);
    totalPaid = Math.round(totalPaid);
    return {
      // For "All Orders" the tile is meant to match the raw order count in
      // the table below it (which shows every status, Cancelled included),
      // so fold cancelledCount into the headline number here.
      orderCount: orderCount + cancelledCount,
      totalBill,
      totalPaid,
      totalDue:      Math.max(0, totalBill - totalPaid),
      totalDiscount: Math.round(totalDiscount),
      cancelledCount,
      cancelledBill: Math.round(cancelledBill),
    };
  }

  // Status tab + date range (rare): SDK query filtered client-side
  let query = firestore.collection("ordersList");
  if (hasDateRange) {
    query = query
      .where("createdAt", ">=", firebase.firestore.Timestamp.fromMillis(startTs))
      .where("createdAt", "<=", firebase.firestore.Timestamp.fromMillis(endTs));
  }
  const snapshot = await query.get();
  let orderCount = 0, cancelledCount = 0;
  let totalBill = 0, totalPaid = 0, totalDiscount = 0, cancelledBill = 0;
  snapshot.docs.forEach((doc) => {
    const d = doc.data();
    if (!isAllOrders && d.orderStatus !== orderStatus) return;
    if (d.orderStatus === "Cancelled") { cancelledCount++; cancelledBill += d.totalBill || 0; }
    else { orderCount++; totalBill += d.totalBill || 0; totalPaid += d.totalPaid || 0; totalDiscount += d.finalDiscount || 0; }
  });
  totalBill = Math.round(totalBill);
  totalPaid = Math.round(totalPaid);
  return {
    // Same headline-matches-table logic as the date-range branch above.
    orderCount: isAllOrders ? orderCount + cancelledCount : orderCount,
    totalBill, totalPaid,
    totalDue:      Math.max(0, totalBill - totalPaid),
    totalDiscount: Math.round(totalDiscount),
    cancelledCount,
    cancelledBill: Math.round(cancelledBill),
  };
};

export const fixOrdersListTotals = async (onProgress) => {
  let lastDoc = null;
  let total = 0;
  const BATCH_SIZE = 20;
  while (true) {
    let queryRef = firestore
      .collection("ordersList")
      .orderBy("createdAt", "desc")
      .limit(BATCH_SIZE);
    if (lastDoc) queryRef = queryRef.startAfter(lastDoc);
    const snapshot = await queryRef.get();
    if (snapshot.empty) break;
    const batch = firestore.batch();
    snapshot.docs.forEach((doc) => {
      const o = doc.data();
      const totalBill =
        (o.subTotal || 0) +
        (o.deliveryCharge || 0) -
        (o.discountApplied || 0) -
        (o.couponApplied ? o.couponApplied.discount || 0 : 0) -
        (o.finalDiscount || 0);
      const totalPaid = (o.payments || []).reduce(
        (s, p) => s + (parseInt(p.amount) || 0),
        0,
      );
      batch.update(doc.ref, { totalBill, totalPaid });
    });
    await batch.commit();
    total += snapshot.docs.length;
    lastDoc = snapshot.docs[snapshot.docs.length - 1];
    if (onProgress) onProgress(total);
    if (snapshot.docs.length < BATCH_SIZE) break;
  }
  return total;
};

export const deleteOrderApi = async (order) => {
  const orderRef = firestore.doc(`ordersApi/${order.orderId}`);
  const snapShot = await orderRef.get();
  if (snapShot.exists) {
    try {
      await orderRef.delete();
    } catch (error) {
      console.log("error deleting order", error.message);
    }
  }
};

// ==================== Wholesale Management: Sourcing ====================

export const getAllSourcings = async (month) => {
  const sourcingsCollectionRef = firestore
    .collection("sourcing")
    .where("month", "==", month);
  try {
    const sourcings = await sourcingsCollectionRef.get();
    const sourcingsArray = [];
    sourcings.forEach((doc) => {
      sourcingsArray.push(doc.data());
    });
    return sourcingsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getAllSourcingsAgent = async (agent, month) => {
  const sourcingsCollectionRef = firestore
    .collection("sourcing")
    .where("agent", "==", agent)
    .where("month", "==", month);
  try {
    const sourcings = await sourcingsCollectionRef.get();
    const sourcingsArray = [];
    sourcings.forEach((doc) => {
      sourcingsArray.push(doc.data());
    });
    return sourcingsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadSourcing = async (sourcingObj) => {
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const snapShot = await sourcingRef.get();
  if (!snapShot.exists) {
    try {
      const batch = firestore.batch();
      batch.set(sourcingRef, { ...sourcingObj });
      batch.set(
        monthStatsRef(sourcingObj.month),
        buildMonthStatsDelta(sourcingObj.agent, {
          invoiceCount: 1,
          totalBill: parseFloat(sourcingObj.totalBill) || 0,
          totalPaid: parseFloat(sourcingObj.totalPaid) || 0,
          totalBuyingCost: parseFloat(sourcingObj.buyingCost) || 0,
          totalAgentProfit: parseFloat(sourcingObj.agentPaid) || 0,
          totalProfit: parseFloat(sourcingObj.finalProfit) || 0,
          totalZapaiProfit: parseFloat(sourcingObj.algProfit) || 0,
        }),
        { merge: true },
      );
      await batch.commit();
      const uploadedSnapShot = await sourcingRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }
};

export const updateSourcing = async (sourcingObj) => {
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  try {
    const beforeSnap = await sourcingRef.get();
    const before = beforeSnap.exists ? beforeSnap.data() : {};
    const after = { ...before, ...sourcingObj };
    const batch = firestore.batch();
    batch.update(sourcingRef, { ...sourcingObj });
    if (before.month) {
      const delta = {
        totalBill:
          (parseFloat(after.totalBill) || 0) -
          (parseFloat(before.totalBill) || 0),
        totalPaid:
          (parseFloat(after.totalPaid) || 0) -
          (parseFloat(before.totalPaid) || 0),
        totalBuyingCost:
          (parseFloat(after.buyingCost) || 0) -
          (parseFloat(before.buyingCost) || 0),
        totalAgentProfit:
          (parseFloat(after.agentPaid) || 0) -
          (parseFloat(before.agentPaid) || 0),
        totalProfit:
          (parseFloat(after.finalProfit) || 0) -
          (parseFloat(before.finalProfit) || 0),
        totalZapaiProfit:
          (parseFloat(after.algProfit) || 0) -
          (parseFloat(before.algProfit) || 0),
        // Refund given (to the customer, already in Tk) and refund taken
        // (from the supplier, in RMB — converted to Tk using the invoice's
        // buy rate) are tracked as their own running totals per month.
        totalRefundGiven:
          sumRefundArray(after.givenRefund) -
          sumRefundArray(before.givenRefund),
        totalRefundTaken:
          (sumRefundArray(after.receivedRefund) -
            sumRefundArray(before.receivedRefund)) *
          (parseFloat(after.buyRate) || 0),
      };
      batch.set(
        monthStatsRef(before.month),
        buildMonthStatsDelta(before.agent, delta),
        { merge: true },
      );
    }
    await batch.commit();
    const snapShot = await sourcingRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const deleteSourcing = async (id) => {
  const sourcingRef = firestore.doc(`sourcing/${id}`);
  try {
    const snapShot = await sourcingRef.get();
    const batch = firestore.batch();
    batch.delete(sourcingRef);
    if (snapShot.exists) {
      const data = snapShot.data();
      batch.set(
        monthStatsRef(data.month),
        buildMonthStatsDelta(data.agent, {
          invoiceCount: -1,
          totalBill: -(parseFloat(data.totalBill) || 0),
          totalPaid: -(parseFloat(data.totalPaid) || 0),
          totalBuyingCost: -(parseFloat(data.buyingCost) || 0),
          totalAgentProfit: -(parseFloat(data.agentPaid) || 0),
          totalProfit: -(parseFloat(data.finalProfit) || 0),
          totalZapaiProfit: -(parseFloat(data.algProfit) || 0),
          totalRefundGiven: -sumRefundArray(data.givenRefund),
          totalRefundTaken:
            -sumRefundArray(data.receivedRefund) *
            (parseFloat(data.buyRate) || 0),
        }),
        { merge: true },
      );
    }
    await batch.commit();
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const getSingleSourcing = async (sourcingId) => {
  const orderRef = firestore.doc(`sourcing/${sourcingId}`);
  try {
    const snapShot = await orderRef.get();
    return snapShot.data();
  } catch (error) {
    return null;
  }
};

export const deleteSourcingProduct = async (sourcingId, productId) => {
  const orderRef = firestore.doc(`sourcing/${sourcingId}`);
  try {
    const snapShot = await orderRef.get();
    await orderRef.update({
      productsArray: snapShot
        .data()
        .productsArray.filter((product) => product.id !== productId),
    });
    const updatedSnapShot = await orderRef.get();
    return updatedSnapShot.data();
  } catch (error) {
    return null;
  }
};

export const handleSourcingCashIn = async (sourcingObj) => {
  const batch = firestore.batch();
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const cashInRef = firestore.doc(`dailyCashIn/${sourcingObj.paymentId}`);

  const sourcingSnapShot = await sourcingRef.get();
  const cashInSnapShot = await cashInRef.get();
  const before = sourcingSnapShot.data();

  if (!cashInSnapShot.exists) {
    try {
      batch.set(cashInRef, {
        amount: parseInt(sourcingObj.totalPaid),
        category: "Sourcing",
        subCategory: `${sourcingObj.agent}`,
        note: `Invoice:${sourcingObj.id}
          Date: ${sourcingSnapShot.data().date}`,
        date: sourcingObj.date,
        id: sourcingObj.paymentId,
        month: sourcingObj.month,
        receiveBy: sourcingObj.receiveBy,
        status: "pending",
        unEditable: true,
      });
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }

  const newTotalPaid =
    parseInt(before.totalPaid) + parseInt(sourcingObj.totalPaid);
  const newFinalProfit = newTotalPaid - parseInt(before.buyingCost);
  const newAlgProfit =
    newTotalPaid - parseInt(before.buyingCost) - before.agentPaid;

  batch.update(sourcingRef, {
    ...sourcingObj,
    date: before.date,
    totalPaid: newTotalPaid,
    status: "approved",
    finalProfit: newFinalProfit,
    agentPaid: before.agentPaid,
    algProfit: newAlgProfit,
    invoiceStatus: newTotalPaid >= before.totalBill ? "Paid" : "Not Paid",
  });

  batch.set(
    monthStatsRef(before.month),
    buildMonthStatsDelta(before.agent, {
      totalPaid: parseInt(sourcingObj.totalPaid),
      totalProfit: newFinalProfit - (parseFloat(before.finalProfit) || 0),
      totalZapaiProfit: newAlgProfit - (parseFloat(before.algProfit) || 0),
    }),
    { merge: true },
  );

  try {
    await batch.commit();
    const updatedRef = firestore.doc(`sourcing/${sourcingObj.id}`);
    const snapShot = await updatedRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
    return false;
  }
};

export const handleSourcingCashOut = async (sourcingObj) => {
  const batch = firestore.batch();
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const transactionRef = firestore.doc(
    `sourcingTransactions/${sourcingObj.paymentId}`,
  );

  const sourcingSnapShot = await sourcingRef.get();
  const transactionSnapShot = await transactionRef.get();
  const before = sourcingSnapShot.data();

  if (!transactionSnapShot.exists) {
    try {
      batch.set(transactionRef, {
        id: sourcingObj.paymentId,
        amount: parseFloat(sourcingObj.buyingcost2).toFixed(2),
        employeeId: sourcingObj.employeeId,
        paidInvoice: `Invoice:${sourcingObj.id}
          Date: ${sourcingSnapShot.data().date}`,
        month: getMonthName(),
        category: "Sourcing",
        subCategory: `${sourcingObj.agent}`,
        country: sourcingObj.country ? sourcingObj.country : "",
        rmbBalance: sourcingObj.rmbBalance,
        rupeeBalance: sourcingObj.rupeeBalance,
        totalRmb: sourcingObj.totalRmb,
        date: sourcingObj.date,
        expenseBy: sourcingObj.expenseBy,
        buyRate: sourcingObj.buyRate,
        sellRate: sourcingObj.sellRate,
        fundType: sourcingObj.fundType,
      });
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }

  const newFinalProfit =
    parseInt(before.totalPaid) - parseInt(sourcingObj.buyingCost);

  batch.update(sourcingRef, {
    ...sourcingObj,
    date: before.date,
    // `month` is never allowed to move once an invoice is created — callers
    // (e.g. monthly-invoices.js's handleCashOut) pass a freshly-computed
    // "current month" in their payload for their own transaction ledger
    // entry, which must not leak into the invoice's own stored month.
    month: before.month,
    status: "approved",
    finalProfit: newFinalProfit,
  });

  batch.set(
    monthStatsRef(before.month),
    buildMonthStatsDelta(before.agent, {
      totalBuyingCost:
        (parseFloat(sourcingObj.buyingCost) || 0) -
        (parseFloat(before.buyingCost) || 0),
      totalProfit: newFinalProfit - (parseFloat(before.finalProfit) || 0),
      // `algProfit` (unlike finalProfit) isn't recomputed here — it comes
      // straight from the client payload, spread into the doc above.
      totalZapaiProfit:
        (parseFloat(sourcingObj.algProfit) || 0) -
        (parseFloat(before.algProfit) || 0),
    }),
    { merge: true },
  );

  try {
    await batch.commit();
    const updatedRef = firestore.doc(`sourcing/${sourcingObj.id}`);
    const snapShot = await updatedRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
    return false;
  }
};

export const handleSourcingAgentPay = async (sourcingObj) => {
  const batch = firestore.batch();
  const sourcingRef = firestore.doc(`sourcing/${sourcingObj.id}`);
  const expenseRef = firestore.doc(`dailyExpenses/${sourcingObj.paymentId}`);

  const sourcingSnapShot = await sourcingRef.get();
  const expenseSnapShot = await expenseRef.get();
  const before = sourcingSnapShot.data();

  if (!expenseSnapShot.exists) {
    try {
      batch.set(expenseRef, {
        amount: parseInt(sourcingObj.agentPaid),
        category: "Sourcing",
        subCategory: `${sourcingObj.agent}(agent %)`,
        note: `Invoice:${sourcingObj.id}
          Date: ${sourcingSnapShot.data().date}`,
        date: sourcingObj.date,
        id: sourcingObj.paymentId,
        month: sourcingObj.month,
        receiveBy: sourcingObj.receiveBy,
        status: "pending",
        unEditable: true,
      });

      const newAgentPaid = before.agentPaid
        ? parseInt(before.agentPaid) + parseInt(sourcingObj.agentPaid)
        : parseInt(sourcingObj.agentPaid);
      // Subtract only this payment's amount from whatever algProfit already
      // was (which may already reflect refund given/taken adjustments) —
      // recomputing from raw totalPaid/buyingCost here would silently
      // discard those adjustments.
      const newAlgProfit =
        (parseFloat(before.algProfit) || 0) - parseInt(sourcingObj.agentPaid);

      batch.update(sourcingRef, {
        agentPays: sourcingObj.agentPays,
        agentPaid: newAgentPaid,
        status: "approved",
        algProfit: newAlgProfit,
      });

      batch.set(
        monthStatsRef(before.month),
        buildMonthStatsDelta(before.agent, {
          totalAgentProfit: newAgentPaid - (parseFloat(before.agentPaid) || 0),
          totalZapaiProfit:
            newAlgProfit - (parseFloat(before.algProfit) || 0),
        }),
        { merge: true },
      );
    } catch (error) {
      notifyError(error.message || String(error));
    }
  }

  try {
    await batch.commit();
    const updatedRef = firestore.doc(`sourcing/${sourcingObj.id}`);
    const snapShot = await updatedRef.get();
    return snapShot.data();
  } catch (error) {
    notifyError(error.message || String(error));
    return false;
  }
};

export const getAllSourcingTransactions = async (employeeId, month) => {
  const transactionsCollectionRef = firestore
    .collection("sourcingTransactions")
    .where("employeeId", "==", employeeId)
    .where("month", "==", month);
  try {
    const transactions = await transactionsCollectionRef.get();
    const transactionsArray = [];
    transactions.forEach((doc) => {
      transactionsArray.push({ id: doc.id, ...doc.data() });
    });
    return transactionsArray;
  } catch (error) {
    notifyError(error.message || String(error));
  }
};

export const uploadSourcingTransaction = async (transactionObj) => {
  const transactionRef = firestore.doc(
    `sourcingTransactions/${transactionObj.id}`,
  );
  const snapShot = await transactionRef.get();
  if (!snapShot.exists) {
    try {
      await transactionRef.set({ ...transactionObj });
      const uploadedSnapShot = await transactionRef.get();
      return uploadedSnapShot.data();
    } catch (error) {
      notifyError(error.message || String(error));
    }
  } else {
    notifyError(
      "there is already a transaction with similar name, please change the name and try again",
    );
  }
};

export const uploadImageD2dExpressProduct = async (file) => {
  const imageRef = storage.ref(`d2dExpressProduct/${file.name}`);
  try {
    await imageRef.put(file);
    var imgUrl = [];
    await imageRef.getDownloadURL().then((url) => {
      imgUrl.push(url);
    });
    return imgUrl[0];
  } catch (error) {
    return null;
  }
};

export const getAllOrdersByTrackingNo = async (trackingNo) => {
  const ordersCollectionRef = firestore
    .collection("orders")
    .where("trackingNo", "==", trackingNo);
  try {
    const orders = await ordersCollectionRef.get();
    const productsArray = [];
    orders.forEach((doc) => {
      productsArray.push(doc.data());
    });
    return productsArray;
  } catch (error) {
    notifyError(error.message || String(error));
    return [];
  }
};

